/* ============================================================================
   DRIVE IQ V212 — DRIVE MODE VEHICLE MARKER SYNC
   CLEAN REPLACEMENT FOR LEGACY DRIVE MODE JS

   V270.1 — MANUAL ZOOM / GESTURE RENDER LOCK
   User pinch/pan/rotate owns the camera completely until gesture settle.

   HARD CONTRACTS
   1. One persistent DriveSession owns lifecycle.
   2. One Mapbox map instance per Drive Mode page lifetime.
   3. One persistent vehicle marker (halo + arrow) per drive session.
   4. One canonical vehicle state feeds marker, camera, progress, reroute.
   5. Route metadata updates never rebuild map/session/marker.
   6. Genuine route geometry replacements update source data IN PLACE.
   7. Maneuver updates are data-only. Voice reads maneuver state only.
   8. Recovery is non-destructive: hold last trustworthy state, request fresh data.
   9. Once a route is active, reload/navigation/reinitialization is forbidden until End Drive.
   10. Every subsystem logs through one diagnostic recorder.

   MIGRATION CONTRACT
   - This file is a clean replacement runtime, not a patch appended to V161.
   - Replace the old Drive Mode JS with this file. Do not load V161 underneath it.
   - Reuses existing Drive IQ HTML/CSS IDs, localStorage handoff keys, Mapbox,
     Geoapify, native voice/reroute/add-stop/review/upgrade bridges, and CarPlay.
   - Legacy version-numbered patch authorities are intentionally not carried over.
   - Core navigation, street-name maneuver guidance, voice, route progress, ETA,
     Stop IQ, Driver Signal IQ, closure defense, reroute, arrival, CarPlay export,
     upgrade hooks, review hooks, Follow/Free View, and diagnostics each have one owner.
============================================================================ */
(function(){
  "use strict";

  if(window.__DRIVE_IQ_V200_TRUST_CORE__){ return; }
  window.__DRIVE_IQ_V200_TRUST_CORE__ = true;
  window.DRIVE_IQ_DRIVE_MODE_BUILD = "V275_GUIDANCE_IQ_V1_TURN_SEQUENCE_EXIT_REASSURANCE";

  /* ==========================================================================
     CONFIG
  ========================================================================== */
  var CFG = {
    mapStyle:"mapbox://styles/mapbox/standard-satellite",
    fallbackCenter:[-89.987,35.149],
    fallbackZoom:10.6,
    driveZoom:15.2,
    drivePitch:62,
    map3DEnabled:true,
    map3DLightPreset:"day",
    map3DUrbanZoom:15.4,
    map3DScenicZoom:14.6,
    map3DArrivalZoom:16.2,
    map3DArrivalMiles:1.2,
    cameraMs:340,
    cameraMinIntervalMs:250,
    cameraStationaryMph:4.5,
    cameraHeadingMph:5.0,
    rawFixFreshMs:12000,
    savedFixFreshMs:10*60*1000,
    maxSnapMiles:0.20,
    staleSnapRescueMiles:0.10,
    offRouteSuspectMiles:0.10,
    offRouteConfirmMiles:0.25,
    offRouteConfirmMs:5000,
    rerouteCooldownMs:22000,
    arrivalNearMiles:0.035,
    arrivalStopMph:4.5,
    arrivalDwellMs:7500,
    routeWatchMs:650,
    diagnosticsMax:300,
    voiceMinGapMs:6500,
    voiceEarlyHighwayMiles:2.5,
    voiceEarlyCityMiles:1.5,
    voiceHighwayMph:45,
    voiceLongReassuranceMinMiles:8,
    voiceLongReassuranceGapMs:16*60*1000,
    voiceLongReassuranceBootDelayMs:10*60*1000,
    maneuverPassedBufferMiles:0.025,
    markerArrowPx:38,
    markerHaloPx:60,
    markerHaloBorderPx:3,
    stopSearchRadiusMeters:16000,
    stopMaxResults:8,
    stopCandidateLimit:20,
    stopFallbackLimit:12,
    incidentPollMs:15000,
    closureMinConfidence:72,
    closureRetryMs:45000,
    reviewMinProgress:0.85,
    reviewMinDriveMs:120000,
    weatherSafetyPollMs:5*60*1000,
    weatherArrivalWindowMs:60*60*1000,
    weatherRouteSampleMax:6,
    weatherImpactRepeatMs:20*60*1000,
    weatherSevereRepeatMs:10*60*1000,
    speedLimitPollMs:10000,
    speedLimitMaxAccuracyMeters:45,
    speedLimitMaxTracepointDistanceMeters:35,
    speedLimitMinConfidence:0.55,
    speedLimitRouteSnapMiles:0.06,
    speedLimitMinTraceMoveMiles:0.012,
    speedLimitTraceMax:6,
    speedLimitStaleMs:45000
  };

  var LS = {
    ROUTE_GEO:"diq_route_geo_v1",
    ROUTE_INTEL:"diq_route_intel_v1",
    DEST:"diq_dest_v1",
    DRIVE_ON:"diq_drive_on_v1",
    ACTIVE_DRIVE_ID:"diq_active_drive_id_v1",
    LAST_FIX:"diq_last_gps_v1",
    DRIVE_PAYLOAD:"diq_drive_payload_v1",
    PLUS_ACTIVE:"diq_plus_active_v1",
    PLUS_PENDING:"diq_plus_pending_v1",
    FOUNDING_DRIVER:"diq_founding_driver_v1",
    UPGRADE_INTENT:"diq_upgrade_intent_v1",
    COMPLETED_TRIPS:"diq_completed_trips_v1",
    PLUS_TEASER_SHOWN:"diq_plus_teaser_shown_v1",
    PLUS_TEASER_TAPPED:"diq_plus_teaser_tapped_v1",
    REVIEW_PROMPT_REQUESTED:"diq_review_prompt_requested_v1",
    REVIEW_LAST_DRIVE_ID:"diq_review_last_drive_id_v1",
    APP_INSTALL_PROMPT_SHOWN:"diq_web_app_install_prompt_shown_v1",
    APP_INSTALL_PROMPT_ACCEPTED:"diq_web_app_install_prompt_accepted_v1",
    ACCESS_TIER:"diq_access_tier_v1",
    USER_ROAD_REPORTS:"diq_user_road_reports_v1",
    CONSTRUCTION_EVENTS:"diq_construction_events_v1",
    CONSTRUCTION_INTEL:"diq_construction_intel_v1",
    GARAGE_SKIN:"diq_garage_skin_v1",
    NATIVE_SKIN:"diq_native_active_skin_v1",
    ACTIVE_SKIN:"diq_active_skin_v1",
    SKIN_FALLBACK_1:"driveiq_skin",
    SKIN_FALLBACK_2:"driveiq_theme",
    SKIN_FALLBACK_3:"diq_theme_v1",
    VOICE_STYLE:"diq_voice_style_v1",
    VEHICLE_MARKER:"diq_vehicle_marker_v1",
    VEHICLE_3D_TYPE:"diq_vehicle_3d_type_v1",
    VEHICLE_3D_COLOR:"diq_vehicle_3d_color_v1",
    VEHICLE_3D_URL:"diq_vehicle_3d_url_v1",
    BRIEFED_DRIVE_ID:"diq_briefed_drive_id_v1",
    HOME:"diq_home_v1",
    HOME_FALLBACK_1:"driveiq_home",
    HOME_FALLBACK_2:"diq_saved_home_v1",
    HOME_FALLBACK_3:"diq_home_destination_v1"
  };

  var MAPBOX_TOKEN = String(window.DRIVE_IQ_MAPBOX_TOKEN || window.SLX_MAPBOX_TOKEN || "");
  var GEOAPIFY_KEY = String(window.DRIVE_IQ_GEOAPIFY_KEY || window.SLX_GEOAPIFY_KEY || "");
  var START_URL = String(window.DRIVE_IQ_START_URL || "/pages/start-drive");
  var APP_STORE_URL = "https://apps.apple.com/us/app/gps-navigation-drive-iq/id6770706972";
  var WEATHER_TILE_BASE = String(
    window.DRIVE_IQ_WEATHER_TILE_BASE ||
    "https://driveiq-intelligence-api-85238371012.us-central1.run.app/v1/weather/tiles"
  ).replace(/\/$/,"");
  var WEATHER_RADAR_SOURCE_ID = "diq-weather-precip";
  var WEATHER_RADAR_LAYER_ID = "diq-weather-precip";

  /* ==========================================================================
     UTILITIES
  ========================================================================== */
  function now(){ return Date.now(); }
  function finite(v){ return typeof v === "number" && isFinite(v); }
  function num(v,d){ v=Number(v); return finite(v)?v:(d||0); }
  function clamp(v,a,b){ return Math.max(a,Math.min(b,v)); }
  function clean(v){ return String(v==null?"":v).replace(/\s+/g," ").trim(); }
  function byId(id){ return document.getElementById(id); }
  function lsGet(k){ try{return localStorage.getItem(k);}catch(e){return null;} }
  function lsSet(k,v){ try{localStorage.setItem(k,String(v));return true;}catch(e){return false;} }
  function lsJson(k,v){ try{localStorage.setItem(k,JSON.stringify(v));return true;}catch(e){return false;} }
  function parse(v){ try{return v?JSON.parse(v):null;}catch(e){return null;} }
  function normBearing(v){ v=((num(v,0)%360)+360)%360; return v; }
  function angleDelta(a,b){ var d=((normBearing(b)-normBearing(a)+540)%360)-180; return d; }
  function rad(v){ return v*Math.PI/180; }
  function deg(v){ return v*180/Math.PI; }
  function haversineMiles(a,b){
    if(!a||!b){return Infinity;}
    var lat1=rad(num(a.lat)),lat2=rad(num(b.lat));
    var dLat=lat2-lat1,dLon=rad(num(b.lng)-num(a.lng));
    var h=Math.sin(dLat/2)*Math.sin(dLat/2)+Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLon/2)*Math.sin(dLon/2);
    return 3958.7613*2*Math.atan2(Math.sqrt(h),Math.sqrt(1-h));
  }
  function bearing(a,b){
    if(!a||!b){return 0;}
    var p1=rad(a.lat),p2=rad(b.lat),dl=rad(b.lng-a.lng);
    return normBearing(deg(Math.atan2(Math.sin(dl)*Math.cos(p2),Math.cos(p1)*Math.sin(p2)-Math.sin(p1)*Math.cos(p2)*Math.cos(dl))));
  }
  function milesToFeet(mi){ return mi*5280; }
  function fmtDistance(mi){
    if(!finite(mi)){ return ""; }
    if(mi < 0.19){ return Math.max(50,Math.round(milesToFeet(mi)/50)*50)+" ft"; }
    if(mi < 10){ return (Math.round(mi*10)/10).toFixed(mi<1?1:1)+" mi"; }
    return Math.round(mi)+" mi";
  }
  function fmtTime(sec){
    sec=Math.max(0,num(sec));
    var min=Math.round(sec/60),h=Math.floor(min/60),m=min%60;
    if(h>0){return h+"h "+m+"m";}
    return min+" min";
  }
  function setText(id,text){ var el=byId(id); if(el){el.textContent=String(text==null?"":text);} }
  function setSub(text){ setText("diqSub",text); }
  function setGps(text){ setText("diqGpsValue",text); setText("diqGps",text); setText("diqGpsText",text); }
  function setSpeed(mph){
    var v=Math.max(0,Math.round(num(mph)));
    setText("diqSpeedValue",v);
    setText("diqSpeed",v);
  }
  function setSpeedLimit(mph){
    var sign=byId("diqSpeedLimitSign"),value=byId("diqSpeedLimitValue");
    if(!sign||!value){return;}
    if(!finite(Number(mph))||Number(mph)<=0){
      value.textContent="--";
      try{sign.classList.add("diqV132LimitUnknown");}catch(e0){}
      return;
    }
    value.textContent=String(Math.max(1,Math.round(Number(mph))));
    try{sign.classList.remove("diqV132LimitUnknown");}catch(e1){}
  }
  function toast(text){
    var el=byId("diqToast");
    if(!el){ return; }
    el.textContent=clean(text);
    try{el.classList.add("show");}catch(e){}
    window.setTimeout(function(){try{el.classList.remove("show");}catch(e){}},1800);
  }
  function setSignal(id,text){
    setText(id,text);

    /*
      V251 WEATHER CARD FIT:
      Weather carries more route context than the other compact signal cards.
      Allow only the weather value to use two lines instead of truncating the
      useful distance/timing context with a single-line ellipsis.
    */
    if(id==="diqSigWeather"){
      var el=byId(id);
      if(el){
        try{
          el.style.whiteSpace="normal";
          el.style.overflow="hidden";
          el.style.textOverflow="clip";
          el.style.display="-webkit-box";
          el.style.webkitBoxOrient="vertical";
          el.style.webkitLineClamp="2";
          el.style.lineHeight="1.12";
          el.style.maxHeight="2.24em";
          el.style.wordBreak="normal";
          el.style.overflowWrap="anywhere";
        }catch(e){}
      }
    }
  }

  /* ==========================================================================
     DIAGNOSTIC RECORDER — ONE PLACE TO LOOK WHEN SOMETHING FAILS
  ========================================================================== */
  var Diagnostics = {
    events:[],
    push:function(owner,event,data){
      var row={ts:now(),owner:owner,event:event,data:data||null};
      this.events.push(row);
      if(this.events.length>CFG.diagnosticsMax){this.events.shift();}
      try{ console.log("[DriveIQ:"+owner+"]",event,data||""); }catch(e){}
      return row;
    },
    error:function(owner,event,data){
      var row={ts:now(),owner:owner,event:event,error:true,data:data||null};
      this.events.push(row);
      if(this.events.length>CFG.diagnosticsMax){this.events.shift();}
      try{ console.error("[DriveIQ:"+owner+"]",event,data||""); }catch(e){}
      return row;
    },
    recent:function(n){ return this.events.slice(-Math.max(1,num(n,50))); }
  };

  /* ==========================================================================
     CENTRAL STATE — MUTATED ONLY BY ITS OWNER METHODS
  ========================================================================== */
  var S = {
    session:{id:"",active:false,routeActive:false,startedAt:0,phase:"STARTING",ended:false},
    map:{instance:null,loaded:false,created:false,threeDContext:"baseline"},
    location:{raw:null,canonical:null,lastValid:null,watchId:null,lastAcceptedAt:0},
    vehicle:{marker:null,element:null,arrow:null,halo:null,created:false,lastHeading:null,headingReady:false,artwork:"rocket_scout",custom3d:null},
    route:{geo:null,intel:null,destination:null,signature:"",renderedSig:"",coords:[],progressMiles:0,progressIndex:0,totalMiles:0,replacing:false,speedLimitMph:null},
    speedLimit:{trace:[],busy:false,lastQueryAt:0,lastVerifiedAt:0,lastQueryFix:null,road:"",confidence:null,source:""},
    maneuver:{list:[],activeIndex:0,current:null,lastSpokenKey:"",lastSpokenAt:0,lastReassuranceKey:"",lastReassuranceAt:0,instructionFetch:false,instructionFetched:false,instructionRouteSig:"",instructionError:""},
    voice:{enabled:true,primed:false,lastText:"",lastAt:0,speaking:false},
    camera:{mode:"FOLLOW",lastAt:0,lastBearing:null,userFreeAt:0,gestureActive:false,gestureKinds:{},gestureSettleTimer:null},
    reroute:{state:"ON_ROUTE",suspectAt:0,lastAt:0,busy:false,reason:"",closureKey:""},
    arrival:{nearSince:0,slowSince:0,parkedSince:0,arrived:false,announced:false},
    carplay:{arrivalSent:false},
    stop:{busy:false,query:"",results:[],markers:[],selected:null,clicksBound:false,card:null},
    incidents:{events:[],closures:[],lastClosureKey:"",lastClosureAt:0},
    weatherSafety:{busy:false,lastAt:0,lastImpactKey:"",lastImpactAt:0,lastSevereKey:"",lastSevereAt:0,lastAlertAt:0,severe:[],impacts:[],currentForecast:null},
    cloudIntelligence:{
      installed:false,lastAt:0,connectivity:"idle",response:null,weather:null,traffic:null,
      trafficPrompt:null,trafficSuppressed:null,lastTrafficVoiceAt:0,trafficRouteSignature:""
    },
    review:{completed:false,requested:false},
    preferences:{skin:"cockpit",voiceStyle:"calm",vehicleMarker:"rocket_scout",started:false},
    timers:{routeWatch:null,heartbeat:null,progress:null,incident:null,weatherSafety:null}
  };


  /* ==========================================================================
     V211 THEME PAINT AUTHORITY — PRESENTATION ONLY

     My Drive IQ remains the preference authority. Drive Mode reads the selected
     skin and paints route/destination/vehicle/Stop IQ accents IN PLACE.

     HARD RULES:
     - Never call map.setStyle().
     - Never recreate the map, route source, vehicle marker, session, or camera.
     - Never mutate route geometry, GPS, reroute, maneuver, or voice state.
  ========================================================================== */
  var DRIVE_SKINS = {
    platinum_gt:{
      /* Pearl-white luxury cockpit with champagne-gold route. */
      route:"#C8A96B",
      casing:"#61543D",
      glow:"rgba(200,169,107,.96)",
      glowSoft:"rgba(230,211,162,.16)",
      glowShadow:"rgba(163,136,87,.54)",
      destination:"#F7F3EA"
    },
    american_muscle:{
      route:"#F04455",
      casing:"#32151C",
      glow:"rgba(240,68,85,.96)",
      glowSoft:"rgba(240,68,85,.12)",
      glowShadow:"rgba(240,68,85,.68)",
      destination:"#E4E6EA"
    },
    carbon_rs:{
      route:"#F04455",
      casing:"#35161D",
      glow:"rgba(240,68,85,.96)",
      glowSoft:"rgba(240,68,85,.12)",
      glowShadow:"rgba(240,68,85,.65)",
      destination:"#D9E0E6"
    },
    cockpit:{
      route:"#00F5FF",
      casing:"#073B4C",
      glow:"rgba(0,245,255,.95)",
      glowSoft:"rgba(0,245,255,.10)",
      glowShadow:"rgba(0,245,255,.65)",
      destination:"#00F5FF"
    },
    aurora:{
      route:"#35D9C4",
      casing:"#6D55C9",
      glow:"rgba(53,217,196,.95)",
      glowSoft:"rgba(53,217,196,.10)",
      glowShadow:"rgba(143,121,255,.58)",
      destination:"#BFF7FF"
    },
    sunset:{
      route:"#D66B38",
      casing:"#5B1738",
      glow:"rgba(214,107,56,.95)",
      glowSoft:"rgba(214,107,56,.11)",
      glowShadow:"rgba(143,63,98,.58)",
      destination:"#F3C66A"
    },
    coastal:{
      route:"#27D8E6",
      casing:"#075D7A",
      glow:"rgba(39,216,230,.95)",
      glowSoft:"rgba(39,216,230,.10)",
      glowShadow:"rgba(106,184,207,.58)",
      destination:"#E7D9A8"
    },
    midnight_luxe:{
      route:"#D8B86A",
      casing:"#3A285F",
      glow:"rgba(216,184,106,.95)",
      glowSoft:"rgba(216,184,106,.10)",
      glowShadow:"rgba(96,76,138,.58)",
      destination:"#F2DDB0"
    },
    volt:{
      route:"#9FE43B",
      casing:"#176B55",
      glow:"rgba(159,228,59,.95)",
      glowSoft:"rgba(159,228,59,.10)",
      glowShadow:"rgba(61,233,213,.58)",
      destination:"#3DE9D5"
    },
    forest:{
      route:"#C58B49",
      casing:"#315F3B",
      glow:"rgba(197,139,73,.95)",
      glowSoft:"rgba(197,139,73,.10)",
      glowShadow:"rgba(49,95,59,.58)",
      destination:"#6E9B55"
    },
    autumn:{
      route:"#C7A14A",
      casing:"#7A351D",
      glow:"rgba(199,161,74,.95)",
      glowSoft:"rgba(199,161,74,.10)",
      glowShadow:"rgba(158,76,36,.58)",
      destination:"#9E4C24"
    },
    holiday:{
      route:"#D05A2C",
      casing:"#5B2A86",
      glow:"rgba(208,90,44,.96)",
      glowSoft:"rgba(111,67,162,.14)",
      glowShadow:"rgba(111,67,162,.68)",
      destination:"#D5A13F"
    }
  };

  function drivePalette(){
    return DRIVE_SKINS[S.preferences.skin] || DRIVE_SKINS.cockpit;
  }

  /* V274 Platinum GT — isolated Drive Mode presentation. No route/session writes. */
  function polishPlatinumDriveMode(){
    var skin=S.preferences.skin;
    var existing=byId("diqPlatinumDriveModeV274");
    if(!existing){
      var style=document.createElement("style");
      style.id="diqPlatinumDriveModeV274";
      style.textContent=[
        'html[data-diq-skin="platinum_gt"] #diqModePill,body[data-diq-skin="platinum_gt"] #diqModePill{background:linear-gradient(135deg,#f7f3ea,#e1d4bc)!important;color:#64502d!important;border:1px solid #c8a96b!important;box-shadow:0 2px 16px rgba(200,169,107,.22)!important}',
        'body[data-diq-skin="platinum_gt"] #diqFollowLock{background:linear-gradient(135deg,#e7d5ac,#bc9d64)!important;border:1px solid #f4e7c9!important;color:#202328!important;box-shadow:0 6px 24px rgba(200,169,107,.25)!important}',
        'body[data-diq-skin="platinum_gt"] #diqFindStops,body[data-diq-skin="platinum_gt"] #diqEndDrive,body[data-diq-skin="platinum_gt"] #diqBack,body[data-diq-skin="platinum_gt"] #diqAudio,body[data-diq-skin="platinum_gt"] #diqVibeBtn{border-color:rgba(200,169,107,.68)!important}',
        'body[data-diq-skin="platinum_gt"] #diqFindStops{color:#eee0bd!important}',
        'body[data-diq-skin="platinum_gt"] #diqEndDrive{color:#f3e0b7!important}',
        'body[data-diq-skin="platinum_gt"] #diqTurnCard,body[data-diq-skin="platinum_gt"] #diqManeuverCard{border-color:rgba(200,169,107,.58)!important;box-shadow:0 8px 30px rgba(0,0,0,.3),inset 0 1px 0 rgba(255,245,224,.06)!important}',
        'body[data-diq-skin="platinum_gt"] #diqTurnIcon{border-color:#c8a96b!important;color:#f2deb6!important;background:rgba(200,169,107,.12)!important}',
        'body[data-diq-skin="platinum_gt"] #diqSpeedValue{color:#f3e6c9!important}'
      ].join('\n');
      (document.head||document.documentElement).appendChild(style);
    }
    var pill=byId("diqModePill");
    if(pill && skin==="platinum_gt" && !FreeTierIQ.free()){
      pill.textContent="PLATINUM GT";
    }
  }

  var ThemePaintAuthority = {
    paint:function(reason){
      var p=drivePalette(),m=S.map.instance,inner=null;

      /*
        Mapbox paint-property updates are non-destructive presentation changes.
        They do not replace style, source data, route geometry, camera, or session.
      */
      if(S.map.loaded&&m){
        try{
          if(m.getLayer("diq-v200-route")){
            m.setPaintProperty("diq-v200-route","line-color",p.route);
            m.setPaintProperty("diq-v200-route","line-width",S.session.phase==="FINAL_APPROACH"?8:7);
          }
          if(m.getLayer("diq-v200-route-casing")){
            m.setPaintProperty("diq-v200-route-casing","line-color",p.casing||p.route);
            m.setPaintProperty("diq-v200-route-casing","line-width",S.session.phase==="FINAL_APPROACH"?14:13);
          }
          if(m.getLayer("diq-v200-dest")){
            m.setPaintProperty("diq-v200-dest","circle-color",p.destination);
            m.setPaintProperty("diq-v200-dest","circle-radius",S.session.phase==="FINAL_APPROACH"?10:8);
            m.setPaintProperty("diq-v200-dest","circle-stroke-width",S.session.phase==="FINAL_APPROACH"?4:3);
          }
          if(m.getLayer("diq-v200-stop-results")){
            m.setPaintProperty("diq-v200-stop-results","circle-stroke-color",p.route);
          }
        }catch(e1){
          Diagnostics.error("ThemePaintAuthority","MAP_PAINT_FAILED",{
            skin:S.preferences.skin,
            reason:reason||"",
            message:String(e1&&e1.message||e1)
          });
        }
      }

      try{
        if(S.vehicle.halo){
          S.vehicle.halo.style.border=CFG.markerHaloBorderPx+"px solid "+p.glow;
          S.vehicle.halo.style.background=p.glowSoft;
          S.vehicle.halo.style.boxShadow=
            "0 0 14px "+p.glowShadow+
            ", inset 0 0 10px "+p.glowSoft;
        }

        if(S.vehicle.arrow){
          S.vehicle.arrow.style.color=p.route;
          S.vehicle.arrow.style.setProperty("--diq-marker-primary",p.route);
          S.vehicle.arrow.style.setProperty("--diq-marker-secondary",p.destination);
          S.vehicle.arrow.style.filter="drop-shadow(0 0 5px "+p.glowShadow+") drop-shadow(0 0 12px "+p.glowSoft+")";
        }
      }catch(e2){
        Diagnostics.error("ThemePaintAuthority","VEHICLE_PAINT_FAILED",{
          skin:S.preferences.skin,
          reason:reason||"",
          message:String(e2&&e2.message||e2)
        });
      }

      Diagnostics.push("ThemePaintAuthority","PAINTED",{
        skin:S.preferences.skin,
        route:p.route,
        destination:p.destination,
        reason:reason||""
      });
      return true;
    }
  };


  /* ==========================================================================
     V227 WEATHER RADAR AUTHORITY — PRESENTATION ONLY

     - Secure OpenWeather precipitation tiles come through Drive IQ Cloud Run.
     - Never rebuilds the map, route, vehicle marker, camera, or session.
     - Radar is inserted BELOW the route casing so navigation stays legible.
  ========================================================================== */
  var WeatherRadarAuthority = {
    visible:false,

    ensure:function(reason){
      var m=S.map.instance;
      if(!S.map.loaded||!m||!WEATHER_TILE_BASE){return false;}

      try{
        if(!m.getSource(WEATHER_RADAR_SOURCE_ID)){
          m.addSource(WEATHER_RADAR_SOURCE_ID,{
            type:"raster",
            tiles:[WEATHER_TILE_BASE+"/{z}/{x}/{y}.png"],
            tileSize:256,
            minzoom:0,
            maxzoom:10
          });
        }

        if(!m.getLayer(WEATHER_RADAR_LAYER_ID)){
          var radarLayer={
            id:WEATHER_RADAR_LAYER_ID,
            type:"raster",
            source:WEATHER_RADAR_SOURCE_ID,
            layout:{
              visibility:this.visible?"visible":"none"
            },
            paint:{
              "raster-opacity":0.52,
              "raster-fade-duration":300
            }
          };
          var radarSlot=ThreeDWorldAuthority.layerSlot("radar");
          if(radarSlot){
            radarLayer.slot=radarSlot;
            m.addLayer(radarLayer);
          }else{
            var beforeId=m.getLayer("diq-v200-route-casing")?"diq-v200-route-casing":undefined;
            m.addLayer(radarLayer,beforeId);
          }
        }else{
          m.setLayoutProperty(
            WEATHER_RADAR_LAYER_ID,
            "visibility",
            this.visible?"visible":"none"
          );
        }

        Diagnostics.push("WeatherRadarAuthority","ENSURED",{
          visible:this.visible,
          reason:reason||""
        });
        return true;
      }catch(e){
        Diagnostics.error("WeatherRadarAuthority","ENSURE_FAILED",{
          message:String(e&&e.message||e),
          reason:reason||""
        });
        return false;
      }
    },

    setVisible:function(show,reason){
      if(FreeTierIQ.free()){show=false;}
      this.visible=!!show;
      if(!S.map.loaded||!S.map.instance){return this.visible;}
      this.ensure(reason||"visibility");
      try{
        if(S.map.instance.getLayer(WEATHER_RADAR_LAYER_ID)){
          S.map.instance.setLayoutProperty(
            WEATHER_RADAR_LAYER_ID,
            "visibility",
            this.visible?"visible":"none"
          );
        }
      }catch(e){
        Diagnostics.error("WeatherRadarAuthority","VISIBILITY_FAILED",{
          visible:this.visible,
          message:String(e&&e.message||e)
        });
      }
      Diagnostics.push("WeatherRadarAuthority","VISIBILITY",{
        visible:this.visible,
        reason:reason||""
      });
      return this.visible;
    },

    weatherTextHasPrecip:function(text){
      var s=clean(text).toLowerCase();
      return /rain|drizzle|shower|thunder|snow|sleet|ice|wintry|precip|wet weather/.test(s);
    },

    weatherCodeHasPrecip:function(code){
      code=Number(code);
      return (
        (code>=51&&code<=67) ||
        (code>=71&&code<=77) ||
        (code>=80&&code<=86) ||
        (code>=95&&code<=99)
      );
    }
  };

  try{
    window.DriveIQWeatherRadar={
      show:function(){return WeatherRadarAuthority.setVisible(true,"manual-show");},
      hide:function(){return WeatherRadarAuthority.setVisible(false,"manual-hide");},
      status:function(){return {visible:WeatherRadarAuthority.visible,source:WEATHER_TILE_BASE};}
    };
  }catch(e0){}


  /* ==========================================================================
     V228 ATMOSPHERIC RAIN AUTHORITY — PRESENTATION ONLY

     Requires Mapbox GL JS v3.9+.
     This effect represents precipitation AT / VERY NEAR the vehicle.
     Radar remains the geographic "weather ahead" layer.

     HARD RULES
     - Never mutates route, GPS, camera, marker, maneuver, reroute, or arrival.
     - Never activates just because rain exists far ahead.
     - Fails silently if setRain is unavailable or unsupported by WebGL/style.
  ========================================================================== */
  var AtmosphericRainAuthority = {
    active:false,
    level:"off",

    supported:function(){
      var m=S.map.instance;
      return !!(m && typeof m.setRain==="function");
    },

    params:function(level){
      if(level==="storm"){
        return {
          density:0.78,
          intensity:0.98,
          color:"#9fa6b4",
          opacity:0.72,
          vignette:0.68,
          "vignette-color":"#343a44",
          direction:[0,74],
          "droplet-size":[2.8,18.0],
          "distortion-strength":0.72,
          "center-thinning":0.12
        };
      }

      if(level==="heavy"){
        return {
          density:0.62,
          intensity:0.90,
          color:"#a7aebb",
          opacity:0.60,
          vignette:0.50,
          "vignette-color":"#3d434d",
          direction:[0,77],
          "droplet-size":[2.3,15.5],
          "distortion-strength":0.56,
          "center-thinning":0.16
        };
      }

      if(level==="moderate"){
        return {
          density:0.40,
          intensity:0.70,
          color:"#afb6c2",
          opacity:0.44,
          vignette:0.30,
          "vignette-color":"#454b55",
          direction:[0,80],
          "droplet-size":[1.8,12.0],
          "distortion-strength":0.38,
          "center-thinning":0.22
        };
      }

      if(level==="light"){
        return {
          density:0.24,
          intensity:0.48,
          color:"#bbc2cc",
          opacity:0.29,
          vignette:0.16,
          "vignette-color":"#505660",
          direction:[0,82],
          "droplet-size":[1.2,8.0],
          "distortion-strength":0.22,
          "center-thinning":0.30
        };
      }

      /* Drizzle should be visible but never feel like a downpour. */
      return {
        density:0.14,
        intensity:0.30,
        color:"#c4cad3",
        opacity:0.19,
        vignette:0.08,
        "vignette-color":"#565c65",
        direction:[0,84],
        "droplet-size":[0.8,5.2],
        "distortion-strength":0.12,
        "center-thinning":0.36
      };
    },

    classify:function(forecast){
      if(!forecast){return "off";}

      var code=Number(forecast.code);
      var precip=num(forecast.precip,0);
      var impact=forecast.impact||null;
      var type=clean(impact&&impact.type||"").toLowerCase();

      var raining=
        (code>=51&&code<=67) ||
        (code>=80&&code<=82) ||
        (code>=95&&code<=99) ||
        /rain|precip|thunder|drizzle|shower/.test(type);

      /*
        V248 LIGHT-RAIN SENSITIVITY:
        precip is hourly precipitation probability, not rainfall intensity.
        Keep the windshield effect conservative, but do not drop a real sprinkle
        simply because the hourly weather code has not flipped to "rain" yet.
        30–44% gets only the subtle drizzle presentation; alert/severity logic is
        unchanged and still requires stronger evidence.
      */
      if(!raining && precip<30){return "off";}
      if(!raining && precip<45){return "drizzle";}

      if(
        (code>=95&&code<=99) ||
        type==="thunderstorm" ||
        /thunder|storm/.test(type) ||
        precip>=90
      ){return "storm";}

      if(
        code===65 ||
        code===67 ||
        code===82 ||
        /heavy rain|downpour|torrential/.test(type) ||
        precip>=78
      ){return "heavy";}

      if(
        code===63 ||
        code===66 ||
        code===81 ||
        type==="rain" ||
        type==="precip" ||
        precip>=60
      ){return "moderate";}

      if(
        code===61 ||
        code===80 ||
        /light rain|shower/.test(type) ||
        precip>=45
      ){return "light";}

      if(
        (code>=51&&code<=57) ||
        /drizzle/.test(type)
      ){return "drizzle";}

      return "light";
    },

    apply:function(level,reason){
      level=FreeTierIQ.free()?"off":(level||"off");

      /* V270.1: do not re-apply the same style-level rain state on every
         styledata event. Repeated setRain() calls can trigger more style work
         while the driver is pinching/zooming the map. */
      if(level===this.level && ((level==="off"&&!this.active)||(level!=="off"&&this.active))){
        return true;
      }

      if(!this.supported()){
        this.active=false;
        this.level="unsupported";
        Diagnostics.push("AtmosphericRainAuthority","UNSUPPORTED",{
          reason:reason||"",
          mapboxVersion:(window.mapboxgl&&window.mapboxgl.version)||"unknown"
        });
        return false;
      }

      try{
        if(level==="off"){
          /* Mapbox accepts null to remove a style-level rain effect. */
          S.map.instance.setRain(null);
          this.active=false;
          this.level="off";
        }else{
          S.map.instance.setRain(this.params(level));
          this.active=true;
          this.level=level;
        }

        Diagnostics.push("AtmosphericRainAuthority","APPLIED",{
          active:this.active,
          level:this.level,
          reason:reason||""
        });
        return true;
      }catch(e){
        Diagnostics.error("AtmosphericRainAuthority","APPLY_FAILED",{
          level:level,
          reason:reason||"",
          message:String(e&&e.message||e)
        });
        return false;
      }
    },

    syncCurrentForecast:function(forecast,reason){
      var miles=num(forecast&&forecast.sample&&forecast.sample.milesAhead,9999);

      /* First sample in routeSamplePlan is the current route position.
         Never use a distant route sample for the windshield effect. */
      if(!forecast || miles>2.0){
        return this.apply("off",reason||"not-near-vehicle");
      }

      var level=this.classify(forecast);
      Diagnostics.push("AtmosphericRainAuthority","CLASSIFIED",{
        level:level,
        code:forecast.code,
        precip:forecast.precip,
        milesAhead:miles,
        impactType:clean(forecast&&forecast.impact&&forecast.impact.type||""),
        reason:reason||"current-weather"
      });
      return this.apply(level,reason||"current-weather");
    }
  };

  try{
    window.DriveIQAtmosphericRain={
      on:function(level){return AtmosphericRainAuthority.apply(level||"moderate","manual-on");},
      off:function(){return AtmosphericRainAuthority.apply("off","manual-off");},
      status:function(){return {
        active:AtmosphericRainAuthority.active,
        level:AtmosphericRainAuthority.level,
        supported:AtmosphericRainAuthority.supported(),
        mapboxVersion:(window.mapboxgl&&window.mapboxgl.version)||"unknown"
      };}
    };
  }catch(e0){}

  /* ==========================================================================
     DESTRUCTIVE-ACTION FIREWALL
  ========================================================================== */
  var DestructiveFirewall = {
    blocked:function(action,owner){
      if(S.session.active && S.session.routeActive && !S.session.ended){
        Diagnostics.error("Firewall","BLOCKED "+action,{requestedBy:owner||"unknown",phase:S.session.phase});
        return true;
      }
      return false;
    },
    navigate:function(url,owner){
      if(this.blocked("navigation",owner)){ return false; }
      try{
        if(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.driveIQNavigate){
          window.webkit.messageHandlers.driveIQNavigate.postMessage({target:url,source:"drive_iq_v200"});
          return true;
        }
      }catch(e1){}
      try{ window.location.href=url; return true; }catch(e2){ return false; }
    }
  };

  /* ==========================================================================
     ROUTE GEOMETRY HELPERS
  ========================================================================== */
  function collectLineCoords(geometry,out){
    if(!geometry){return;}
    var c=geometry.coordinates,i;
    if(geometry.type==="LineString" && c){
      for(i=0;i<c.length;i++){
        if(c[i]&&finite(Number(c[i][0]))&&finite(Number(c[i][1]))){out.push([Number(c[i][0]),Number(c[i][1])]);}
      }
    }else if(geometry.type==="MultiLineString" && c){
      for(i=0;i<c.length;i++){ collectLineCoords({type:"LineString",coordinates:c[i]},out); }
    }
  }
  function routeCoords(route){
    var out=[],i,f;
    if(!route){return out;}
    if(route.type==="FeatureCollection" && route.features){
      for(i=0;i<route.features.length;i++){f=route.features[i]; if(f&&f.geometry){collectLineCoords(f.geometry,out);}}
    }else if(route.type==="Feature"&&route.geometry){collectLineCoords(route.geometry,out);}
    else if(route.geometry){collectLineCoords(route.geometry,out);}
    return out;
  }
  function geometrySignature(route){
    var c=routeCoords(route),parts=[],i;
    for(i=0;i<c.length;i++){
      if(i===0 || i===c.length-1 || i%Math.max(1,Math.floor(c.length/120))===0){parts.push(c[i][0].toFixed(5)+","+c[i][1].toFixed(5));}
    }
    return c.length+"|"+parts.join("|");
  }
  function cumulativeMiles(coords,index){
    var d=0,i,max=Math.min(coords.length-1,Math.max(0,index));
    for(i=1;i<=max;i++){
      d+=haversineMiles({lng:coords[i-1][0],lat:coords[i-1][1]},{lng:coords[i][0],lat:coords[i][1]});
    }
    return d;
  }
  function nearestProjection(fix,coords,startIndex){
    if(!fix||!coords||coords.length<2){return null;}
    var best=null,bestD=Infinity,i,a,b,abx,aby,apx,apy,t,lng,lat,d;
    var start=Math.max(0,num(startIndex,0)-40),end=Math.min(coords.length-2,num(startIndex,0)+140);
    if(startIndex==null || !finite(Number(startIndex))){start=0;end=coords.length-2;}
    var cos=Math.cos(rad(fix.lat));
    for(i=start;i<=end;i++){
      a=coords[i];b=coords[i+1];
      abx=(b[0]-a[0])*cos; aby=b[1]-a[1];
      apx=(fix.lng-a[0])*cos; apy=fix.lat-a[1];
      var den=abx*abx+aby*aby;
      t=den?clamp((apx*abx+apy*aby)/den,0,1):0;
      lng=a[0]+(b[0]-a[0])*t; lat=a[1]+(b[1]-a[1])*t;
      d=haversineMiles(fix,{lng:lng,lat:lat});
      if(d<bestD){bestD=d;best={lng:lng,lat:lat,index:i,t:t,distMiles:d};}
    }
    return best;
  }
  function distanceAlongProjection(p,coords){
    if(!p||!coords||coords.length<2){return 0;}
    var base=cumulativeMiles(coords,p.index);
    var a={lng:coords[p.index][0],lat:coords[p.index][1]};
    var x={lng:p.lng,lat:p.lat};
    return base+haversineMiles(a,x);
  }

  /* FOUNDER IQ V2.4 — isolated, best-effort milestone telemetry.
     A failed event never blocks navigation, rerouting, CarPlay, or StoreKit.
     Stable IDs allow Firestore to deduplicate repeated submissions.
     No GPS points, destination names, or route geometry are transmitted. */
  var FounderTripTelemetry=(function(){
    var endpoint="https://drive-iq-founder-iq-api-85238371012.us-central1.run.app/events";
    function record(name,driveId,extra){
      driveId=clean(driveId);
      if(!driveId){return;}
      var eventId="diq_v24_"+name+"_"+driveId.replace(/[^a-zA-Z0-9._:-]/g,"_").slice(0,100);
      var payload={app:"drive_iq",event:name,eventId:eventId,
        occurredAt:new Date().toISOString(),drive_id:driveId,
        source:"drive_mode_milestone_v24",surface:"drive_mode",page:"drive_mode",
        properties:extra||{}};
      try{
        fetch(endpoint,{method:"POST",mode:"cors",cache:"no-store",credentials:"omit",
          headers:{"Content-Type":"application/json"},body:JSON.stringify(payload),keepalive:true})
          .catch(function(){});
      }catch(ignore){}
    }
    return {record:record};
  })();

  /* ==========================================================================
     DRIVE SESSION AUTHORITY
  ========================================================================== */
  var DriveSession = {
    start:function(){
      if(S.session.active){return true;}
      var id=clean(lsGet(LS.ACTIVE_DRIVE_ID)) || ("drive-"+now());
      S.session.id=id; S.session.active=true; S.session.startedAt=now(); S.session.phase="STARTING"; S.session.ended=false;
      lsSet(LS.DRIVE_ON,"1"); lsSet(LS.ACTIVE_DRIVE_ID,id);
      Diagnostics.push("DriveSession","START",{id:id});
      return true;
    },
    setRouteActive:function(active){
      S.session.routeActive=!!active;
      if(active && S.session.phase==="STARTING"){S.session.phase="DEPARTING";}
      Diagnostics.push("DriveSession","ROUTE_ACTIVE",{active:!!active});
    },
    phase:function(next,reason){
      if(!next||next===S.session.phase){return;}
      var old=S.session.phase;S.session.phase=next;
      Diagnostics.push("DriveSession","PHASE",{from:old,to:next,reason:reason||""});
      try{ThreeDWorldAuthority.applyContext("phase:"+next);}catch(e0){}
    },
    end:function(reason){
      if(S.session.ended){return;}
      S.session.ended=true;S.session.active=false;S.session.routeActive=false;S.session.phase="ENDED";
      lsSet(LS.DRIVE_ON,"0");
      try{ if(S.location.watchId!=null && navigator.geolocation){navigator.geolocation.clearWatch(S.location.watchId);} }catch(e){}
      Diagnostics.push("DriveSession","END",{reason:reason||"user"});

      var cleanCompletion=false;
      try{ cleanCompletion=!!ReviewIQ.recordCleanCompletion(reason||"end-drive"); }catch(reviewErr){}

      /*
        V269 DEBUG TEST HARNESS
        Xcode DEBUG builds only: SLXWebViewController V114 injects
        window.DRIVE_IQ_NATIVE_DEBUG_TEST = true.
        That lets End Drive simulate the first clean completion for testing.
        Release/App Store builds never receive this flag, so production still
        requires the real 2-minute / 85%-progress clean completion.
      */
      try{
        if(window.DRIVE_IQ_NATIVE_DEBUG_TEST===true && !cleanCompletion){
          lsSet(LS.COMPLETED_TRIPS,"1");
          lsSet(LS.PLUS_TEASER_SHOWN,"0");
          lsSet(LS.PLUS_TEASER_TAPPED,"0");
          lsSet(LS.REVIEW_LAST_DRIVE_ID,"debug-v269-"+now());
          S.review.completed=true;
          cleanCompletion=true;

          Diagnostics.push("DebugFirstTrip","V269_END_DRIVE_SIMULATED",{
            completedTrips:1,
            reason:reason||"end-drive"
          });
        }
      }catch(debugHarnessErr){
        Diagnostics.error("DebugFirstTrip","V269_SIMULATION_FAILED",{
          message:String(debugHarnessErr&&debugHarnessErr.message||debugHarnessErr)
        });
      }

      sendCarPlayIdle(reason||"end-drive");

      /*
        V263 NATIVE FIRST-TRIP PLUS TEASER
        Native users see a one-time soft Plus introduction after trip #1.
      */
      try{
        if(cleanCompletion && PlusTeaserIQ.maybeShowAfterFirstTrip(reason||"end-drive")){
          return;
        }
      }catch(plusTeaserErr){
        Diagnostics.error("PlusTeaserIQ","END_HANDOFF_FAILED",{
          message:String(plusTeaserErr&&plusTeaserErr.message||plusTeaserErr)
        });
      }

      /*
        V242 WEB FIRST-TRIP INSTALL HANDOFF:
        A genuine first completed trip on the iPhone web experience gets one
        App Store prompt before we return to Start Drive. Native Drive IQ never
        sees this surface.
      */
      try{
        if(cleanCompletion && AppInstallIQ.maybePromptAfterFirstTrip(reason||"end-drive")){
          return;
        }
      }catch(appInstallErr){
        Diagnostics.error("AppInstallIQ","END_HANDOFF_FAILED",{
          message:String(appInstallErr&&appInstallErr.message||appInstallErr)
        });
      }

      DestructiveFirewall.navigate(START_URL,"DriveSession.end");
    }
  };

  /* ==========================================================================
     V246 3D WORLD AUTHORITY — PRESENTATION ONLY

     HARD RULES
     - Mapbox Standard Satellite owns terrain / buildings / landmarks / lighting.
     - Never calls setStyle() during an active drive.
     - Never changes route geometry, GPS, marker, camera, reroute, maneuver, or arrival.
     - CameraAuthority remains the only owner of center / bearing / pitch / zoom.
     - Free tier keeps its existing lightweight Streets presentation.
  ========================================================================== */
  var ThreeDWorldAuthority = {
    enabled:function(){
      return !!CFG.map3DEnabled && !FreeTierIQ.free();
    },

    config:function(){
      return {
        basemap:{
          lightPreset:CFG.map3DLightPreset||"day",
          show3dObjects:true,
          showRoadsAndTransit:true,
          showPedestrianRoads:false,
          showPlaceLabels:true,
          showPointOfInterestLabels:false,
          showRoadLabels:true,
          showTransitLabels:false,
          showAdminBoundaries:false
        }
      };
    },

    context:function(){
      if(!this.enabled()){return "off";}

      var remaining=Math.max(0,num(S.route.totalMiles)-num(S.route.progressMiles));
      var mph=num((S.location.raw||S.location.canonical||{}).speedMph,0);

      if(S.session.phase==="FINAL_APPROACH" || (S.session.routeActive && remaining>0 && remaining<=CFG.map3DArrivalMiles)){
        return "arrival";
      }

      if(S.session.phase==="CRUISING" && mph>=45){
        return "scenic";
      }

      return "baseline";
    },

    applyContext:function(reason){
      var m=S.map.instance;
      if(!this.enabled()||!S.map.loaded||!m){return false;}

      var next=this.context();
      if(next===S.map.threeDContext && reason!=="map-load"){return true;}
      S.map.threeDContext=next;

      try{
        if(typeof m.setConfigProperty==="function"){
          m.setConfigProperty("basemap","show3dObjects",true);

          if(next==="arrival"){
            m.setConfigProperty("basemap","showPointOfInterestLabels",true);
            m.setConfigProperty("basemap","showPlaceLabels",true);
            m.setConfigProperty("basemap","showRoadLabels",true);
          }else if(next==="scenic"){
            m.setConfigProperty("basemap","showPointOfInterestLabels",false);
            m.setConfigProperty("basemap","showPlaceLabels",true);
            m.setConfigProperty("basemap","showRoadLabels",true);
          }else{
            m.setConfigProperty("basemap","showPointOfInterestLabels",false);
            m.setConfigProperty("basemap","showPlaceLabels",true);
            m.setConfigProperty("basemap","showRoadLabels",true);
          }
        }

        Diagnostics.push("ThreeDWorldAuthority","CONTEXT",{
          context:next,
          phase:S.session.phase,
          remainingMiles:remaining,
          speedMph:mph,
          reason:reason||""
        });
        return true;
      }catch(e){
        Diagnostics.error("ThreeDWorldAuthority","CONTEXT_FAILED",{
          context:next,
          message:String(e&&e.message||e),
          reason:reason||""
        });
        return false;
      }
    },

    ensure:function(reason){
      var m=S.map.instance;
      if(!this.enabled()||!S.map.loaded||!m){return false;}

      try{
        if(typeof m.setConfigProperty==="function"){
          m.setConfigProperty("basemap","show3dObjects",true);
          m.setConfigProperty("basemap","showRoadsAndTransit",true);
          m.setConfigProperty("basemap","showPedestrianRoads",false);
          m.setConfigProperty("basemap","showPlaceLabels",true);
          m.setConfigProperty("basemap","showPointOfInterestLabels",false);
          m.setConfigProperty("basemap","showRoadLabels",true);
          m.setConfigProperty("basemap","showTransitLabels",false);
          m.setConfigProperty("basemap","showAdminBoundaries",false);
          m.setConfigProperty("basemap","lightPreset",CFG.map3DLightPreset||"day");
        }

        this.applyContext(reason||"ensure");

        Diagnostics.push("ThreeDWorldAuthority","ENSURED",{
          enabled:true,
          style:CFG.mapStyle,
          lightPreset:CFG.map3DLightPreset||"day",
          context:S.map.threeDContext,
          reason:reason||""
        });
        return true;
      }catch(e){
        Diagnostics.error("ThreeDWorldAuthority","ENSURE_FAILED",{
          message:String(e&&e.message||e),
          reason:reason||""
        });
        return false;
      }
    },

    layerSlot:function(kind){
      if(!this.enabled()){return null;}
      return kind==="radar"?"middle":"top";
    }
  };

  /* ==========================================================================
     MAP AUTHORITY — MAP IS CREATED ONCE AND NEVER RECREATED DURING ACTIVE DRIVE
  ========================================================================== */
  var MapAuthority = {
    createOnce:function(){
      if(S.map.created){return S.map.instance;}
      if(!window.mapboxgl || !MAPBOX_TOKEN){
        Diagnostics.error("MapAuthority","MAPBOX_UNAVAILABLE",{});setSub("Map unavailable");return null;
      }
      var container=byId("diqMap");
      if(!container){Diagnostics.error("MapAuthority","NO_CONTAINER",{id:"diqMap"});return null;}
      try{
        mapboxgl.accessToken=MAPBOX_TOKEN;
        var mapOptions={
          container:"diqMap",
          style:FreeTierIQ.free()?"mapbox://styles/mapbox/streets-v12":CFG.mapStyle,
          center:CFG.fallbackCenter,
          zoom:CFG.fallbackZoom,
          pitch:0,
          bearing:0,
          attributionControl:false
        };
        if(ThreeDWorldAuthority.enabled()){
          mapOptions.config=ThreeDWorldAuthority.config();
        }
        var m=new mapboxgl.Map(mapOptions);
        S.map.instance=m;S.map.created=true;window.DRIVE_IQ_DRIVE_MAP=m;
        Diagnostics.push("MapAuthority","CREATED",{});
        m.on("load",function(){
          S.map.loaded=true;Diagnostics.push("MapAuthority","LOADED",{});
          ThreeDWorldAuthority.ensure("map-load");
          RouteAuthority.ensureLayers();
          WeatherRadarAuthority.ensure("map-load");
          VehicleMarkerAuthority.createOnce();
          ThemePaintAuthority.paint("map-load");
          RouteAuthority.paint();
          RouteAuthority.ensureVisible("map-load");
          VehicleMarkerAuthority.paint();
          CameraAuthority.follow(true);

          // V255 route paint burst: Mapbox Standard can finish internal style work
          // just after the first load callback. Reassert the existing source/layers
          // without rebuilding the map or route.
          [80,260,700].forEach(function(delay){
            window.setTimeout(function(){
              if(S.session.routeActive&&!S.session.ended){
                RouteAuthority.ensureVisible("map-load-"+delay);
                RouteAuthority.paintDestination();
              }
            },delay);
          });
        });
        m.on("idle",function(){
          /* V270.1 MANUAL CAMERA LOCK:
             While the driver is actively manipulating the map, idle/style
             callbacks are not allowed to touch route/map presentation. */
          if(S.camera.gestureActive){return;}
          if(S.map.loaded && S.session.routeActive && !S.session.ended){
            try{
              RouteAuthority.ensureVisible("map-idle");
              RouteAuthority.paintDestination();
            }catch(eIdleRoute){
              Diagnostics.error("RouteAuthority","IDLE_REASSERT_FAILED",{
                message:String(eIdleRoute&&eIdleRoute.message||eIdleRoute)
              });
            }
          }
        });
        m.on("styledata",function(){
          if(!S.map.loaded){return;}

          /* Mapbox Standard can emit styledata repeatedly during pinch zoom,
             rotation, pitch, and camera settling. During a genuine user gesture
             the existing route source/layers stay authoritative and untouched. */
          if(S.camera.gestureActive){
            Diagnostics.push("MapAuthority","STYLEDATA_DEFERRED_USER_GESTURE",{});
            return;
          }

          /* Passive reconciliation only. Do not rewrite basemap config when the
             3D context is already correct. */
          ThreeDWorldAuthority.applyContext("styledata-passive");
          RouteAuthority.ensureLayers();
          WeatherRadarAuthority.ensure("styledata");
          if(S.weatherSafety.currentForecast){
            AtmosphericRainAuthority.syncCurrentForecast(
              S.weatherSafety.currentForecast,
              "styledata"
            );
          }
          ThemePaintAuthority.paint("styledata");
          RouteAuthority.paint(false);
          RouteAuthority.ensureVisible("styledata");
          VehicleMarkerAuthority.paint();
          try{
            ensureStopLayer();
            if(S.stop.results&&S.stop.results.length){StopIQ.render(false);}
          }catch(stopStyleErr){}
        });
        /* V270.1: a real user gesture owns the camera until Mapbox finishes
           inertial settling. During that window route render work is frozen. */
        m.on("dragstart",function(e){CameraAuthority.onMapGestureStart(e,"drag");});
        m.on("zoomstart",function(e){CameraAuthority.onMapGestureStart(e,"zoom");});
        m.on("rotatestart",function(e){CameraAuthority.onMapGestureStart(e,"rotate");});
        m.on("pitchstart",function(e){CameraAuthority.onMapGestureStart(e,"pitch");});
        m.on("dragend",function(){CameraAuthority.onMapGestureEnd("drag");});
        m.on("zoomend",function(){CameraAuthority.onMapGestureEnd("zoom");});
        m.on("rotateend",function(){CameraAuthority.onMapGestureEnd("rotate");});
        m.on("pitchend",function(){CameraAuthority.onMapGestureEnd("pitch");});

        /* V270.2 safety: if a compound pinch/pan gesture ends without every
           component emitting its matching *end event, moveend clears the
           temporary gesture lock after Mapbox finishes inertial movement. */
        m.on("moveend",function(){CameraAuthority.onAllMapGesturesEnd("moveend");});

        m.on("rotate",function(){VehicleMarkerAuthority.orientToCamera();});
        m.on("error",function(e){Diagnostics.error("MapAuthority","MAP_ERROR",{message:e&&e.error?String(e.error):"map error"});});
        return m;
      }catch(e){Diagnostics.error("MapAuthority","CREATE_FAILED",{message:String(e&&e.message||e)});return null;}
    },
    resize:function(reason){
      var m=S.map.instance;if(!m){return;}
      try{m.resize();Diagnostics.push("MapAuthority","RESIZE",{reason:reason||""});}catch(e){}
      try{ThreeDWorldAuthority.applyContext("map-resize");}catch(e0){}
      RouteAuthority.ensureLayers();ThemePaintAuthority.paint("map-resize");RouteAuthority.ensureVisible("map-resize");RouteAuthority.paintDestination();VehicleMarkerAuthority.paint();
    }
  };

  /* ==========================================================================
     VEHICLE MARKER AUTHORITY — ONE PERSISTENT MAPBOX MARKER

     My Drive IQ chooses the artwork. Drive Mode never replaces the Mapbox marker.
     Changing marker style only swaps artwork INSIDE the persistent marker element.
  ========================================================================== */
  var VEHICLE_MARKER_ALLOW={rocket_scout:1,turbo_bolt:1,falcon_wing:1,compass_star:1,rally_rider:1,trail_beast:1};

  function normalizeVehicleMarker(v){
    var x=clean(v).toLowerCase().replace(/[\s-]+/g,"_");
    return VEHICLE_MARKER_ALLOW[x]?x:"rocket_scout";
  }

  function sourceForcesClassicMarker(reason){
    /*
      V252 REAL VEHICLE GARAGE:
      My Drive IQ still stores legacy marker IDs for compatibility, but those IDs
      now represent real vehicles. Do not force classic SVG artwork on preference
      changes; the vehicle authority translates the saved ID into its 3D asset.
      FreeTierIQ still keeps the classic marker path because applyArtwork() does
      not load custom 3D artwork for the free tier.
    */
    return false;
  }

  function readSelected3DVehicle(){
    var url=clean(lsGet(LS.VEHICLE_3D_URL)||"");
    var type=clean(lsGet(LS.VEHICLE_3D_TYPE)||"").toLowerCase();
    var color=clean(lsGet(LS.VEHICLE_3D_COLOR)||"").toLowerCase();
    var marker=normalizeVehicleMarker(lsGet(LS.VEHICLE_MARKER)||S.preferences.vehicleMarker||"rocket_scout");
    var base="https://cdn.shopify.com/s/files/1/0695/7921/0863/files/";
    var garage={
      rocket_scout:{
        url:base+"driveiq-topdown-black-suv.png",
        type:"suv",
        color:"black",
        label:"Black SUV"
      },
      turbo_bolt:{
        url:base+"driveiq-topdown-blue-pickup-truck.png",
        type:"pickup",
        color:"blue",
        label:"Blue Pickup"
      },
      falcon_wing:{
        url:base+"driveiq-topdown-red-sports-coupe.png",
        type:"sports",
        color:"red",
        label:"Red Sports Car"
      },
      compass_star:{
        url:base+"driveiq-topdown-gray-crossover-suv.png",
        type:"crossover",
        color:"gray",
        label:"Gray Crossover"
      },
      rally_rider:{
        url:base+"driveiq-topdown-green-offroad-suv.png",
        type:"offroad",
        color:"green",
        label:"Green Off-Road"
      },
      trail_beast:{
        url:base+"driveiq-topdown-white-cargo-van.png",
        type:"van",
        color:"white",
        label:"White Van"
      }
    };

    /*
      V253 TOP-DOWN VEHICLE PACK:
      Legacy My Drive IQ marker IDs now resolve to the top-down Shopify assets.
      This intentionally overrides stale angled-image URLs left in storage from
      earlier builds. Silver Sedan gets its dedicated top-down asset as well.
    */
    if(type==="sedan" && color==="silver"){
      return {
        url:base+"driveiq-topdown-silver-sedan.png",
        type:"sedan",
        color:"silver",
        label:"Silver Sedan"
      };
    }

    if(garage[marker]){
      return garage[marker];
    }

    if(/^https?:\/\//i.test(url)){
      return {
        url:url,
        type:(type||"vehicle").toLowerCase(),
        color:(color||"").toLowerCase(),
        label:clean(((color?color+" ":"")+(type||"vehicle")).replace(/[\-_]+/g," "))
      };
    }

    return null;
  }

  function markerSvg(name){
    name=normalizeVehicleMarker(name);

    if(name==="turbo_bolt"){
      return '<svg viewBox="0 0 64 64" width="50" height="50" aria-hidden="true"><path d="M38 3 12 36h17l-5 25 28-37H35z" fill="currentColor" stroke="rgba(3,16,22,.95)" stroke-width="4" stroke-linejoin="round"/><path d="M35 12 24 31h11l-3 13 12-17h-9z" fill="var(--diq-marker-secondary)" opacity=".72"/></svg>';
    }
    if(name==="falcon_wing"){
      return '<svg viewBox="0 0 64 64" width="52" height="52" aria-hidden="true"><path d="M32 3 38 28 60 43 39 40 32 61 25 40 4 43 26 28z" fill="currentColor" stroke="rgba(3,16,22,.95)" stroke-width="3.5" stroke-linejoin="round"/><path d="M32 10 35 31 50 39 36 36 32 51 28 36 14 39 29 31z" fill="var(--diq-marker-secondary)" opacity=".62"/><path d="M32 7v35" stroke="rgba(255,255,255,.72)" stroke-width="2" stroke-linecap="round"/></svg>';
    }
    if(name==="compass_star"){
      return '<svg viewBox="0 0 64 64" width="50" height="50" aria-hidden="true"><circle cx="32" cy="32" r="24" fill="rgba(2,12,18,.72)" stroke="currentColor" stroke-width="3"/><path d="M32 4 39 26 60 32 39 38 32 60 25 38 4 32 25 26z" fill="currentColor" stroke="rgba(3,16,22,.95)" stroke-width="2.5" stroke-linejoin="round"/><path d="M32 8 37 29 32 32 27 29z" fill="var(--diq-marker-secondary)"/><circle cx="32" cy="32" r="4" fill="#fff" opacity=".9"/></svg>';
    }
    if(name==="rally_rider"){
      return '<svg viewBox="0 0 64 64" width="50" height="50" aria-hidden="true"><path d="M22 7 27 3h10l5 4 5 13v31l-6 9H23l-6-9V20z" fill="currentColor" stroke="rgba(3,16,22,.95)" stroke-width="3.5" stroke-linejoin="round"/><path d="m24 18 4-8h8l4 8-3 8H27z" fill="rgba(2,14,20,.85)"/><path d="M23 33h18v15H23z" fill="var(--diq-marker-secondary)" opacity=".52"/><rect x="13" y="19" width="5" height="13" rx="2" fill="rgba(3,16,22,.95)"/><rect x="46" y="19" width="5" height="13" rx="2" fill="rgba(3,16,22,.95)"/><rect x="13" y="40" width="5" height="13" rx="2" fill="rgba(3,16,22,.95)"/><rect x="46" y="40" width="5" height="13" rx="2" fill="rgba(3,16,22,.95)"/><circle cx="25" cy="11" r="2.3" fill="#fff"/><circle cx="39" cy="11" r="2.3" fill="#fff"/></svg>';
    }
    if(name==="trail_beast"){
      return '<svg viewBox="0 0 64 64" width="52" height="52" aria-hidden="true"><rect x="13" y="18" width="7" height="16" rx="3" fill="rgba(3,16,22,.98)"/><rect x="44" y="18" width="7" height="16" rx="3" fill="rgba(3,16,22,.98)"/><rect x="13" y="39" width="7" height="16" rx="3" fill="rgba(3,16,22,.98)"/><rect x="44" y="39" width="7" height="16" rx="3" fill="rgba(3,16,22,.98)"/><path d="M23 7 28 3h8l5 4 5 12v35l-6 7H24l-6-7V19z" fill="currentColor" stroke="rgba(3,16,22,.95)" stroke-width="3.5" stroke-linejoin="round"/><path d="M23 20h18l-3-10H26z" fill="rgba(2,14,20,.82)"/><rect x="23" y="32" width="18" height="15" rx="3" fill="var(--diq-marker-secondary)" opacity=".55"/><path d="M20 28h24M20 51h24" stroke="rgba(255,255,255,.55)" stroke-width="2"/><circle cx="25" cy="12" r="2.3" fill="#fff"/><circle cx="39" cy="12" r="2.3" fill="#fff"/></svg>';
    }

    /* Rocket Scout — default */
    return '<svg viewBox="0 0 64 64" width="52" height="52" aria-hidden="true"><path d="M32 2c10 9 14 21 13 35l10 10-13-2-10 17-10-17-13 2 10-10C18 23 22 11 32 2z" fill="currentColor" stroke="rgba(3,16,22,.95)" stroke-width="3.5" stroke-linejoin="round"/><path d="M32 9c5 7 7 14 7 23l-7 12-7-12c0-9 2-16 7-23z" fill="var(--diq-marker-secondary)" opacity=".62"/><circle cx="32" cy="22" r="5" fill="rgba(2,14,20,.88)" stroke="#fff" stroke-width="1.5"/><path d="M27 47 32 61 37 47" fill="var(--diq-marker-secondary)" opacity=".9"/></svg>';
  }

  var VehicleMarkerAuthority = {
    applyArtwork:function(name,reason){
      var custom=null,img=null;
      if(!FreeTierIQ.free()&&!sourceForcesClassicMarker(reason)){
        custom=readSelected3DVehicle();
      }
      if(custom && custom.url){
        S.vehicle.artwork="vehicle_3d";
        S.vehicle.custom3d=custom;
        try{window.DriveIQVehicleMarker="vehicle_3d";}catch(e0){}
        if(!S.vehicle.arrow){return "vehicle_3d";}
        try{
          S.vehicle.arrow.innerHTML="";
          img=document.createElement("img");
          img.src=custom.url;
          img.alt=custom.label||"Vehicle";
          img.decoding="async";
          img.loading="eager";
          img.draggable=false;
          img.referrerPolicy="no-referrer";
          img.style.width="70px";
          img.style.height="70px";
          img.style.objectFit="contain";
          img.style.display="block";
          img.style.pointerEvents="none";
          img.style.userSelect="none";
          img.style.filter="drop-shadow(0 0 6px rgba(0,0,0,.45)) drop-shadow(0 0 14px rgba(0,245,255,.18))";
          S.vehicle.arrow.appendChild(img);
          S.vehicle.arrow.setAttribute("data-diq-marker","vehicle_3d");
          S.vehicle.arrow.setAttribute("data-diq-vehicle-type",custom.type||"");
          S.vehicle.arrow.setAttribute("data-diq-vehicle-color",custom.color||"");
          S.vehicle.arrow.setAttribute("data-diq-vehicle-url",custom.url);
          ThemePaintAuthority.paint(reason||"marker-artwork-3d");
          Diagnostics.push("VehicleMarkerAuthority","ARTWORK_APPLIED",{marker:"vehicle_3d",vehicleType:custom.type||"",vehicleColor:custom.color||"",reason:reason||""});
          return "vehicle_3d";
        }catch(e1){
          Diagnostics.error("VehicleMarkerAuthority","ARTWORK_FAILED",{marker:"vehicle_3d",message:String(e1&&e1.message||e1)});
        }
      }

      S.vehicle.custom3d=null;
      name=normalizeVehicleMarker(name||S.preferences.vehicleMarker||lsGet(LS.VEHICLE_MARKER));
      S.preferences.vehicleMarker=name;
      S.vehicle.artwork=name;
      try{window.DriveIQVehicleMarker=name;}catch(e2){}
      if(!S.vehicle.arrow){return name;}
      try{
        S.vehicle.arrow.innerHTML=markerSvg(name);
        S.vehicle.arrow.setAttribute("data-diq-marker",name);
        S.vehicle.arrow.removeAttribute("data-diq-vehicle-type");
        S.vehicle.arrow.removeAttribute("data-diq-vehicle-color");
        S.vehicle.arrow.removeAttribute("data-diq-vehicle-url");
        ThemePaintAuthority.paint(reason||"marker-artwork");
        Diagnostics.push("VehicleMarkerAuthority","ARTWORK_APPLIED",{marker:name,reason:reason||""});
      }catch(e3){Diagnostics.error("VehicleMarkerAuthority","ARTWORK_FAILED",{marker:name,message:String(e3&&e3.message||e3)});}
      return name;
    },
    createOnce:function(){
      if(S.vehicle.created){return S.vehicle.marker;}
      if(!S.map.loaded||!S.map.instance||!window.mapboxgl){return null;}
      var p=drivePalette();
      var root=document.createElement("div");
      root.setAttribute("data-diq-v200-vehicle","1");
      root.style.width=CFG.markerHaloPx+"px";root.style.height=CFG.markerHaloPx+"px";root.style.position="relative";root.style.pointerEvents="none";
      var halo=document.createElement("div");
      halo.style.position="absolute";halo.style.inset="0";halo.style.borderRadius="50%";halo.style.border=CFG.markerHaloBorderPx+"px solid "+p.glow;halo.style.background=p.glowSoft;halo.style.boxShadow="0 0 14px "+p.glowShadow+", inset 0 0 10px "+p.glowSoft;
      var arrow=document.createElement("div");
      arrow.style.position="absolute";arrow.style.left="50%";arrow.style.top="50%";arrow.style.width="52px";arrow.style.height="52px";arrow.style.display="flex";arrow.style.alignItems="center";arrow.style.justifyContent="center";arrow.style.color=p.route;arrow.style.setProperty("--diq-marker-primary",p.route);arrow.style.setProperty("--diq-marker-secondary",p.destination);arrow.style.transform="translate(-50%,-50%) rotate(0deg)";arrow.style.transformOrigin="50% 50%";arrow.style.willChange="transform";
      root.appendChild(halo);root.appendChild(arrow);
      S.vehicle.arrow=arrow;S.vehicle.halo=halo;S.vehicle.element=root;
      this.applyArtwork(S.preferences.vehicleMarker||lsGet(LS.VEHICLE_MARKER)||"rocket_scout","create");
      try{
        var marker=new mapboxgl.Marker({element:root,anchor:"center",rotationAlignment:"viewport",pitchAlignment:"viewport"}).setLngLat(CFG.fallbackCenter).addTo(S.map.instance);
        S.vehicle.marker=marker;S.vehicle.created=true;
        Diagnostics.push("VehicleMarkerAuthority","CREATED",{haloPx:CFG.markerHaloPx,marker:S.vehicle.artwork,rotationAlignment:"viewport",pitchAlignment:"viewport"});
        return marker;
      }catch(e){Diagnostics.error("VehicleMarkerAuthority","CREATE_FAILED",{message:String(e&&e.message||e)});return null;}
    },
    heading:function(){
      var f=S.location.canonical||S.location.raw;
      var mph=num((S.location.raw||f||{}).speedMph);
      var candidate=null;

      /* V220 FORWARD-HEADING AUTHORITY
         - At driving speed, GPS course is authoritative.
         - Smooth only across the shortest angular path, so 359° -> 1° never flips.
         - Below driving speed, hold the last trustworthy heading instead of letting
           noisy GPS / route projection spin the vehicle around.
         - Before the first trustworthy course exists, seed from route geometry. */
      if(f&&finite(Number(f.heading))&&mph>=CFG.cameraHeadingMph){
        candidate=normBearing(Number(f.heading));
        if(S.vehicle.headingReady&&finite(Number(S.vehicle.lastHeading))){
          candidate=normBearing(Number(S.vehicle.lastHeading)+angleDelta(Number(S.vehicle.lastHeading),candidate)*0.42);
        }
        S.vehicle.lastHeading=candidate;
        S.vehicle.headingReady=true;
      }else if(!S.vehicle.headingReady && S.route.coords.length>1 && S.route.progressIndex<S.route.coords.length-1){
        var a=S.route.coords[Math.max(0,S.route.progressIndex)];
        var b=S.route.coords[Math.min(S.route.coords.length-1,S.route.progressIndex+3)];
        candidate=bearing({lng:a[0],lat:a[1]},{lng:b[0],lat:b[1]});
        S.vehicle.lastHeading=candidate;
        S.vehicle.headingReady=true;
      }

      if(!S.vehicle.headingReady||!finite(Number(S.vehicle.lastHeading))){S.vehicle.lastHeading=0;}
      return normBearing(Number(S.vehicle.lastHeading));
    },
    orientToCamera:function(){
      if(!S.vehicle.arrow){return false;}
      var h=this.heading(),mapBearing=0,relative=0;
      /* V220 SCREEN-FORWARD CONTRACT
         The Mapbox marker container is viewport-aligned, so it can never inherit
         map/camera rotation. In FOLLOW, every approved vehicle artwork points
         straight toward the top of the screen. In FREE view, the artwork alone
         rotates relative to the current map bearing to show real travel direction. */
      if(S.camera.mode==="FOLLOW"){
        relative=0;
      }else{
        try{if(S.map.instance&&S.map.instance.getBearing){mapBearing=normBearing(S.map.instance.getBearing());}}catch(e1){}
        relative=angleDelta(mapBearing,h);
      }
      try{
        S.vehicle.arrow.style.transform="translate(-50%,-50%) rotate("+relative+"deg)";
        S.vehicle.arrow.setAttribute("data-diq-screen-heading",String(Math.round(normBearing(h))));
        return true;
      }catch(e2){return false;}
    },
    paint:function(){
      if(!S.vehicle.created){this.createOnce();}
      var m=S.vehicle.marker,f=S.location.canonical||S.location.lastValid;
      if(!m||!f){return false;}
      try{m.setLngLat([f.lng,f.lat]);}catch(e1){}
      this.orientToCamera();
      try{S.vehicle.element.style.display="block";S.vehicle.element.style.opacity="1";}catch(e3){}
      return true;
    }
  };

  /* ==========================================================================
     V230 SPEED LIMIT AUTHORITY — VERIFIED CURRENT ROAD ONLY

     Safety contract:
     - Never infer a posted limit from route progress or a future route segment.
     - Saved/restored GPS does not qualify.
     - Use live raw GPS trace + Mapbox Map Matching maxspeed.
     - Latest tracepoint must itself match close to GPS with acceptable confidence.
     - Respect Mapbox's explicit speed unit. Never assume units.
     - Unknown / low confidence / stale / poor GPS = "--".
     - Presentation only. Never changes route, reroute, camera, or maneuver state.
  ========================================================================== */
  var SpeedLimitAuthority = {
    reset:function(reason){
      S.speedLimit.trace=[];
      S.speedLimit.busy=false;
      S.speedLimit.lastQueryAt=0;
      S.speedLimit.lastVerifiedAt=0;
      S.speedLimit.lastQueryFix=null;
      S.speedLimit.road="";
      S.speedLimit.confidence=null;
      S.speedLimit.source="";
      S.route.speedLimitMph=null;
      setSpeedLimit(null);
      Diagnostics.push("SpeedLimitAuthority","RESET",{reason:reason||""});
    },

    clear:function(reason){
      S.route.speedLimitMph=null;
      S.speedLimit.road="";
      S.speedLimit.confidence=null;
      S.speedLimit.source="";
      setSpeedLimit(null);
      Diagnostics.push("SpeedLimitAuthority","CLEAR",{reason:reason||""});
    },

    mphFromMaxspeed:function(maxspeed){
      var speed,unit;
      if(!maxspeed||maxspeed.unknown===true||maxspeed.none===true){return null;}
      speed=Number(maxspeed.speed);
      unit=clean(maxspeed.unit).toLowerCase();
      if(!finite(speed)||speed<=0){return null;}
      if(unit==="mph"){return Math.round(speed);}
      if(unit==="km/h"||unit==="kmh"||unit==="kph"){return Math.round(speed*0.621371);}
      return null;
    },

    latestMatchedMaxspeed:function(matching,tp){
      var legs,legIndex,leg,ann,maxspeeds,idx;

      /*
        V231 FIX:
        Mapbox's annotations=maxspeed is returned on
        matching.legs[].annotation.maxspeed[], not on tracepoints.

        The newest GPS point is the end of this rolling trace, so use the
        final segment annotation of the leg ending at that tracepoint.

        Do not walk backward to an older known limit. If the current segment
        is unknown, Drive IQ should show "--".
      */
      if(!matching){return null;}

      legs=matching.legs||[];
      if(!legs.length){return null;}

      legIndex=legs.length-1;

      if(tp&&finite(Number(tp.waypoint_index))){
        idx=Math.floor(Number(tp.waypoint_index))-1;
        if(idx>=0&&idx<legs.length){legIndex=idx;}
      }

      leg=legs[legIndex]||null;
      ann=leg&&leg.annotation?leg.annotation:null;
      maxspeeds=ann&&ann.maxspeed?ann.maxspeed:null;

      if(!maxspeeds||!maxspeeds.length){return null;}

      return maxspeeds[maxspeeds.length-1]||null;
    },

    appendTrace:function(raw){
      var trace=S.speedLimit.trace,last;
      if(!raw){return;}
      last=trace.length?trace[trace.length-1]:null;

      /* Replace tiny GPS jitter instead of treating it as travel. */
      if(last&&haversineMiles(last,raw)<0.002){
        trace[trace.length-1]={lat:raw.lat,lng:raw.lng,accuracy:raw.accuracy,ts:raw.ts};
        return;
      }

      trace.push({lat:raw.lat,lng:raw.lng,accuracy:raw.accuracy,ts:raw.ts});
      while(trace.length>CFG.speedLimitTraceMax){trace.shift();}
    },

    shouldQuery:function(raw,routeProjection){
      var t=now(),last=S.speedLimit.lastQueryFix,trace=S.speedLimit.trace;

      if(!S.session.routeActive||S.arrival.arrived){return false;}
      if(!MAPBOX_TOKEN){return false;}
      if(!raw||!finite(raw.lat)||!finite(raw.lng)){return false;}
      if(!finite(Number(raw.accuracy))||Number(raw.accuracy)<=0||Number(raw.accuracy)>CFG.speedLimitMaxAccuracyMeters){return false;}
      if(!routeProjection||routeProjection.distMiles>CFG.speedLimitRouteSnapMiles){return false;}
      if(trace.length<2){return false;}
      if(haversineMiles(trace[0],trace[trace.length-1])<CFG.speedLimitMinTraceMoveMiles){return false;}
      if(S.speedLimit.busy){return false;}
      if(t-S.speedLimit.lastQueryAt<CFG.speedLimitPollMs){return false;}
      if(last&&haversineMiles(last,raw)<CFG.speedLimitMinTraceMoveMiles&&t-S.speedLimit.lastVerifiedAt<CFG.speedLimitStaleMs){return false;}

      return true;
    },

    request:function(raw){
      var trace=S.speedLimit.trace.slice(-CFG.speedLimitTraceMax);
      var coords=[],i,url;

      for(i=0;i<trace.length;i++){
        coords.push(Number(trace[i].lng).toFixed(6)+","+Number(trace[i].lat).toFixed(6));
      }

      if(coords.length<2){return false;}

      url="https://api.mapbox.com/matching/v5/mapbox/driving/"+
        coords.join(";")+
        ".json?annotations=maxspeed&overview=full&geometries=geojson&tidy=true&access_token="+
        encodeURIComponent(MAPBOX_TOKEN);

      S.speedLimit.busy=true;
      S.speedLimit.lastQueryAt=now();
      S.speedLimit.lastQueryFix={lat:raw.lat,lng:raw.lng};

      fetch(url).then(function(res){
        if(!res.ok){throw new Error("HTTP "+res.status);}
        return res.json();
      }).then(function(data){
        var tracepoints=data&&data.tracepoints?data.tracepoints:[];
        var lastIndex=tracepoints.length-1;
        var tp=lastIndex>=0?tracepoints[lastIndex]:null;
        var mi,matching,confidence,dist,mph;

        S.speedLimit.busy=false;

        /* The current/latest point must match. Never reuse an older road. */
        if(!tp){
          SpeedLimitAuthority.clear("latest-tracepoint-unmatched");
          return;
        }

        dist=Number(tp.distance);
        if(!finite(dist)||dist>CFG.speedLimitMaxTracepointDistanceMeters){
          SpeedLimitAuthority.clear("road-match-too-far");
          return;
        }

        mi=Number(tp.matchings_index);
        matching=data&&data.matchings&&finite(mi)?data.matchings[mi]:null;
        confidence=matching&&finite(Number(matching.confidence))?Number(matching.confidence):null;

        if(confidence===null||confidence<CFG.speedLimitMinConfidence){
          SpeedLimitAuthority.clear("low-map-match-confidence");
          return;
        }

        var latestMaxspeed=SpeedLimitAuthority.latestMatchedMaxspeed(matching,tp);
        mph=SpeedLimitAuthority.mphFromMaxspeed(latestMaxspeed);

        if(!finite(Number(mph))||Number(mph)<=0){
          Diagnostics.push("SpeedLimitAuthority","NO_CURRENT_MAXSPEED",{
            road:clean(tp.name||""),
            waypointIndex:finite(Number(tp.waypoint_index))?Number(tp.waypoint_index):null,
            matchingLegs:matching&&matching.legs?matching.legs.length:0,
            maxspeed:latestMaxspeed||null
          });
          SpeedLimitAuthority.clear("unknown-posted-limit");
          return;
        }

        S.route.speedLimitMph=Number(mph);
        S.speedLimit.lastVerifiedAt=now();
        S.speedLimit.road=clean(tp.name||"");
        S.speedLimit.confidence=confidence;
        S.speedLimit.source="mapbox-map-matching";
        setSpeedLimit(S.route.speedLimitMph);

        Diagnostics.push("SpeedLimitAuthority","VERIFIED",{
          mph:S.route.speedLimitMph,
          road:S.speedLimit.road,
          confidence:confidence,
          matchDistanceMeters:dist
        });

        sendCarPlaySnapshot("speed-limit");
      }).catch(function(err){
        S.speedLimit.busy=false;
        if(now()-S.speedLimit.lastVerifiedAt>CFG.speedLimitStaleMs){
          SpeedLimitAuthority.clear("map-match-error");
        }
        Diagnostics.error("SpeedLimitAuthority","REQUEST_FAILED",{
          message:String(err&&err.message||err)
        });
      });

      return true;
    },

    observe:function(raw,routeProjection){
      this.appendTrace(raw);

      if(!S.session.routeActive||S.arrival.arrived){
        this.clear("no-active-route");
        return false;
      }

      if(!raw||!finite(Number(raw.accuracy))||Number(raw.accuracy)<=0||Number(raw.accuracy)>CFG.speedLimitMaxAccuracyMeters){
        this.clear("gps-not-accurate-enough");
        return false;
      }

      if(!routeProjection||routeProjection.distMiles>CFG.speedLimitRouteSnapMiles){
        this.clear("not-confidently-on-route");
        return false;
      }

      if(S.speedLimit.lastVerifiedAt&&now()-S.speedLimit.lastVerifiedAt>CFG.speedLimitStaleMs){
        this.clear("verified-limit-stale");
      }

      if(this.shouldQuery(raw,routeProjection)){
        return this.request(raw);
      }

      return false;
    },

    status:function(){
      return {
        mph:S.route.speedLimitMph,
        road:S.speedLimit.road,
        confidence:S.speedLimit.confidence,
        source:S.speedLimit.source,
        lastVerifiedAt:S.speedLimit.lastVerifiedAt,
        tracePoints:S.speedLimit.trace.length,
        busy:S.speedLimit.busy
      };
    }
  };

  /* ==========================================================================
     ROUTE AUTHORITY — ONE GEOMETRY OWNER, UPDATE SOURCE IN PLACE
  ========================================================================== */
  var RouteAuthority = {
    loadFromStorage:function(reason){
      var raw=lsGet(LS.ROUTE_GEO),
          payload=parse(lsGet(LS.DRIVE_PAYLOAD)),
          canonicalGeo=parse(raw),
          payloadGeo=payload&&payload.route?payload.route:null,
          intel=parse(lsGet(LS.ROUTE_INTEL)),
          dest=parse(lsGet(LS.DEST));

      /*
        V257 CANONICAL ROUTE GEOMETRY LOCK:
        diq_route_geo_v1 owns the full road-shaped route used by the map.
        The live drive payload may contain a clipped / remaining / lightweight
        geometry for status export. It must NEVER replace a valid canonical route.
      */
      var canonicalCoords=routeCoords(canonicalGeo);
      var payloadCoords=routeCoords(payloadGeo);
      var geo=canonicalCoords.length>=2?canonicalGeo:(payloadCoords.length>=2?payloadGeo:null);

      if(payload&&payload.route_intel){intel=payload.route_intel;}
      if(payload&&payload.destination){dest=payload.destination;}

      if(!geo||!routeCoords(geo).length){
        SpeedLimitAuthority.reset("no-route");
        Diagnostics.push("RouteAuthority","NO_ROUTE",{reason:reason||""});
        return false;
      }

      Diagnostics.push("RouteAuthority","GEOMETRY_SOURCE",{
        reason:reason||"storage",
        source:canonicalCoords.length>=2?"canonical-route-geo":"payload-fallback",
        canonicalPoints:canonicalCoords.length,
        payloadPoints:payloadCoords.length
      });

      return this.install(geo,intel,dest,reason||"storage");
    },
    install:function(geo,intel,dest,reason){
      var sig=geometrySignature(geo),same=!!(S.route.signature&&sig===S.route.signature);
      S.route.intel=intel||S.route.intel||null;S.route.destination=dest||S.route.destination||null;
      if(same){
        S.route.geo=geo;
        Diagnostics.push("RouteAuthority","METADATA_UPDATE",{reason:reason||""});
        this.ensureLayers();
        this.paint();
        this.paintDestination();
        this.ensureVisible("same-geometry-refresh");
        return true;
      }
      SpeedLimitAuthority.reset("route-geometry-install");
      var coords=routeCoords(geo);if(coords.length<2){return false;}
      S.route.replacing=true;S.route.geo=geo;S.route.signature=sig;S.route.coords=coords;S.route.totalMiles=cumulativeMiles(coords,coords.length-1);
      S.route.progressIndex=0;S.route.progressMiles=0;S.route.replacing=false;
      DriveSession.setRouteActive(true);
      FounderTripTelemetry.record("trip_started",S.session.id,{milestone:"route_active"});
      Diagnostics.push("RouteAuthority","GEOMETRY_INSTALL",{reason:reason||"",points:coords.length,miles:S.route.totalMiles});
      this.ensureLayers();this.paint(true);this.paintDestination();this.ensureVisible("geometry-install");
      S.maneuver.instructionFetched=false;
      S.maneuver.instructionFetch=false;
      S.maneuver.instructionRouteSig="";
      S.maneuver.instructionError="";
      ManeuverAuthority.rebuild();
      try{ IncidentIQ.refresh("route-install"); }catch(incidentErr){}
      try{ if(typeof WeatherSafetyAuthority!=="undefined"){WeatherSafetyAuthority.refresh("route-install");} }catch(weatherRouteErr){}
      return true;
    },
    ensureLayers:function(){
      var m=S.map.instance,p=drivePalette();if(!S.map.loaded||!m){return;}
      try{
        var routeSlot=ThreeDWorldAuthority.layerSlot("route");
        if(!m.getSource("diq-v200-route")){
          m.addSource("diq-v200-route",{type:"geojson",data:{type:"FeatureCollection",features:[]}});
          /* A genuinely new Mapbox source has no route geometry yet. */
          S.route.renderedSig="";
        }
        if(!m.getLayer("diq-v200-route-casing")){
          var casingLayer={id:"diq-v200-route-casing",type:"line",source:"diq-v200-route",layout:{"line-cap":"round","line-join":"round"},paint:{"line-color":"rgba(0,0,0,.78)","line-width":13,"line-opacity":.92}};
          if(routeSlot){casingLayer.slot=routeSlot;}
          m.addLayer(casingLayer);
        }
        if(!m.getLayer("diq-v200-route")){
          var routeLayer={id:"diq-v200-route",type:"line",source:"diq-v200-route",layout:{"line-cap":"round","line-join":"round"},paint:{"line-color":p.route,"line-width":7,"line-opacity":1}};
          if(routeSlot){routeLayer.slot=routeSlot;}
          m.addLayer(routeLayer);
        }
        if(!m.getSource("diq-v200-dest")){m.addSource("diq-v200-dest",{type:"geojson",data:{type:"FeatureCollection",features:[]}});}
        if(!m.getLayer("diq-v200-dest")){
          var destLayer={id:"diq-v200-dest",type:"circle",source:"diq-v200-dest",paint:{"circle-radius":8,"circle-color":p.destination,"circle-stroke-width":3,"circle-stroke-color":"#06131c"}};
          if(routeSlot){destLayer.slot=routeSlot;}
          m.addLayer(destLayer);
        }
      }catch(e){Diagnostics.error("RouteAuthority","LAYER_ERROR",{message:String(e&&e.message||e)});}
    },
    paint:function(force){
      var m=S.map.instance;
      if(!S.map.loaded||!m||!S.route.geo||S.route.coords.length<2){return false;}
      this.ensureLayers();
      try{
        var src=m.getSource("diq-v200-route");
        if(src&&src.setData){
          /*
            V270 ROUTE RENDER LOCK

            The route source must NOT be re-fed during camera-only activity.
            Mapbox Standard can emit styledata while pinch-zooming, changing
            pitch/zoom, or recentering Follow Lock. V269 was calling setData()
            from styledata and then again from ensureVisible(), which could make
            the GeoJSON source reparse for a frame and visually blink.

            Only write canonical geometry when:
              1) the source is new/recreated,
              2) accepted route geometry actually changed, or
              3) an explicit forced paint is requested.

            Camera movement, pinch zoom, Follow Lock, theme/styledata refreshes,
            and visibility reassertions never replace route source data.
          */
          var renderSig=S.route.signature||geometrySignature(S.route.geo);
          var sourceSig=String(S.route.renderedSig||"");

          if(!force && renderSig && sourceSig===renderSig){
            Diagnostics.push("RouteAuthority","PAINT_SKIPPED_ALREADY_RENDERED",{
              points:S.route.coords.length,
              reason:"same-canonical-geometry"
            });
            return true;
          }

          var displayGeo={
            type:"Feature",
            properties:{driveIQRoute:true},
            geometry:{
              type:"LineString",
              coordinates:S.route.coords.slice()
            }
          };

          src.setData(displayGeo);
          S.route.renderedSig=renderSig||"canonical";

          Diagnostics.push("RouteAuthority","PAINT_CANONICAL_COORDS",{
            points:S.route.coords.length,
            miles:S.route.totalMiles,
            forced:!!force
          });
          return true;
        }
      }catch(e){
        Diagnostics.error("RouteAuthority","PAINT_FAILED",{message:String(e&&e.message||e)});
      }
      return false;
    },
    ensureVisible:function(reason){
      var m=S.map.instance;
      if(!S.map.loaded||!m||!S.route.geo){return false;}
      try{
        this.ensureLayers();
        var casing=m.getLayer("diq-v200-route-casing");
        var route=m.getLayer("diq-v200-route");
        if(casing){
          m.setLayoutProperty("diq-v200-route-casing","visibility","visible");
          m.setPaintProperty("diq-v200-route-casing","line-opacity",0.92);
        }
        if(route){
          m.setLayoutProperty("diq-v200-route","visibility","visible");
          m.setPaintProperty("diq-v200-route","line-opacity",1);
        }
        /*
          V270: visibility/paint reassertion is presentation-only.
          Never call RouteAuthority.paint() here. Camera/style events may invoke
          ensureVisible frequently and must not cause GeoJSON setData().
        */
        ThemePaintAuthority.paint(reason||"route-visible");
        Diagnostics.push("RouteAuthority","VISIBLE_REASSERTED",{
          reason:reason||"",
          points:S.route.coords.length,
          routeActive:S.session.routeActive
        });
        return true;
      }catch(e){
        Diagnostics.error("RouteAuthority","VISIBLE_REASSERT_FAILED",{
          reason:reason||"",
          message:String(e&&e.message||e)
        });
        return false;
      }
    },
    destinationFix:function(){
      var d=S.route.destination;
      if(d&&finite(Number(d.lat))&&finite(Number(d.lng))){return {lat:Number(d.lat),lng:Number(d.lng),label:clean(d.label||d.name||"Destination")};}
      var c=S.route.coords;if(c.length){var x=c[c.length-1];return {lat:x[1],lng:x[0],label:"Destination"};}
      return null;
    },
    paintDestination:function(){
      var m=S.map.instance,d=this.destinationFix();if(!S.map.loaded||!m||!d){return;}
      this.ensureLayers();
      try{var src=m.getSource("diq-v200-dest");if(src&&src.setData){src.setData({type:"FeatureCollection",features:[{type:"Feature",properties:{},geometry:{type:"Point",coordinates:[d.lng,d.lat]}}]});}}catch(e){}
    },
    remainingMiles:function(){ return Math.max(0,S.route.totalMiles-S.route.progressMiles); },
    progressRatio:function(){ return S.route.totalMiles>0?clamp(S.route.progressMiles/S.route.totalMiles,0,1):0; },
    updateProgress:function(fix){
      if(!fix||S.route.coords.length<2){return null;}
      var p=nearestProjection(fix,S.route.coords,S.route.progressIndex);
      if(!p){return null;}
      /* Prevent noisy backward jumps. */
      if(p.index+3<S.route.progressIndex && p.distMiles<CFG.maxSnapMiles){p=nearestProjection(fix,S.route.coords,null)||p;}
      if(p.distMiles<=CFG.maxSnapMiles){
        S.route.progressIndex=Math.max(S.route.progressIndex,p.index);
        S.route.progressMiles=Math.max(S.route.progressMiles,distanceAlongProjection(p,S.route.coords));
      }
      return p;
    },
    pollStorage:function(){
      if(S.arrival.arrived){return;}

      var payload=parse(lsGet(LS.DRIVE_PAYLOAD));
      var canonicalGeo=parse(lsGet(LS.ROUTE_GEO));
      var payloadGeo=payload&&payload.route?payload.route:null;
      var intel=parse(lsGet(LS.ROUTE_INTEL));
      var dest=parse(lsGet(LS.DEST));

      if(payload&&payload.route_intel){intel=payload.route_intel;}
      if(payload&&payload.destination){dest=payload.destination;}

      /*
        V262 ACTIVE ROUTE GEOMETRY LOCK

        Once active guidance has accepted a route, storage polling is METADATA ONLY.
        A background writer may publish a clipped / simplified / remaining segment
        into diq_route_geo_v1. That must never replace the in-memory route being drawn.

        Legitimate route replacements already call RouteAuthority.install(...) directly
        from reroute / add-stop / traffic-accept paths. So during an active route:
          - keep S.route.geo / S.route.coords untouched
          - refresh intel + destination only
          - never install geometry from the 650 ms storage poller
      */
      if(S.session.routeActive && S.route.signature && S.route.coords && S.route.coords.length>=2){
        S.route.intel=intel||S.route.intel;
        S.route.destination=dest||S.route.destination;
        this.paintDestination();

        var canonicalCoords=routeCoords(canonicalGeo);
        var payloadCoords=routeCoords(payloadGeo);
        var incomingGeo=canonicalCoords.length>=2?canonicalGeo:(payloadCoords.length>=2?payloadGeo:null);
        var incomingSig=incomingGeo?geometrySignature(incomingGeo):"";

        if(incomingSig && incomingSig!==S.route.signature){
          Diagnostics.push("RouteAuthority","STORAGE_GEOMETRY_IGNORED_ACTIVE_LOCK",{
            activePoints:S.route.coords.length,
            incomingCanonicalPoints:canonicalCoords.length,
            incomingPayloadPoints:payloadCoords.length,
            rerouteState:S.reroute.state
          });
        }
        return;
      }

      /* Boot / pre-guidance only: storage may seed the initial route. */
      var canonicalCoordsBoot=routeCoords(canonicalGeo);
      var payloadCoordsBoot=routeCoords(payloadGeo);
      var geo=canonicalCoordsBoot.length>=2?canonicalGeo:(payloadCoordsBoot.length>=2?payloadGeo:null);
      if(!geo){return;}

      var sig=geometrySignature(geo);
      if(!sig){return;}

      if(sig===S.route.signature){
        S.route.intel=intel||S.route.intel;
        S.route.destination=dest||S.route.destination;
        this.paintDestination();
        return;
      }

      Diagnostics.push("RouteAuthority","BOOT_STORAGE_GEOMETRY_INSTALL",{
        canonicalPoints:canonicalCoordsBoot.length,
        payloadPoints:payloadCoordsBoot.length,
        chosen:canonicalCoordsBoot.length>=2?"canonical-route-geo":"payload-fallback"
      });

      this.install(geo,intel,dest,"boot-storage-geometry");
    }
  };

  /* ==========================================================================
     LOCATION AUTHORITY — ONE CANONICAL POSITION
  ========================================================================== */
  var LocationAuthority = {
    accept:function(pos,source){
      var c=pos&&pos.coords?pos.coords:pos||{},lat=Number(c.latitude!=null?c.latitude:c.lat),lng=Number(c.longitude!=null?c.longitude:c.lng);
      if(!finite(lat)||!finite(lng)){return false;}
      var raw={lat:lat,lng:lng,accuracy:num(c.accuracy!=null?c.accuracy:c.acc,0),heading:finite(Number(c.heading))?normBearing(Number(c.heading)):null,speedMph:Math.max(0,num(c.speed,0)*2.23694),ts:now(),source:source||"gps"};
      S.location.raw=raw;S.location.lastAcceptedAt=raw.ts;lsJson(LS.LAST_FIX,{lat:raw.lat,lng:raw.lng,acc:raw.accuracy,heading:raw.heading,speed:num(c.speed,0),ts:raw.ts});
      var p=RouteAuthority.updateProgress(raw),canonical=raw;
      if(p&&p.distMiles<=CFG.maxSnapMiles){canonical={lat:p.lat,lng:p.lng,accuracy:raw.accuracy,heading:raw.heading,speedMph:raw.speedMph,ts:raw.ts,source:"route-snap",rawDistanceMiles:p.distMiles};}
      S.location.canonical=canonical;S.location.lastValid=canonical;
      setGps(raw.accuracy?"±"+Math.round(raw.accuracy)+"m":"LOCKED");setSpeed(raw.speedMph);
      SpeedLimitAuthority.observe(raw,p);
      Diagnostics.push("LocationAuthority","FIX",{source:canonical.source,accuracy:raw.accuracy,speedMph:Math.round(raw.speedMph),routeDist:p?p.distMiles:null});
      VehicleMarkerAuthority.paint();CameraAuthority.follow(false);ManeuverAuthority.tick();RerouteAuthority.observe(raw,p);ArrivalAuthority.tick();HUD.paint();sendCarPlaySnapshot("gps");
      return true;
    },
    restoreSaved:function(){
      var f=parse(lsGet(LS.LAST_FIX));if(!f){return false;}
      var ts=num(f.ts,0);if(!finite(Number(f.lat))||!finite(Number(f.lng))||now()-ts>CFG.savedFixFreshMs){return false;}
      var raw={lat:Number(f.lat),lng:Number(f.lng),accuracy:num(f.acc),heading:finite(Number(f.heading))?normBearing(Number(f.heading)):null,speedMph:num(f.speed)*2.23694,ts:ts,source:"saved"};
      S.location.raw=raw;S.location.lastValid=raw;S.location.canonical=raw;RouteAuthority.updateProgress(raw);SpeedLimitAuthority.clear("saved-fix-not-live");VehicleMarkerAuthority.paint();Diagnostics.push("LocationAuthority","RESTORED_SAVED",{});return true;
    },
    start:function(){
      if(!navigator.geolocation){setGps("NO GPS");Diagnostics.error("LocationAuthority","GEOLOCATION_UNAVAILABLE",{});return false;}
      setGps("LOCKING");setSpeedLimit(null);
      try{navigator.geolocation.getCurrentPosition(function(p){LocationAuthority.accept(p,"initial");},function(e){Diagnostics.error("LocationAuthority","INITIAL_ERROR",{code:e&&e.code});},{enableHighAccuracy:true,timeout:18000,maximumAge:0});}catch(e1){}
      if(S.location.watchId==null){
        try{S.location.watchId=navigator.geolocation.watchPosition(function(p){LocationAuthority.accept(p,"watch");},function(e){Diagnostics.error("LocationAuthority","WATCH_ERROR",{code:e&&e.code});},{enableHighAccuracy:true,timeout:22000,maximumAge:3000});Diagnostics.push("LocationAuthority","WATCH_STARTED",{id:S.location.watchId});}catch(e2){}
      }
      return true;
    }
  };

  /* ==========================================================================
     CAMERA AUTHORITY — CAMERA CANNOT MUTATE VEHICLE STATE
  ========================================================================== */
  var CameraAuthority = {
    enterFree:function(reason,explicit){
      if(!S.session.active){return false;}
      /* Active guidance is FOLLOW by default. Only an actual user map gesture
         or an explicit camera control may unlock it. */
      if(!explicit){
        Diagnostics.push("CameraAuthority","FREE_REJECTED_NON_USER",{reason:reason||"programmatic"});
        return false;
      }
      S.camera.mode="FREE";S.camera.userFreeAt=now();
      Diagnostics.push("CameraAuthority","FREE",{reason:reason||"gesture"});
      setSub("Free View • tap Follow to recenter");
      return true;
    },
    onMapGestureStart:function(e,reason){
      /* Programmatic easeTo/fitBounds camera events do not carry originalEvent.
         This prevents Drive IQ's own follow animation from disabling FOLLOW. */
      if(e&&e.originalEvent){
        reason=reason||"gesture";
        if(S.camera.gestureSettleTimer){
          window.clearTimeout(S.camera.gestureSettleTimer);
          S.camera.gestureSettleTimer=null;
        }
        S.camera.gestureKinds[reason]=true;
        S.camera.gestureActive=true;

        /*
          V270.2 GESTURE INPUT FIX

          DO NOT call map.stop() from zoomstart / dragstart / rotatestart /
          pitchstart. Mapbox fires these events as the interaction is beginning;
          stopping the map here can cancel the same pinch/pan gesture the driver
          is trying to perform.

          Entering FREE is enough to stop Drive IQ's follow loop because
          CameraAuthority.follow() refuses to run unless mode === "FOLLOW".
          The user's native Mapbox gesture remains fully interactive.
        */
        Diagnostics.push("CameraAuthority","USER_GESTURE_LOCK",{kind:reason});
        return this.enterFree(reason,true);
      }
      Diagnostics.push("CameraAuthority","PROGRAMMATIC_CAMERA_EVENT",{reason:reason||"camera"});
      return false;
    },
    onMapGestureEnd:function(reason){
      reason=reason||"gesture";
      S.camera.gestureKinds[reason]=false;

      var k,stillActive=false;
      for(k in S.camera.gestureKinds){
        if(Object.prototype.hasOwnProperty.call(S.camera.gestureKinds,k)&&S.camera.gestureKinds[k]){
          stillActive=true;
          break;
        }
      }
      if(stillActive){return false;}

      if(S.camera.gestureSettleTimer){window.clearTimeout(S.camera.gestureSettleTimer);}

      function settle(){
        var m=S.map.instance;
        try{
          if(m&&typeof m.isMoving==="function"&&m.isMoving()){
            S.camera.gestureSettleTimer=window.setTimeout(settle,120);
            return;
          }
        }catch(moveErr){}

        S.camera.gestureActive=false;
        S.camera.gestureSettleTimer=null;
        Diagnostics.push("CameraAuthority","USER_GESTURE_SETTLED",{mode:S.camera.mode});

        /* One reconciliation after the gesture. If Mapbox kept the route source,
           paint(false) is a no-op. If the source genuinely disappeared, it is
           restored once here—not repeatedly while the driver is zooming. */
        if(S.map.loaded&&m&&S.session.routeActive&&!S.session.ended){
          try{
            RouteAuthority.ensureLayers();
            RouteAuthority.paint(false);
            RouteAuthority.ensureVisible("gesture-settled");
            RouteAuthority.paintDestination();
            ThemePaintAuthority.paint("gesture-settled");
            VehicleMarkerAuthority.paint();
          }catch(reconcileErr){
            Diagnostics.error("CameraAuthority","GESTURE_RECONCILE_FAILED",{
              message:String(reconcileErr&&reconcileErr.message||reconcileErr)
            });
          }
        }
      }

      S.camera.gestureSettleTimer=window.setTimeout(settle,180);
      return true;
    },
    onAllMapGesturesEnd:function(reason){
      var k;
      if(!S.camera.gestureActive){return false;}

      for(k in S.camera.gestureKinds){
        if(Object.prototype.hasOwnProperty.call(S.camera.gestureKinds,k)){
          S.camera.gestureKinds[k]=false;
        }
      }

      Diagnostics.push("CameraAuthority","GESTURE_END_FALLBACK",{
        reason:reason||"moveend"
      });

      return this.onMapGestureEnd(reason||"moveend");
    },
    forceFollow:function(){
      /*
        V261 FOLLOW CAMERA ONLY:
        Follow Lock is strictly a camera action.
        It must never touch route geometry, route layers, route source data,
        theme paint, maneuver state, reroute state, or session state.

        The route is already alive and visible before the driver taps Follow.
        Repainting / setData during the recenter was the destabilizing action.
      */
      if(S.camera.gestureSettleTimer){
        window.clearTimeout(S.camera.gestureSettleTimer);
        S.camera.gestureSettleTimer=null;
      }
      S.camera.gestureActive=false;
      S.camera.gestureKinds={};
      S.camera.mode="FOLLOW";
      S.camera.userFreeAt=0;

      Diagnostics.push("CameraAuthority","FOLLOW_LOCK_CAMERA_ONLY",{
        routeActive:S.session.routeActive,
        points:S.route.coords ? S.route.coords.length : 0
      });

      setSub(S.session.routeActive?"Follow Lock • route attached":"Follow Lock");

      return this.follow(true);
    },
    heading:function(){return VehicleMarkerAuthority.heading();},
    follow:function(force){
      if(S.camera.mode!=="FOLLOW"||!S.map.loaded||!S.map.instance){return false;}
      var f=S.location.canonical||S.location.lastValid;if(!f){return false;}
      if(!force&&now()-S.camera.lastAt<CFG.cameraMinIntervalMs){return false;}
      var h=this.heading(),mph=num((S.location.raw||{}).speedMph),zoom=CFG.driveZoom,pitch=CFG.drivePitch;
      if(mph<CFG.cameraStationaryMph){pitch=Math.min(52,pitch);}
      try{
        S.map.instance.easeTo({
          center:[f.lng,f.lat],
          zoom:zoom,
          pitch:pitch,
          bearing:h,
          duration:force?0:CFG.cameraMs,
          essential:true,
          offset:[0,Math.round(window.innerHeight*0.12)]
        });
        S.camera.lastAt=now();S.camera.lastBearing=h;
        VehicleMarkerAuthority.orientToCamera();
        return true;
      }catch(e){
        Diagnostics.error("CameraAuthority","FOLLOW_FAILED",{message:String(e&&e.message||e)});
        return false;
      }
    },
    overview:function(){
      Diagnostics.push("CameraAuthority","OVERVIEW_DISABLED",{});
      return false;
    }
  };

  /* ==========================================================================
     MANEUVER AUTHORITY — MANEUVER CHANGES NEVER TOUCH MAP/SESSION LIFECYCLE
  ========================================================================== */
  function instructionRoots(route){
    var roots=[];if(!route){return roots;}
    if(route.features){roots.push(route.features);}
    roots.push(route);return roots;
  }
  function findInstructionObjects(obj,out,depth){
    if(!obj||depth>8){return;}
    var i,k,v;
    if(Array.isArray(obj)){for(i=0;i<obj.length;i++){findInstructionObjects(obj[i],out,depth+1);}return;}
    if(typeof obj!=="object"){return;}
    if(obj.instruction||obj.instruction_text||obj.text||obj.maneuver||obj.type||obj.street_name||obj.name){out.push(obj);}
    for(k in obj){if(!Object.prototype.hasOwnProperty.call(obj,k)){continue;}v=obj[k];if(v&&typeof v==="object"&&k!=="geometry"&&k!=="coordinates"){findInstructionObjects(v,out,depth+1);}}
  }
  function firstCleanString(){
    for(var i=0;i<arguments.length;i++){
      var s=clean(arguments[i]);
      if(s){return s;}
    }
    return "";
  }

  function instructionText(o){
    if(!o){return "";}
    var parts=[],inst,v,k,d;
    try{
      inst=o.instruction||o.instructions||o.maneuver||o.sign;
      if(typeof inst==="string"){
        parts.push(inst);
      }else if(inst&&typeof inst==="object"){
        parts.push(inst.text||"");
        parts.push(inst.instruction||"");
        parts.push(inst.type||"");
        parts.push(inst.modifier||"");
        parts.push(inst.direction||"");
        parts.push(inst.action||"");
        parts.push(inst.transition_instruction||"");
        parts.push(inst.verbal_pre_transition_instruction||"");
        parts.push(inst.verbal_post_transition_instruction||"");
        parts.push(inst.name||"");
        parts.push(inst.street||"");
        parts.push(inst.road||"");
        parts.push(inst.ref||"");
        parts.push(inst.destination||"");
      }

      parts.push(o.instruction_text||"");
      parts.push(o.text||"");
      parts.push(o.description||"");
      parts.push(o.type||"");
      parts.push(o.modifier||"");
      parts.push(o.direction||"");
      parts.push(o.action||"");
      parts.push(o.turn_type||"");
      parts.push(o.transition_instruction||"");
      parts.push(o.verbal_pre_transition_instruction||"");
      parts.push(o.verbal_post_transition_instruction||"");
      parts.push(o.name||"");
      parts.push(o.street_name||"");
      parts.push(o.street||"");
      parts.push(o.road||"");
      parts.push(o.way_name||"");
      parts.push(o.ref||"");
      parts.push(o.exit||"");
      parts.push(o.destination||"");

      d=o.instruction_details;
      if(typeof d==="string"){
        parts.push(d);
      }else if(d&&typeof d==="object"){
        for(k in d){
          if(Object.prototype.hasOwnProperty.call(d,k)){
            v=d[k];
            if(typeof v==="string"||typeof v==="number"){
              parts.push(String(v));
            }else if(v&&typeof v==="object"){
              parts.push(v.text||"");
              parts.push(v.type||"");
              parts.push(v.modifier||"");
              parts.push(v.direction||"");
              parts.push(v.action||"");
              parts.push(v.street||"");
              parts.push(v.road||"");
              parts.push(v.name||"");
              parts.push(v.way_name||"");
              parts.push(v.ref||"");
              parts.push(v.destination||"");
            }
          }
        }
      }
    }catch(e){}
    return clean(parts.join(" "));
  }

  function normalizeRoadName(road){
    road=clean(road||"");
    if(!road){return "";}
    road=road.replace(/\binterstate\s+(\d{1,3})\b/ig,"I-$1");
    road=road.replace(/\bi\s+(\d{1,3})\b/ig,"I-$1");
    road=road.replace(/\bi-(\d{1,3})\b/ig,"I-$1");
    road=road.replace(/\bu\.s\.\s*(\d{1,3})\b/ig,"US-$1");
    road=road.replace(/\bus\s+(\d{1,3})\b/ig,"US-$1");
    road=road.replace(/\bhwy\s+(\d{1,3})\b/ig,"Highway $1");
    return clean(road);
  }

  function instructionStreet(o){
    var inst,d,s="";
    if(!o){return "";}
    try{
      inst=o.instruction||o.instructions||o.maneuver;
      if(inst&&typeof inst!=="string"){
        s=firstCleanString(
          inst.street_name,inst.street,inst.road,inst.name,inst.way_name,
          inst.ref,inst.exit,inst.destination
        );
      }
      if(!s){
        d=o.instruction_details;
        if(d&&typeof d==="object"){
          s=firstCleanString(
            d.street_name,d.street,d.road,d.name,d.way_name,
            d.ref,d.exit,d.destination,
            d.to&&d.to.name,d.to&&d.to.street,d.to&&d.to.road
          );
        }
      }
      if(!s){
        s=firstCleanString(
          o.street_name,o.street,o.road,o.name,o.way_name,
          o.ref,o.exit,o.destination,
          o.to&&o.to.name,o.to&&o.to.street,o.to&&o.to.road
        );
      }
    }catch(e){}
    return normalizeRoadName(s);
  }

  function instructionIndex(o){
    var vals=[
      o&&o.route_index,o&&o.index,o&&o.geometry_index,o&&o.waypoint_index,
      o&&o.from_index,o&&o.begin_index,o&&o.beginIndex,o&&o.start_index,
      o&&o.point_index
    ],d,i,c;
    for(i=0;i<vals.length;i++){
      if(finite(Number(vals[i]))){return Number(vals[i]);}
    }
    try{
      d=o&&o.instruction_details;
      if(d&&typeof d==="object"){
        vals=[d.from_index,d.begin_index,d.beginIndex,d.start_index,d.index,d.geometry_index,d.point_index];
        for(i=0;i<vals.length;i++){
          if(finite(Number(vals[i]))){return Number(vals[i]);}
        }
      }
      if(o&&o.maneuver&&finite(Number(o.maneuver.location_index))){
        return Number(o.maneuver.location_index);
      }
      c=instructionCoord(o);
      if(c){return nearestRouteIndexToPoint(S.route.coords,c[0],c[1]);}
    }catch(e){}
    return null;
  }
  function classifyInstruction(text){
    var low=clean(text).toLowerCase();
    if(/u[- ]?turn|uturn/.test(low)){return {type:"uturn",icon:"↶",label:"Make a U-turn"};}
    if(/roundabout|rotary/.test(low)){return {type:"roundabout",icon:"⟳",label:"Enter roundabout"};}
    if(/take .*exit|exit |exit_|ramp/.test(low)){return {type:"exit",icon:"↗",label:"Take the exit"};}
    if(/merge/.test(low)){return {type:"merge",icon:"↗",label:"Merge"};}
    if(/keep left|stay left|slight_left|bear left/.test(low)){return {type:"keep_left",icon:"↖",label:"Keep left"};}
    if(/keep right|stay right|slight_right|bear right/.test(low)){return {type:"keep_right",icon:"↗",label:"Keep right"};}
    if(/turn left|left onto|left on|\bleft\b|turn_left/.test(low)){return {type:"left",icon:"←",label:"Turn left"};}
    if(/turn right|right onto|right on|\bright\b|turn_right/.test(low)){return {type:"right",icon:"→",label:"Turn right"};}
    if(/arriv|destination reached|finish/.test(low)){return {type:"arrival",icon:"◎",label:"Arrive"};}
    return {type:"continue",icon:"↑",label:"Continue"};
  }
  function geometryManeuvers(coords){
    var out=[],last=-20;
    for(var i=3;i<coords.length-4;i++){
      if(i-last<7){continue;}
      var a={lng:coords[i-3][0],lat:coords[i-3][1]},b={lng:coords[i][0],lat:coords[i][1]},c={lng:coords[i+3][0],lat:coords[i+3][1]};
      var diff=angleDelta(bearing(a,b),bearing(b,c));if(Math.abs(diff)<30){continue;}
      var type=diff>0?"right":"left",label=diff>0?"Turn right":"Turn left",icon=diff>0?"→":"←";
      out.push({index:i,distanceAlong:cumulativeMiles(coords,i),type:type,label:label,icon:icon,street:"",rawText:label,source:"geometry"});last=i;
    }
    return out;
  }

  function nearestRouteIndexToPoint(coords,lng,lat){
    if(!coords||!coords.length||!finite(Number(lng))||!finite(Number(lat))){return null;}
    var best=Infinity,bestIdx=null,step=Math.max(1,Math.floor(coords.length/1200)),i,d;
    for(i=0;i<coords.length;i+=step){
      d=milesBetween([Number(lng),Number(lat)],[Number(coords[i][0]),Number(coords[i][1])]);
      if(d<best){best=d;bestIdx=i;}
    }
    if(bestIdx==null){return null;}
    var lo=Math.max(0,bestIdx-step*2),hi=Math.min(coords.length-1,bestIdx+step*2);
    for(i=lo;i<=hi;i++){
      d=milesBetween([Number(lng),Number(lat)],[Number(coords[i][0]),Number(coords[i][1])]);
      if(d<best){best=d;bestIdx=i;}
    }
    return bestIdx;
  }

  function instructionCoord(o){
    var c=null;
    try{
      if(o&&o.geometry&&Array.isArray(o.geometry.coordinates)){c=o.geometry.coordinates;}
      if(!c&&o&&Array.isArray(o.location)){c=o.location;}
      if(!c&&o&&o.maneuver&&Array.isArray(o.maneuver.location)){c=o.maneuver.location;}
      if(!c&&o&&finite(Number(o.lon))&&finite(Number(o.lat))){c=[Number(o.lon),Number(o.lat)];}
      if(!c&&o&&finite(Number(o.lng))&&finite(Number(o.lat))){c=[Number(o.lng),Number(o.lat)];}
    }catch(e){}
    return c&&finite(Number(c[0]))&&finite(Number(c[1]))?[Number(c[0]),Number(c[1])]:null;
  }

  function streetFromInstructionText(text){
    var t=clean(text||""),m,i;
    if(!t){return "";}
    var patterns=[
      /\b(?:onto|on|toward|towards|for)\s+((?:I[-\s]?\d{1,3}|Interstate\s+\d{1,3}|US[-\s]?\d{1,3}|U\.S\.\s*\d{1,3}|Hwy\s+\d{1,3}|Highway\s+\d{1,3}|SR[-\s]?\d{1,3}|TN[-\s]?\d{1,3}|MS[-\s]?\d{1,3}|AR[-\s]?\d{1,3})(?:\s+[NSEW]|\s+North|\s+South|\s+East|\s+West)?)/i,
      /\b((?:I[-\s]?\d{1,3}|Interstate\s+\d{1,3}|US[-\s]?\d{1,3}|U\.S\.\s*\d{1,3}|Hwy\s+\d{1,3}|Highway\s+\d{1,3})(?:\s+[NSEW]|\s+North|\s+South|\s+East|\s+West)?)\b/i,
      /\b(?:onto|on|toward|towards|for|to)\s+([A-Z0-9][A-Za-z0-9'.\- ]{1,48}\s(?:Road|Rd|Street|St|Avenue|Ave|Boulevard|Blvd|Drive|Dr|Lane|Ln|Parkway|Pkwy|Highway|Hwy|Freeway|Expressway|Bypass|Circle|Cir|Court|Ct|Way))\b/i,
      /\b(?:continue|head|turn|merge|keep)\s+(?:left\s+|right\s+)?(?:onto|on|toward|towards|for|to)?\s*([A-Z0-9][A-Za-z0-9'.\- ]{1,48}\s(?:Road|Rd|Street|St|Avenue|Ave|Boulevard|Blvd|Drive|Dr|Lane|Ln|Parkway|Pkwy|Highway|Hwy|Freeway|Expressway|Bypass|Circle|Cir|Court|Ct|Way))\b/i
    ];
    for(i=0;i<patterns.length;i++){
      m=t.match(patterns[i]);
      if(m&&m[1]){return normalizeRoadName(m[1]);}
    }
    return "";
  }

  function bestInstructionStreet(o){
    return instructionStreet(o)||streetFromInstructionText(instructionText(o));
  }

  function routeInstructionFetchUrl(){
    if(!GEOAPIFY_KEY||!S.route.coords||S.route.coords.length<2){return "";}
    var start=S.route.coords[0],dest=RouteAuthority.destinationFix();
    if(!start||!dest){return "";}
    return "https://api.geoapify.com/v1/routing?waypoints="+
      encodeURIComponent(String(start[1])+","+String(start[0])+"|"+String(dest.lat)+","+String(dest.lng))+
      "&mode=drive&format=geojson&details=instruction_details&apiKey="+encodeURIComponent(GEOAPIFY_KEY);
  }

  function hydrateInstructionManeuvers(reason){
    var sig=S.route.signature||"",url;
    if(!sig||!S.session.routeActive){return false;}
    if(S.maneuver.instructionFetch){return false;}
    if(S.maneuver.instructionFetched&&S.maneuver.instructionRouteSig===sig){return false;}
    if(!GEOAPIFY_KEY){
      S.maneuver.instructionError="missing-geoapify-key";
      Diagnostics.error("ManeuverAuthority","NO_INSTRUCTION_KEY",{});
      return false;
    }

    url=routeInstructionFetchUrl();
    if(!url){return false;}

    S.maneuver.instructionFetch=true;
    S.maneuver.instructionRouteSig=sig;
    setText("diqRouteIntelText","Loading street names…");
    setText("diqRouteIntelSub","Route is active • hydrating turn instructions");
    Diagnostics.push("ManeuverAuthority","INSTRUCTION_FETCH_START",{reason:reason||"route-install"});

    fetch(url).then(function(r){
      if(!r.ok){throw new Error("HTTP "+r.status);}
      return r.json();
    }).then(function(data){
      if(S.route.signature!==sig || (S.route.geo&&S.route.geo.properties&&S.route.geo.properties.diqTrafficVerifiedGuidance)){
        Diagnostics.push("ManeuverAuthority","STALE_FETCH_IGNORED",{routeSignature:sig});
        return;
      }
      var objs=[],roots=instructionRoots(data),coords=S.route.coords,out=[],seen={},i,j,o,idx,c,txt,street,cls,key;
      for(i=0;i<roots.length;i++){findInstructionObjects(roots[i],objs,0);}

      for(j=0;j<objs.length;j++){
        o=objs[j];
        txt=instructionText(o);
        if(!txt){continue;}

        idx=instructionIndex(o);
        if(idx==null||idx<=0||idx>=coords.length-1){
          c=instructionCoord(o);
          if(c){idx=nearestRouteIndexToPoint(coords,c[0],c[1]);}
        }
        if(idx==null||idx<=0||idx>=coords.length-1){continue;}

        street=bestInstructionStreet(o);
        cls=classifyInstruction(txt);
        if(cls.type==="continue"&&!street&&!/continue|head|straight|depart/i.test(txt)){continue;}

        key=String(idx)+"|"+cls.type+"|"+street;
        if(seen[key]){continue;}
        seen[key]=true;

        out.push({
          index:idx,
          distanceAlong:cumulativeMiles(coords,idx),
          type:cls.type,
          label:cls.label,
          icon:cls.icon,
          street:street,
          road:street,
          rawText:txt,
          streetSource:instructionStreet(o)?"explicit":"instruction-text",
          source:"geoapify-instruction"
        });
      }

      out.sort(function(a,b){return a.distanceAlong-b.distanceAlong;});

      S.maneuver.instructionFetch=false;
      S.maneuver.instructionFetched=true;
      S.maneuver.instructionError="";

      if(out.length){
        S.maneuver.list=out;
        S.maneuver.activeIndex=0;
        S.maneuver.current=null;
        S.maneuver.lastSpokenKey="";
        Diagnostics.push("ManeuverAuthority","INSTRUCTION_FETCH_SUCCESS",{
          count:out.length,
          named:out.filter(function(x){return !!x.street;}).length
        });
        ManeuverAuthority.tick();
      }else{
        S.maneuver.instructionError="no-usable-instructions";
        Diagnostics.error("ManeuverAuthority","INSTRUCTION_FETCH_EMPTY",{});
        ManeuverAuthority.tick();
      }
    }).catch(function(e){
      if(S.route.signature!==sig){return;}
      S.maneuver.instructionFetch=false;
      S.maneuver.instructionFetched=true;
      S.maneuver.instructionError=String(e&&e.message||e);
      Diagnostics.error("ManeuverAuthority","INSTRUCTION_FETCH_FAILED",{message:S.maneuver.instructionError});
      ManeuverAuthority.tick();
    });

    return true;
  }

  function isUsefulManeuver(m){
    if(!m){return false;}
    if(m.type!=="continue"){return true;}
    if(m.street){return true;}
    var t=clean(m.rawText||"").toLowerCase();
    return /continue|head|depart|straight/.test(t);
  }

  function nextNamedOrActionable(list,startIndex){
    var i,m,n,j,candidate,delta;
    for(i=Math.max(0,startIndex||0);i<list.length;i++){
      m=list[i];
      if(!isUsefulManeuver(m)){continue;}

      /*
        V254 CARPLAY ACTIONABLE MANEUVER:
        Geometry fallback can produce long "Continue" items before the next
        actual turn. Prefer the next named/actionable maneuver when it is
        reasonably ahead, instead of leaving CarPlay stuck on "Continue ahead".
        This changes maneuver selection only; route/camera/session stay untouched.
      */
      if(m.type==="continue"&&!m.street){
        for(j=i+1;j<list.length;j++){
          n=list[j];
          if(!isUsefulManeuver(n)){continue;}
          delta=Number(n.distanceAlong||0)-Number(m.distanceAlong||0);

          if(
            delta>=0 &&
            delta<=2.0 &&
            (n.type!=="continue" || !!n.street)
          ){
            candidate=n;
            break;
          }

          if(delta>2.0){break;}
        }

        if(candidate){return candidate;}
      }

      return m;
    }
    return null;
  }

  var ManeuverAuthority = {
    rebuild:function(){
      // Driver-accepted traffic guidance was verified against this exact
      // geometry before commit. Do not replace it with inferred turns or
      // a separate provider's directions for a potentially different road.
      var locked=S.route.geo&&S.route.geo.properties&&S.route.geo.properties.diqTrafficVerifiedGuidance;
      var verified=S.route.geo&&S.route.geo.properties&&S.route.geo.properties.diqTrafficManeuvers;
      if(locked && Array.isArray(verified) && verified.length){
        S.maneuver.list=verified.map(function(m){return Object.assign({},m);});
        S.maneuver.activeIndex=0;S.maneuver.current=null;
        S.maneuver.lastSpokenKey="";S.maneuver.lastReassuranceKey="";
        S.maneuver.lastReassuranceAt=0;
        S.maneuver.instructionFetch=false;S.maneuver.instructionFetched=true;
        S.maneuver.instructionRouteSig=S.route.signature;
        S.maneuver.instructionError="";
        Diagnostics.push("ManeuverAuthority","VERIFIED_TRAFFIC_GUIDANCE_INSTALLED",{
          count:verified.length,signature:S.route.signature
        });
        this.tick();return;
      }
      var coords=S.route.coords,objs=[],out=[],seen={},roots=instructionRoots(S.route.geo),i,j,o,idx,cls,text,street,key;
      for(i=0;i<roots.length;i++){findInstructionObjects(roots[i],objs,0);}
      for(j=0;j<objs.length;j++){
        o=objs[j];idx=instructionIndex(o);if(idx==null||idx<=0||idx>=coords.length-1){continue;}
        text=instructionText(o);street=bestInstructionStreet(o);cls=classifyInstruction(text);if(cls.type==="continue"&&!text){continue;}
        key=idx+"|"+cls.type+"|"+street;if(seen[key]){continue;}seen[key]=true;
        out.push({index:idx,distanceAlong:cumulativeMiles(coords,idx),type:cls.type,label:cls.label,icon:cls.icon,street:street,road:street,rawText:text,streetSource:instructionStreet(o)?"explicit":"instruction-text",source:"instruction"});
      }
      out.sort(function(a,b){return a.distanceAlong-b.distanceAlong;});
      if(!out.length){out=geometryManeuvers(coords);}
      S.maneuver.list=out;S.maneuver.activeIndex=0;S.maneuver.current=null;S.maneuver.lastSpokenKey="";S.maneuver.lastReassuranceKey="";S.maneuver.lastReassuranceAt=0;
      Diagnostics.push("ManeuverAuthority","REBUILT",{count:out.length,source:out.length&&out[0].source,named:out.filter(function(x){return !!x.street;}).length});
      this.tick();

      /*
        If the route handoff is geometry-only or contains unnamed turns, hydrate
        instruction_details once for this route. Geometry is NOT replaced.
      */
      var named=out.filter(function(x){return !!x.street;}).length;
      if(!out.length || named===0 || out[0].source==="geometry"){
        hydrateInstructionManeuvers("rebuild-needs-street-names");
      }
    },
    current:function(){
      var list=S.maneuver.list,along=S.route.progressMiles,i,m;
      if(!list.length){return null;}
      for(i=Math.max(0,S.maneuver.activeIndex-1);i<list.length;i++){
        if(list[i].distanceAlong+CFG.maneuverPassedBufferMiles>=along){
          m=nextNamedOrActionable(list,i);
          if(m){
            S.maneuver.activeIndex=Math.max(i,list.indexOf(m));
            return m;
          }
        }
      }
      return null;
    },
    tick:function(){
      var m=this.current(),prev=S.maneuver.current;
      S.maneuver.current=m;
      if(!m){
        if(S.maneuver.instructionFetch){
          setTurnCard("↑","Loading street names…","Route guidance active");
          setText("diqRouteIntelText","Loading street names…");
          setText("diqRouteIntelSub","Route guidance active");
        }else{
          setTurnCard("↑","Continue on route","Street name unavailable");
          setText("diqRouteIntelText","Continue on route");
          setText("diqRouteIntelSub","Street name unavailable • route remains active");
        }
        return;
      }
      var left=Math.max(0,m.distanceAlong-S.route.progressMiles);
      if(!prev||prev.index!==m.index){Diagnostics.push("ManeuverAuthority","ADVANCE",{index:m.index,type:m.type,street:m.street});}
      var title=m.label+(m.street?" onto "+m.street:"");
      setTurnCard(m.icon,title,fmtDistance(left));
      setText("diqRouteIntelText",title);
      setText("diqRouteIntelSub",fmtDistance(left)+" ahead");
      VoiceAuthority.maybeSpeak(m,left);
      if(left<0.25&&S.session.phase!=="FINAL_APPROACH"){DriveSession.phase("APPROACHING","maneuver-near");}
      else if(S.session.phase==="DEPARTING"&&RouteAuthority.progressRatio()>0.03){DriveSession.phase("CRUISING","route-progress");}
    }
  };

  function setTurnCard(icon,title,sub){
    setText("diqTurnIcon",icon||"↑");
    setText("diqTurnTitle",title||"Continue");setText("diqManeuverTitle",title||"Continue");setText("diqTurnText",title||"Continue");
    setText("diqTurnSub",sub||"");setText("diqManeuverSub",sub||"");setText("diqTurnMeta",sub||"");
  }

  /* ==========================================================================
     PREFERENCE AUTHORITY — MY DRIVE IQ INPUT, PRESENTATION ONLY

     Reads:
       diq_active_skin_v1
       driveiq_skin
       driveiq_theme
       diq_theme_v1
       diq_voice_style_v1
       diq_vehicle_marker_v1

     Listens:
       driveiq:themechange
       diqVoiceChanged
       driveiq:markerchange

     HARD RULE:
     Preferences may change CSS/presentation, Mapbox paint properties, marker colors,
     and voice style only. They never call map.setStyle(), recreate the map/marker,
     replace route geometry, move the camera, or mutate DriveSession, LocationAuthority,
     RouteAuthority state, CameraAuthority, ManeuverAuthority, or reroute state.
  ========================================================================== */
  var PreferenceAuthority = {
    skins:{platinum_gt:1,american_muscle:1,carbon_rs:1,cockpit:1,aurora:1,sunset:1,coastal:1,midnight_luxe:1,volt:1,forest:1,autumn:1,holiday:1},
    voiceStyles:{calm:1,friendly:1,southern:1,minimal:1,detailed:1,professional:1},
    vehicleMarkers:{rocket_scout:1,turbo_bolt:1,falcon_wing:1,compass_star:1,rally_rider:1,trail_beast:1},

    normalizeSkin:function(v){
      var s=clean(v).toLowerCase().replace(/[\s-]+/g,"_");
      if(s==="midnight"||s==="midnightluxe"){s="midnight_luxe";}
      if(s==="americanmuscle"||s==="muscle"||s==="muscle_gt"){s="american_muscle";}
      if(s==="carbonrs"||s==="carbon_rs_silver"){s="carbon_rs";}
      return this.skins[s]?s:"cockpit";
    },

    normalizeVoice:function(v){
      var s=clean(v).toLowerCase().replace(/[\s-]+/g,"_");
      return this.voiceStyles[s]?s:"calm";
    },

    normalizeVehicleMarker:function(v){
      var s=clean(v).toLowerCase().replace(/[\s-]+/g,"_");
      return this.vehicleMarkers[s]?s:"rocket_scout";
    },

    readHandoffSkin:function(){
      /* V275: Start Drive writes the chosen skin into the same fresh, matching
         trip payload used for route handoff. Honor it before older page storage.
         Do not use an old payload for an unrelated or completed trip. */
      var payload=parse(lsGet(LS.DRIVE_PAYLOAD));
      if(!payload || !payload.drive_id ||
         clean(payload.drive_id)!==clean(lsGet(LS.ACTIVE_DRIVE_ID)) ||
         lsGet(LS.DRIVE_ON)!=="1"){return "";}
      var started=Date.parse(payload.started_at||"");
      if(!isFinite(started) || started>Date.now()+60000 || Date.now()-started>15*60*1000){return "";}
      var raw=clean(payload.skin||payload.theme);
      if(!raw){return "";}
      var normalized=this.normalizeSkin(raw);
      return (this.skins[normalized] &&
        (normalized!=="cockpit" || raw.toLowerCase()==="cockpit"))?normalized:"";
    },

    readSkin:function(){
      /* Garage is authoritative normally; the fresh matching Start Drive
         payload is authoritative at entry to this particular trip. */
      var keys=[LS.GARAGE_SKIN,LS.ACTIVE_SKIN,LS.NATIVE_SKIN,LS.SKIN_FALLBACK_1,LS.SKIN_FALLBACK_2,LS.SKIN_FALLBACK_3],i,v="";
      for(i=0;i<keys.length;i++){
        v=clean(lsGet(keys[i]));
        if(v && this.skins[this.normalizeSkin(v)] && this.normalizeSkin(v)!=="cockpit") {break;}
        if(v && this.normalizeSkin(v)==="cockpit" && v.toLowerCase()==="cockpit") {break;}
        v="";
      }
      return this.normalizeSkin(v||"cockpit");
    },

    readVoice:function(){
      return this.normalizeVoice(lsGet(LS.VOICE_STYLE)||"calm");
    },

    readVehicleMarker:function(){
      return this.normalizeVehicleMarker(lsGet(LS.VEHICLE_MARKER)||"rocket_scout");
    },

    applySkin:function(next,source){
      var skin=this.normalizeSkin(next);
      var keys=[LS.ACTIVE_SKIN,LS.NATIVE_SKIN,LS.SKIN_FALLBACK_1,LS.SKIN_FALLBACK_2,LS.SKIN_FALLBACK_3],i,pill,root;
      if(FreeTierIQ.free()){skin="cockpit";}
      S.preferences.skin=skin;

      /* VIBE AUTHORITY — persist the same shared keys used by My Drive IQ. */
      if(!FreeTierIQ.free()){
        for(i=0;i<keys.length;i++){lsSet(keys[i],skin);}
        try{document.cookie="diq_active_skin_v1="+encodeURIComponent(skin)+"; path=/; max-age=31536000; SameSite=Lax";}catch(cookieErr){}
      }

      /* Presentation only: no map.setStyle(), route rebuild, camera reset, or marker recreation. */
      try{document.documentElement.setAttribute("data-diq-skin",skin);document.documentElement.setAttribute("data-diq-theme",skin);}catch(e1){}
      try{document.body.setAttribute("data-diq-skin",skin);document.body.setAttribute("data-diq-theme",skin);}catch(e2){}
      try{
        root=byId("diqDriveRoot")||byId("driveIQRoot")||byId("diqApp")||byId("diqPage");
        if(root){root.setAttribute("data-diq-skin",skin);root.setAttribute("data-diq-theme",skin);}
      }catch(e3){}

      pill=byId("diqModePill");
      if(pill){
        var labels={platinum_gt:"PLATINUM GT",american_muscle:"AMERICAN MUSCLE",carbon_rs:"CARBON RS",cockpit:"COCKPIT",aurora:"AURORA",sunset:"SUNSET",coastal:"COASTAL",midnight_luxe:"MIDNIGHT LUXE",volt:"VOLT",forest:"FOREST",autumn:"AUTUMN",holiday:"HOLIDAY"};
        pill.textContent=FreeTierIQ.free()?"DRIVE IQ FREE":(labels[skin]||"COCKPIT");
      }

      try{polishPlatinumDriveMode();}catch(platinumPolishError){}
      try{window.DriveIQActiveSkin=skin;}catch(e4){}
      /* Legacy renderer does not know Signature Cockpits. Never let it
         silently reset American Muscle / Carbon RS to a generic theme. */
      try{
        if(skin!=="platinum_gt" && skin!=="american_muscle" && skin!=="carbon_rs" &&
           window.DriveIQ && typeof window.DriveIQ.applyTheme==="function"){
          window.DriveIQ.applyTheme(skin);
        }
      }catch(e5){Diagnostics.error("PreferenceAuthority","THEME_RENDERER_ERROR",{message:String(e5&&e5.message||e5)});}

      ThemePaintAuthority.paint(source||"preference");
      try{syncVibeButtons();}catch(syncErr){}
      /* Keep CarPlay visually synchronized with My Drive IQ / Drive Mode. */
      try{sendCarPlaySnapshot("theme-change");}catch(carPlayThemeErr){}
      Diagnostics.push("PreferenceAuthority","SKIN_APPLIED",{skin:skin,source:source||"storage"});
      return skin;
    },

    applyVoice:function(next,source){
      var style=FreeTierIQ.free()?"calm":this.normalizeVoice(next);
      S.preferences.voiceStyle=style;
      try{document.body.setAttribute("data-diq-voice-style",style);}catch(e1){}
      Diagnostics.push("PreferenceAuthority","VOICE_STYLE_APPLIED",{voiceStyle:style,source:source||"storage"});
      return style;
    },

    applyVehicleMarker:function(next,source){
      var marker=FreeTierIQ.free()?"compass_star":this.normalizeVehicleMarker(next);
      S.preferences.vehicleMarker=marker;
      try{window.DriveIQVehicleMarker=marker;}catch(e0){}
      if(S.vehicle.arrow){VehicleMarkerAuthority.applyArtwork(marker,source||"preference");}
      Diagnostics.push("PreferenceAuthority","VEHICLE_MARKER_APPLIED",{marker:marker,source:source||"storage"});
      return marker;
    },

    voiceProfile:function(){
      var s=this.normalizeVoice(S.preferences.voiceStyle);
      if(s==="friendly"){return {style:s,rate:.96,pitch:1.02,volume:1};}
      if(s==="southern"){return {style:s,rate:.92,pitch:.98,volume:1};}
      if(s==="minimal"){return {style:s,rate:.98,pitch:1,volume:1};}
      if(s==="detailed"){return {style:s,rate:.92,pitch:1,volume:1};}
      if(s==="professional"){return {style:s,rate:.95,pitch:.99,volume:1};}
      return {style:"calm",rate:.94,pitch:1,volume:1};
    },

    onThemeChange:function(ev){
      var d=ev&&ev.detail?ev.detail:{};
      /* Reject older renderer events that disagree with the Garage choice. */
      var selected=PreferenceAuthority.readSkin();
      var incoming=d.skin||d.theme||selected;
      PreferenceAuthority.applySkin(PreferenceAuthority.normalizeSkin(incoming)===selected?incoming:selected,"driveiq:themechange");
    },

    onVoiceChange:function(ev){
      var d=ev&&ev.detail?ev.detail:{};
      PreferenceAuthority.applyVoice(d.voice||d.style||PreferenceAuthority.readVoice(),"diqVoiceChanged");
    },

    onMarkerChange:function(ev){
      var d=ev&&ev.detail?ev.detail:{};
      PreferenceAuthority.applyVehicleMarker(d.marker||PreferenceAuthority.readVehicleMarker(),"driveiq:markerchange");
    },

    start:function(){
      if(this.started){return true;}
      this.started=true;
      S.preferences.started=true;
      var freshHandoff=this.readHandoffSkin();
      if(freshHandoff && !FreeTierIQ.free()){
        /* Reconcile this page's stale Garage key with the explicit skin selected
           at Start Drive. This also keeps older themechange events honest. */
        lsSet(LS.GARAGE_SKIN,freshHandoff);
      }
      this.applySkin(FreeTierIQ.free()?"cockpit":(freshHandoff||this.readSkin()),"boot-storage");
      this.applyVoice(FreeTierIQ.free()?"calm":this.readVoice(),"boot-storage");
      this.applyVehicleMarker(FreeTierIQ.free()?"compass_star":this.readVehicleMarker(),"boot-storage");
      window.addEventListener("driveiq:themechange",this.onThemeChange,false);
      window.addEventListener("diqVoiceChanged",this.onVoiceChange,false);
      window.addEventListener("driveiq:markerchange",this.onMarkerChange,false);
      Diagnostics.push("PreferenceAuthority","STARTED",{skin:S.preferences.skin,voiceStyle:S.preferences.voiceStyle,vehicleMarker:S.preferences.vehicleMarker,vehicle3d:readSelected3DVehicle()});
      return true;
    },

    stop:function(){
      if(!this.started){return;}
      this.started=false;
      S.preferences.started=false;
      window.removeEventListener("driveiq:themechange",this.onThemeChange,false);
      window.removeEventListener("diqVoiceChanged",this.onVoiceChange,false);
      window.removeEventListener("driveiq:markerchange",this.onMarkerChange,false);
    }
  };

  /* ==========================================================================
     VOICE AUTHORITY — READ-ONLY CONSUMER OF MANEUVER STATE
  ========================================================================== */
  var VoiceAuthority = {
    enabled:function(){ return S.voice.enabled; },
    toggle:function(){
      S.voice.enabled=!S.voice.enabled;
      if(!S.voice.enabled){
        try{if(window.speechSynthesis){window.speechSynthesis.cancel();}}catch(e){}
        S.voice.speaking=false;
        Diagnostics.push("VoiceAuthority","TOGGLE",{enabled:false});
        setSub("Voice guidance off");
        return false;
      }
      Diagnostics.push("VoiceAuthority","TOGGLE",{enabled:true});
      setSub("Voice guidance on");
      /* Audible confirmation proves the speech lane is actually alive. */
      window.setTimeout(function(){VoiceAuthority.speak("Voice guidance on.","toggle-confirmation");},60);
      return true;
    },
    line:function(m,left,bucket){
      /*
        Maneuver truth comes only from ManeuverAuthority.
        Voice style can change phrasing length, never the action/street/distance.
      */
      var action=m.label||"Continue",road=m.street?" onto "+m.street:"";
      // Only repeat an exit identifier when the active instruction explicitly provides it.
      // This is NOT lane-level guidance; no lane counts are inferred from geometry.
      if(m.type==="exit"){
        var exitMatch=clean(m.rawText||"").match(/\bexit\s+(\d{1,3}[A-Z]?)\b/i);
        if(exitMatch){action="Take Exit "+exitMatch[1].toUpperCase();}
      }
      var dist=fmtDistance(left),style=PreferenceAuthority.normalizeVoice(S.preferences.voiceStyle);

      if(style==="minimal"){
        return bucket==="now" ? action+road+" now." : action+road+" in "+dist+".";
      }
      if(style==="friendly"){
        return bucket==="now" ? action+road+" now." : "Coming up in "+dist+", "+action.toLowerCase()+road+".";
      }
      if(style==="southern"){
        return bucket==="now" ? action+road+" now." : "In "+dist+", "+action.toLowerCase()+road+".";
      }
      if(style==="detailed"){
        return bucket==="now" ? action+road+" now." : "In "+dist+", "+action.toLowerCase()+road+". Stay on the current route until then.";
      }
      if(style==="professional"){
        return bucket==="now" ? action+road+" now." : "In "+dist+", "+action.toLowerCase()+road+".";
      }

      if(bucket==="early"){
        return "Ahead in "+dist+", "+action.toLowerCase()+road+".";
      }
      return bucket==="now" ? action+road+" now." : "In "+dist+", "+action.toLowerCase()+road+".";
    },
    bucket:function(left){
      var mph=num((S.location.raw||{}).speedMph,0);
      var early=mph>=CFG.voiceHighwayMph?CFG.voiceEarlyHighwayMiles:CFG.voiceEarlyCityMiles;
      // Earlier prompts give the native speech bridge time to start speaking.
      // Ranges are speed-aware; 70 mph covers ~103 feet each second.
      if(left<= (mph>=CFG.voiceHighwayMph?0.075:0.045)){return "now";}
      if(left<= (mph>=CFG.voiceHighwayMph?0.33:0.22)){return "800ft";}
      if(left<= (mph>=CFG.voiceHighwayMph?0.72:0.55)){return "half";}
      if(left<= (mph>=CFG.voiceHighwayMph?1.30:1.05)){return "mile";}
      if(left<=early){return "early";}
      return "";
    },

    followingManeuver:function(m){
      var list=S.maneuver.list||[],i=list.indexOf(m),j,n;
      if(i<0){return null;}
      for(j=i+1;j<list.length;j++){
        n=list[j];
        if(!isUsefulManeuver(n)){continue;}
        if(Number(n.distanceAlong)<=Number(m.distanceAlong)+0.01){continue;}
        return n;
      }
      return null;
    },
    sequenceSuffix:function(m,left,bucket){
      if(bucket!=="half"&&bucket!=="800ft"&&bucket!=="mile"){return "";}
      var next=this.followingManeuver(m),gap=next?Number(next.distanceAlong)-Number(m.distanceAlong):Infinity;
      if(gap<0.01||gap>0.20){return "";}
      if(!/^(left|right|keep_left|keep_right|exit|merge)$/.test(next.type)){return "";}
      var action=next.type==="keep_left"?"keep left":next.type==="keep_right"?"keep right":
        next.type==="exit"?"take the exit":next.type==="merge"?"merge":next.label.toLowerCase();
      return " Then, in about "+fmtDistance(gap)+", "+action+(next.street?" onto "+next.street:"")+".";
    },

    currentRoad:function(m){
      var list=S.maneuver.list||[],i=-1,j,x;
      if(!m){return "";}
      for(j=0;j<list.length;j++){
        if(list[j]===m || Number(list[j].index)===Number(m.index)){i=j;break;}
      }
      if(i<0){i=Math.max(0,num(S.maneuver.activeIndex,0));}
      for(j=i-1;j>=0;j--){
        x=list[j];
        if(!x){continue;}
        if(Number(x.distanceAlong||0)>Number(S.route.progressMiles||0)+CFG.maneuverPassedBufferMiles){continue;}
        if(clean(x.street)){return normalizeRoadName(x.street);}
      }
      if(m.type==="continue"&&clean(m.street)){return normalizeRoadName(m.street);}
      return "";
    },

    reassuranceLine:function(m,left){
      var road=this.currentRoad(m);
      var miles=Math.max(1,Math.round(left));
      if(!S.maneuver.lastReassuranceAt){
        return (road?"Stay on "+road:"Continue on your current route")+" for about "+miles+" miles. I'll keep you updated if anything changes.";
      }
      return "You're making progress. "+(road?"Stay on "+road:"Continue on your route")+" for about "+miles+" more miles until the next instruction.";
    },

    maybeLongReassurance:function(m,left){
      if(!S.voice.enabled||!m||left<CFG.voiceLongReassuranceMinMiles){return false;}
      if(now()-S.session.startedAt<CFG.voiceLongReassuranceBootDelayMs){return false;}
      if(S.weatherSafety&&S.weatherSafety.lastAlertAt&&now()-S.weatherSafety.lastAlertAt<20000){return false;}
      if(S.voice.speaking){return false;}

      var road=this.currentRoad(m);
      if(S.maneuver.lastReassuranceAt&&now()-S.maneuver.lastReassuranceAt<CFG.voiceLongReassuranceGapMs){return false;}
      if(S.maneuver.lastSpokenAt&&now()-S.maneuver.lastSpokenAt<15000){return false;}

      var line=this.reassuranceLine(m,left);
      if(!this.speak(line,"route-reassurance")){return false;}

      S.maneuver.lastReassuranceKey=m.index+"|"+road;
      S.maneuver.lastReassuranceAt=now();
      S.maneuver.lastSpokenAt=now();
      Diagnostics.push("VoiceAuthority","LONG_REASSURANCE",{text:line,leftMiles:left,road:road||""});
      return true;
    },

    maybeSpeak:function(m,left){
      if(!S.voice.enabled||!m){return false;}

      var b=this.bucket(left);
      if(!b){
        return this.maybeLongReassurance(m,left);
      }

      var key=m.index+"|"+b;
      if(key===S.maneuver.lastSpokenKey){return false;}
      // Closely spaced turns must not wait behind the ordinary 6.5s cadence.
      var gap=now()-S.maneuver.lastSpokenAt;
      var changed=!!(S.maneuver.lastSpokenKey&&S.maneuver.lastSpokenKey.split("|")[0]!==String(m.index));
      if(gap<CFG.voiceMinGapMs&&b!=="now"&&!(changed&&gap>=1800)){return false;}

      var line=this.line(m,left,b);
      var suffix=this.sequenceSuffix(m,left,b);
      if(suffix){line+=suffix;}
      if(!this.speak(line,"maneuver")){return false;}

      S.maneuver.lastSpokenKey=key;
      S.maneuver.lastSpokenAt=now();
      return true;
    },
    speak:function(text,source){
      var line=clean(text),src=clean(source||"voice");
      if(!line||!S.voice.enabled){return false;}

      /*
        V208 destructive voice firewall:
        Stop/scan discovery is presentation state, not navigation guidance.
        Never let it enter the shared native/browser speech lane.
      */
      if(src==="stop-iq"||src==="stop-scan"||src==="scan"){
        Diagnostics.push("VoiceAuthority","SCAN_SPEECH_BLOCKED",{source:src,text:line});
        return false;
      }

      /*
        V210: Preserve the native bridge contract used by the working voice path.
        V208 changed only the source tag; some native handlers may silently ignore
        unknown source identifiers even though the JS bridge call itself succeeds.
        Scan speech remains blocked above, so restoring this identifier does not
        reopen Stop IQ chatter.
      */
      /* Native first. Voice has no access to map/session/route mutation APIs. */
      var payload={type:"routeGuidance",text:line,priority:"navigation",voiceStyle:S.preferences.voiceStyle,source:"v200-complete",context:src,timestamp:now()};
      try{
        if(window.DriveIQNative&&typeof window.DriveIQNative.voice==="function"){
          var nativeAccepted=window.DriveIQNative.voice(payload);
          // A bridge can explicitly decline delivery. Fall through instead of
          // reporting a successful native announcement that never reached Swift.
          if(nativeAccepted!==false){
            Diagnostics.push("VoiceAuthority","NATIVE_SPEAK",{text:line,source:payload.source,context:src});
            S.voice.lastText=line;S.voice.lastAt=now();
            return true;
          }
          Diagnostics.push("VoiceAuthority","NATIVE_DECLINED",{text:line,context:src});
        }
      }catch(e1){Diagnostics.error("VoiceAuthority","NATIVE_ERROR",{message:String(e1&&e1.message||e1)});}
      try{
        if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQVoice){
          window.webkit.messageHandlers.driveIQVoice.postMessage(payload);
          Diagnostics.push("VoiceAuthority","WEBKIT_SPEAK",{text:line,source:payload.source,context:src});
          S.voice.lastText=line;S.voice.lastAt=now();
          return true;
        }
      }catch(eBridge){Diagnostics.error("VoiceAuthority","WEBKIT_ERROR",{message:String(eBridge&&eBridge.message||eBridge)});}
      try{
        if(window.speechSynthesis&&window.SpeechSynthesisUtterance){
          var profile=PreferenceAuthority.voiceProfile();
          var u=new SpeechSynthesisUtterance(line);u.lang="en-US";u.rate=profile.rate;u.pitch=profile.pitch;u.volume=profile.volume;
          u.onstart=function(){S.voice.speaking=true;Diagnostics.push("VoiceAuthority","WEB_STARTED",{text:line});};
          u.onend=function(){S.voice.speaking=false;Diagnostics.push("VoiceAuthority","WEB_ENDED",{text:line});};
          u.onerror=function(ev){S.voice.speaking=false;Diagnostics.error("VoiceAuthority","WEB_UTTERANCE_ERROR",{text:line,error:ev&&ev.error?String(ev.error):"unknown"});};
          try{if(window.speechSynthesis.paused){window.speechSynthesis.resume();}}catch(eResume){}
          window.speechSynthesis.speak(u);
          S.voice.lastText=line;S.voice.lastAt=now();
          Diagnostics.push("VoiceAuthority","WEB_SPEAK",{text:line,pending:!!window.speechSynthesis.pending,speaking:!!window.speechSynthesis.speaking});
          return true;
        }
      }catch(e2){Diagnostics.error("VoiceAuthority","WEB_ERROR",{message:String(e2&&e2.message||e2)});}
      return false;
    }
  };


  /* ==========================================================================
     V221 DRIVE BRIEFING AUTHORITY
     Short opening. ETA-aware destination weather. No invented conditions.

     CONTRACT:
     - Tell the driver Drive IQ is watching traffic + weather.
     - Only add traffic detail when it is meaningful and supported.
     - Evaluate destination weather across +/- 60 minutes around ETA.
     - Never interrupt an imminent maneuver or a fresh life-safety alert.
     - Speak once per drive ID.
  ========================================================================== */
  var BriefingAuthority = {
    timer:null,
    weather:null,
    weatherPending:false,
    started:false,

    alreadyBriefed:function(){
      return !!(S.session.id && clean(lsGet(LS.BRIEFED_DRIVE_ID))===clean(S.session.id));
    },

    etaSnapshot:function(){
      var meta=routeMetadata(),remain=RouteAuthority.remainingMiles(),seconds=null,totalMiles=meta.totalDistanceMiles||S.route.totalMiles;
      if(meta.totalSeconds!=null && totalMiles>0){
        seconds=Math.max(0,Number(meta.totalSeconds)*(remain/Math.max(totalMiles,0.001)));
      }
      return {
        seconds:seconds,
        minutes:seconds==null?null:Math.max(1,Math.round(seconds/60)),
        arrival:seconds==null?null:new Date(now()+seconds*1000)
      };
    },

    destinationName:function(){
      var d=S.route.destination||{},fix=RouteAuthority.destinationFix()||{};
      return clean(d.label||d.name||d.title||d.address||fix.label||"your destination");
    },

    imminentManeuver:function(){
      var m=S.maneuver.current;
      if(!m){return false;}
      var left=Math.max(0,num(m.distanceAlong)-num(S.route.progressMiles));
      return left<=0.22;
    },

    trafficSnapshot:function(){
      var intel=S.route.intel||{},delay=null,trafficDuration=null,baseDuration=null,i;
      var delayKeys=["trafficDelaySeconds","traffic_delay_seconds","delaySeconds","delay_seconds","trafficDelay","traffic_delay"];
      var trafficKeys=["trafficDurationSeconds","traffic_duration_seconds","trafficTime","traffic_time"];
      var baseKeys=["freeFlowDurationSeconds","free_flow_duration_seconds","baseDurationSeconds","base_duration_seconds"];
      for(i=0;i<delayKeys.length;i++){
        if(finite(Number(intel[delayKeys[i]]))){delay=Number(intel[delayKeys[i]]);break;}
      }
      if(delay==null){
        for(i=0;i<trafficKeys.length;i++){
          if(finite(Number(intel[trafficKeys[i]]))){trafficDuration=Number(intel[trafficKeys[i]]);break;}
        }
        for(i=0;i<baseKeys.length;i++){
          if(finite(Number(intel[baseKeys[i]]))){baseDuration=Number(intel[baseKeys[i]]);break;}
        }
        if(trafficDuration!=null&&baseDuration!=null){delay=Math.max(0,trafficDuration-baseDuration);}
      }
      return {
        known:delay!=null,
        delayMinutes:delay==null?null:Math.max(0,Math.round(delay/60)),
        closures:S.incidents.closures.length,
        issues:S.incidents.events.length
      };
    },

    weatherCodeText:function(code){
      code=Number(code);
      if(code===0){return "clear";}
      if(code===1||code===2){return "partly cloudy";}
      if(code===3){return "cloudy";}
      if(code===45||code===48){return "foggy";}
      if(code>=51&&code<=57){return "drizzle";}
      if(code>=61&&code<=67){return "rain";}
      if(code>=71&&code<=77){return "snow";}
      if(code>=80&&code<=82){return "rain showers";}
      if(code>=85&&code<=86){return "snow showers";}
      if(code>=95&&code<=99){return "thunderstorms";}
      return "";
    },

    weatherRiskScore:function(code,precip){
      code=Number(code);precip=num(precip,0);
      if(code>=95&&code<=99){return 100+precip;}
      if(code===45||code===48){return 80+precip;}
      if(code>=71&&code<=86){return 75+precip;}
      if(code>=61&&code<=67){return 55+precip;}
      if(code>=80&&code<=82){return 50+precip;}
      if(code>=51&&code<=57){return 30+precip;}
      return precip;
    },

    fetchWeather:function(force){
      if(this.weatherPending||(!force&&this.weather)){return;}
      var d=RouteAuthority.destinationFix(),eta=this.etaSnapshot();
      if(!d||!finite(Number(d.lat))||!finite(Number(d.lng))){return;}
      this.weatherPending=true;
      var url="https://api.open-meteo.com/v1/forecast?latitude="+encodeURIComponent(d.lat)+
        "&longitude="+encodeURIComponent(d.lng)+
        "&hourly=temperature_2m,precipitation_probability,weather_code"+
        "&temperature_unit=fahrenheit&forecast_days=3&timezone=auto";
      fetch(url).then(function(r){
        if(!r.ok){throw new Error("HTTP "+r.status);}
        return r.json();
      }).then(function(data){
        var h=data&&data.hourly||{},times=h.time||[],temps=h.temperature_2m||[],precip=h.precipitation_probability||[],codes=h.weather_code||[];
        if(!times.length){throw new Error("no hourly weather");}
        var target=eta.arrival?eta.arrival.getTime():now(),windowMs=CFG.weatherArrivalWindowMs;
        var best=-1,bestScore=-Infinity,nearest=0,nearestDiff=Infinity,i,t,diff,score;
        for(i=0;i<times.length;i++){
          t=new Date(times[i]).getTime();
          if(!finite(t)){continue;}
          diff=Math.abs(t-target);
          if(diff<nearestDiff){nearestDiff=diff;nearest=i;}
          if(diff<=windowMs){
            score=BriefingAuthority.weatherRiskScore(codes[i],precip[i]);
            if(score>bestScore){bestScore=score;best=i;}
          }
        }
        if(best<0){best=nearest;}
        BriefingAuthority.weather={
          tempF:finite(Number(temps[best]))?Math.round(Number(temps[best])):null,
          precip:finite(Number(precip[best]))?Math.round(Number(precip[best])):null,
          code:finite(Number(codes[best]))?Number(codes[best]):null,
          text:BriefingAuthority.weatherCodeText(codes[best]),
          targetTime:times[best]||"",
          etaWindowMinutes:60
        };
        BriefingAuthority.weatherPending=false;
        Diagnostics.push("BriefingAuthority","WEATHER_READY",BriefingAuthority.weather);
      }).catch(function(e){
        BriefingAuthority.weatherPending=false;
        Diagnostics.push("BriefingAuthority","WEATHER_UNAVAILABLE",{message:String(e&&e.message||e)});
      });
    },

    trafficLine:function(t){
      if(t.closures>0){return "A road closure is showing ahead.";}
      if(t.known&&t.delayMinutes>=5){return "Traffic is adding about "+t.delayMinutes+" minutes.";}
      if(t.issues>0){return "Road conditions are being reported along the route.";}
      return "";
    },

    weatherLine:function(){
      var w=this.weather;
      if(!w){return "";}
      if(w.text==="thunderstorms"){return "Thunderstorms are possible near your destination around arrival.";}
      if(w.text==="snow"||w.text==="snow showers"){return "Snow is possible near your destination around arrival.";}
      if(w.text==="foggy"){return "Fog may affect conditions near your destination around arrival.";}
      if(w.precip!=null&&w.precip>=55){
        return "Rain is possible near your destination around arrival, around a "+w.precip+" percent chance.";
      }
      return "";
    },

    build:function(){
      var eta=this.etaSnapshot(),name=this.destinationName(),traffic=this.trafficSnapshot();
      var etaText=eta.minutes!=null?(eta.minutes+" minute"+(eta.minutes===1?"":"s")):"";
      var trafficText=this.trafficLine(traffic),weatherText=this.weatherLine(),lead="Route locked.";
      if(etaText&&name&&name!=="your destination"){
        lead+=" About "+etaText+" to "+name+".";
      }else if(etaText){
        lead+=" About "+etaText+" remaining.";
      }
      return clean(lead+" I'm watching traffic and weather ahead. "+trafficText+" "+weatherText);
    },

    speak:function(reason){
      if(!S.voice.enabled||!S.session.active||!S.session.routeActive||S.arrival.arrived){return false;}
      if(this.alreadyBriefed()){return false;}
      if(S.weatherSafety&&S.weatherSafety.lastAlertAt&&now()-S.weatherSafety.lastAlertAt<20000){
        var selfSafety=this;
        Diagnostics.push("BriefingAuthority","DEFER_SAFETY_ALERT",{reason:reason||""});
        this.timer=window.setTimeout(function(){selfSafety.speak("safety-clear");},12000);
        return false;
      }
      if(this.imminentManeuver()){
        Diagnostics.push("BriefingAuthority","DEFER_IMMINENT_MANEUVER",{reason:reason||""});
        var self=this;
        this.timer=window.setTimeout(function(){self.speak("maneuver-clear");},9000);
        return false;
      }
      var line=this.build();
      if(!line){return false;}
      if(!VoiceAuthority.speak(line,"drive-briefing")){return false;}
      lsSet(LS.BRIEFED_DRIVE_ID,S.session.id);
      S.maneuver.lastSpokenAt=now();
      Diagnostics.push("BriefingAuthority","SPOKEN",{reason:reason||"",text:line});
      return true;
    },

    start:function(){
      if(this.started||!S.session.active||!S.session.routeActive||this.alreadyBriefed()){return false;}
      this.started=true;
      this.fetchWeather(false);
      var self=this;
      this.timer=window.setTimeout(function(){self.speak("opening");},6500);
      Diagnostics.push("BriefingAuthority","SCHEDULED",{delayMs:6500});
      return true;
    }
  };

  /* ==========================================================================
     V221 WEATHER SAFETY IQ — ROUTE CORRIDOR + LIFE-SAFETY WATCH

     - Forecasts the route corridor at the time the driver is expected to reach it.
     - Correlates meaningful route weather with known aggregate traffic delay.
     - Uses NOAA/NWS active alerts for U.S. life-safety warnings when available.
     - Never says a route is safe and never tells a driver to continue into danger.
     - Severe alerts are elevated immediately; ordinary weather stays quiet.
  ========================================================================== */
  function routeSamplePlan(){
    var coords=S.route.coords||[],max=Math.max(2,CFG.weatherRouteSampleMax),start=Math.max(0,S.route.progressIndex||0);
    var end=coords.length-1,out=[],i,idx,along,remaining=RouteAuthority.remainingMiles(),eta=BriefingAuthority.etaSnapshot();
    if(coords.length<2||start>=end){return out;}
    for(i=0;i<max;i++){
      idx=Math.round(start+(end-start)*(i/(max-1)));
      idx=clamp(idx,start,end);
      along=Math.max(0,cumulativeMiles(coords,idx)-S.route.progressMiles);
      out.push({
        index:idx,
        lat:Number(coords[idx][1]),
        lng:Number(coords[idx][0]),
        milesAhead:along,
        etaMs:eta.seconds!=null&&remaining>0?now()+eta.seconds*1000*clamp(along/remaining,0,1):now()
      });
    }
    return out;
  }

  function forecastImpact(code,precip,wind,gust,visibility,snowfall){
    code=Number(code);precip=num(precip,0);wind=num(wind,0);gust=num(gust,0);visibility=num(visibility,99999);snowfall=num(snowfall,0);
    if(code>=95&&code<=99){return {level:"high",type:"thunderstorm",label:"Thunderstorms",score:100+precip};}
    if(code===45||code===48||visibility<1600){return {level:"high",type:"visibility",label:"Low visibility or fog",score:90};}
    if(snowfall>0.1||(code>=71&&code<=86)){return {level:"high",type:"snow",label:"Snow or wintry weather",score:85+precip};}
    if(gust>=45||wind>=35){return {level:"high",type:"wind",label:"Strong winds",score:82+Math.max(gust,wind)};}
    if(((code>=61&&code<=67)||(code>=80&&code<=82))&&precip>=60){return {level:"medium",type:"rain",label:"Rain",score:65+precip};}
    if(precip>=70){return {level:"medium",type:"precip",label:"Wet weather",score:60+precip};}
    return null;
  }


  function weatherCodeLabel(code){
    code=Number(code);
    if(code===0){return "Clear";}
    if(code===1){return "Mostly clear";}
    if(code===2){return "Partly cloudy";}
    if(code===3){return "Cloudy";}
    if(code===45||code===48){return "Low visibility";}
    if(code===51||code===53){return "Light drizzle";}
    if(code===55||code===56||code===57){return "Drizzle";}
    if(code===61){return "Light rain";}
    if(code===63||code===66){return "Rain";}
    if(code===65||code===67){return "Heavy rain";}
    if(code>=71&&code<=77){return "Snow";}
    if(code===80){return "Light showers";}
    if(code===81){return "Showers";}
    if(code===82){return "Heavy showers";}
    if(code>=85&&code<=86){return "Snow showers";}
    if(code>=95&&code<=99){return "Thunderstorms";}
    return "Weather checked";
  }

  function weatherCodeHasRoutePrecip(code){
    code=Number(code);
    return (
      (code>=51&&code<=67) ||
      (code>=71&&code<=77) ||
      (code>=80&&code<=86) ||
      (code>=95&&code<=99)
    );
  }

  function routeWeatherCountdownText(forecast){
    if(!forecast||!forecast.sample){return "";}

    var etaMs=Number(forecast.sample.etaMs);
    if(!isFinite(etaMs)){return "";}

    var min=Math.max(0,Math.round(etaMs/60000));

    if(min<=1){return "now";}
    if(min<60){return "in "+min+" min";}

    var h=Math.floor(min/60),m=min%60;
    return m>=15?("in "+h+"h "+Math.round(m/15)*15+"m"):("in "+h+"h");
  }

  function estimateRouteWeatherMinutes(forecasts,chosenIndex){
    if(!forecasts||forecasts.length<2||chosenIndex==null||chosenIndex<0){return null;}

    var i,start=chosenIndex,end=chosenIndex,current,prev,next;
    current=forecasts[chosenIndex];
    if(!current||!weatherCodeHasRoutePrecip(current.code)){return null;}

    for(i=chosenIndex-1;i>=0;i--){
      prev=forecasts[i];
      if(!prev||!weatherCodeHasRoutePrecip(prev.code)){break;}
      start=i;
    }

    for(i=chosenIndex+1;i<forecasts.length;i++){
      next=forecasts[i];
      if(!next||!weatherCodeHasRoutePrecip(next.code)){break;}
      end=i;
    }

    if(start===end){return null;}

    var startMs=Number(forecasts[start]&&forecasts[start].sample&&forecasts[start].sample.etaMs);
    var endMs=Number(forecasts[end]&&forecasts[end].sample&&forecasts[end].sample.etaMs);
    if(!isFinite(startMs)||!isFinite(endMs)||endMs<=startMs){return null;}

    var minutes=Math.round((endMs-startMs)/60000);
    if(minutes<3){return null;}
    return Math.min(180,minutes);
  }

  function renderNormalRouteWeather(forecasts){
    if(!forecasts||!forecasts.length){
      setSignal("diqSigWeather","Weather data unavailable");
      Diagnostics.push("WeatherSafetyAuthority","NORMAL_WEATHER_UNAVAILABLE",{});
      return false;
    }

    var chosen=null,chosenIndex=-1,i,miles;
    var currentForecast=forecasts[0]||null;
    S.weatherSafety.currentForecast=currentForecast;
    AtmosphericRainAuthority.syncCurrentForecast(
      currentForecast,
      "normal-route-weather"
    );

    /* Prefer a point meaningfully ahead so the weather cell describes the road
       in front of the driver, not merely the current GPS point. */
    for(i=0;i<forecasts.length;i++){
      miles=num(forecasts[i]&&forecasts[i].sample&&forecasts[i].sample.milesAhead,-1);
      if(miles>=5){chosen=forecasts[i];chosenIndex=i;break;}
    }
    if(!chosen){chosen=forecasts[0];chosenIndex=0;}

    miles=num(chosen&&chosen.sample&&chosen.sample.milesAhead,0);
    var label=weatherCodeLabel(chosen.code);
    var precip=Math.round(num(chosen.precip,0));
    var durationMin=estimateRouteWeatherMinutes(forecasts,chosenIndex);
    var countdown=routeWeatherCountdownText(chosen);
    var isPrecip=weatherCodeHasRoutePrecip(chosen.code);
    var parts=[];

    if(isPrecip && countdown){
      parts.push(countdown==="now" ? (label+" now") : (label+" "+countdown));
    }else{
      parts.push(label);
    }

    if(miles>=1){
      parts.push((miles<10?Math.max(1,Math.round(miles)):Math.round(miles))+" mi");
    }

    if(durationMin!==null){
      parts.push("~"+durationMin+" min");
    }else if(!countdown && precip>=20){
      parts.push(precip+"%");
    }

    /*
      V248: radar visibility must not be decided only by the farther-ahead display
      sample. If the current/near-vehicle sample shows precipitation or a meaningful
      precipitation probability, keep the radar layer visible so actual nearby
      echoes are not hidden while the driver is in a sprinkle.
    */
    var currentPrecip=Math.round(num(currentForecast&&currentForecast.precip,0));
    var currentCode=num(currentForecast&&currentForecast.code,-1);
    WeatherRadarAuthority.setVisible(
      WeatherRadarAuthority.weatherCodeHasPrecip(currentCode) ||
      currentPrecip>=30 ||
      WeatherRadarAuthority.weatherCodeHasPrecip(chosen.code) ||
      precip>=40,
      "normal-route-weather"
    );
    setSignal("diqSigWeather",parts.join(" • "));
    Diagnostics.push("WeatherSafetyAuthority","NORMAL_WEATHER_RENDERED",{
      label:label,
      code:chosen.code,
      precip:precip,
      milesAhead:miles,
      estimatedDurationMinutes:durationMin
    });
    return true;
  }

  function weatherPointForecast(sample){
    var url="https://api.open-meteo.com/v1/forecast?latitude="+encodeURIComponent(sample.lat)+
      "&longitude="+encodeURIComponent(sample.lng)+
      "&hourly=precipitation_probability,weather_code,wind_speed_10m,wind_gusts_10m,visibility,snowfall"+
      "&wind_speed_unit=mph&forecast_days=3&timezone=auto";
    return fetch(url).then(function(r){
      if(!r.ok){throw new Error("forecast HTTP "+r.status);}
      return r.json();
    }).then(function(data){
      var h=data&&data.hourly||{},times=h.time||[],best=0,bestDiff=Infinity,i,t,diff;
      for(i=0;i<times.length;i++){
        t=new Date(times[i]).getTime();if(!finite(t)){continue;}
        diff=Math.abs(t-sample.etaMs);if(diff<bestDiff){bestDiff=diff;best=i;}
      }
      var impact=forecastImpact(
        (h.weather_code||[])[best],
        (h.precipitation_probability||[])[best],
        (h.wind_speed_10m||[])[best],
        (h.wind_gusts_10m||[])[best],
        (h.visibility||[])[best],
        (h.snowfall||[])[best]
      );
      return {
        sample:sample,
        impact:impact,
        precip:num((h.precipitation_probability||[])[best],0),
        code:num((h.weather_code||[])[best],-1),
        windMph:num((h.wind_speed_10m||[])[best],0),
        gustMph:num((h.wind_gusts_10m||[])[best],0),
        visibilityM:num((h.visibility||[])[best],99999),
        forecastTime:(times||[])[best]||""
      };
    });
  }

  function severeEventKind(name){
    var s=clean(name).toLowerCase();
    if(/tornado warning/.test(s)){return "tornado";}
    if(/flash flood warning/.test(s)){return "flash_flood";}
    if(/severe thunderstorm warning/.test(s)){return "severe_thunderstorm";}
    if(/hurricane warning|hurricane local statement/.test(s)){return "hurricane";}
    if(/storm surge warning/.test(s)){return "storm_surge";}
    if(/extreme wind warning/.test(s)){return "extreme_wind";}
    return "";
  }

  function nwsAlertsForPoint(sample){
    var url="https://api.weather.gov/alerts/active?point="+encodeURIComponent(sample.lat+","+sample.lng);
    return fetch(url,{headers:{Accept:"application/geo+json"}}).then(function(r){
      if(!r.ok){throw new Error("NWS HTTP "+r.status);}
      return r.json();
    }).then(function(data){
      var f=data&&data.features||[],out=[],i,props,kind;
      for(i=0;i<f.length;i++){
        props=f[i]&&f[i].properties||{};
        kind=severeEventKind(props.event||"");
        if(!kind){continue;}
        out.push({
          id:clean(f[i].id||props.id||props["@id"]||((props.event||"")+"|"+(props.sent||""))),
          kind:kind,
          event:clean(props.event||"Severe weather warning"),
          headline:clean(props.headline||""),
          instruction:clean(props.instruction||""),
          severity:clean(props.severity||""),
          certainty:clean(props.certainty||""),
          expires:clean(props.expires||""),
          sample:sample,
          source:"NOAA/NWS"
        });
      }
      return out;
    });
  }

  /* ==========================================================================
     V224 CLOUD INTELLIGENCE PRESENTATION — READ ONLY

     HARD RULES
     - Listens only for native DriveIntelligenceService events.
     - Presentation only: never changes route, GPS, camera, marker, maneuver,
       reroute, arrival, CarPlay navigation state, or voice.
     - Uses the EXISTING Weather cell in .diqHorizon. No new floating card,
       sheet, overlay, or map obstruction is created.
     - Native/cloud failure never blocks navigation.
     - Existing life-safety weather remains authoritative and may overwrite
       this presentation at any time.
  ========================================================================== */
  var CloudIntelligencePresentation = {
    installed:false,

    normalizeEnvelope:function(detail){
      var d=detail||{};
      if(d.detail&&typeof d.detail==="object"){d=d.detail;}
      if(d.response&&typeof d.response==="object"){d=d.response;}
      if(d.payload&&typeof d.payload==="object"){d=d.payload;}
      return d&&typeof d==="object"?d:{};
    },

    advisoryList:function(detail){
      var d=this.normalizeEnvelope(detail);
      return Array.isArray(d.advisories)?d.advisories:[];
    },

    weatherAdvisory:function(detail){
      var list=this.advisoryList(detail),i,a;
      for(i=0;i<list.length;i++){
        a=list[i];
        if(a&&String(a.type||"").toLowerCase()==="weather"){return a;}
      }
      return null;
    },

    trafficAdvisory:function(detail){
      var list=this.advisoryList(detail),i,a;
      for(i=0;i<list.length;i++){
        a=list[i];
        if(a&&String(a.type||"").toLowerCase()==="traffic"){return a;}
      }
      return null;
    },

    isLifeSafetyActive:function(){
      return !!(S.weatherSafety&&Array.isArray(S.weatherSafety.severe)&&S.weatherSafety.severe.length);
    },

    milesAhead:function(advisory){
      var meters=Number(advisory&&advisory.distanceAheadMeters);
      if(!isFinite(meters)||meters<0){return null;}
      return meters/1609.344;
    },

    compactEta:function(advisory){
      var sec=Number(advisory&&advisory.etaToEventSeconds);
      if(!isFinite(sec)||sec<0){return "";}
      var min=Math.max(1,Math.round(sec/60));
      if(min<=2){return "very soon";}
      if(min<60){return "in "+min+" min";}
      var h=Math.floor(min/60),m=min%60;
      return m>=15?("in "+h+"h "+Math.round(m/15)*15+"m"):("in "+h+"h");
    },

    compactDuration:function(advisory){
      if(!advisory){return "";}

      var secKeys=[
        "eventDurationSeconds",
        "weatherDurationSeconds",
        "durationSeconds"
      ];
      var minKeys=[
        "eventDurationMinutes",
        "weatherDurationMinutes",
        "durationMinutes"
      ];
      var i,v,minutes=null;

      for(i=0;i<secKeys.length;i++){
        v=Number(advisory[secKeys[i]]);
        if(isFinite(v)&&v>0){minutes=Math.round(v/60);break;}
      }

      if(minutes===null){
        for(i=0;i<minKeys.length;i++){
          v=Number(advisory[minKeys[i]]);
          if(isFinite(v)&&v>0){minutes=Math.round(v);break;}
        }
      }

      if(minutes===null){
        var start=Date.parse(advisory.eventStartsAt||advisory.startsAt||"");
        var end=Date.parse(advisory.eventEndsAt||advisory.endsAt||"");
        if(isFinite(start)&&isFinite(end)&&end>start){
          minutes=Math.round((end-start)/60000);
        }
      }

      if(minutes===null||minutes<3){return "";}
      minutes=Math.min(180,minutes);
      return "for ~"+minutes+" min";
    },

    compactCondition:function(advisory){
      var msg=clean(advisory&&advisory.message||"");
      if(!msg){return "Route looks clear";}
      var lower=msg.toLowerCase();
      if(lower.indexOf("thunderstorm")>=0){return "Thunderstorms";}
      if(lower.indexOf("heavy rain")>=0){return "Heavy rain";}
      if(lower.indexOf("moderate rain")>=0){return "Rain";}
      if(lower.indexOf("rain")>=0){return "Rain possible";}
      if(lower.indexOf("snow")>=0||lower.indexOf("wintry")>=0){return "Wintry weather";}
      if(lower.indexOf("visibility")>=0||lower.indexOf("fog")>=0){return "Low visibility";}
      if(lower.indexOf("wind")>=0){return "Strong winds";}
      if(lower.indexOf("heat")>=0){return "Extreme heat";}
      if(lower.indexOf("cold")>=0){return "Extreme cold";}
      return msg.replace(/\s+about\s+\d+(?:\.\d+)?\s+miles?\s+ahead\.?/i,"")
                .replace(/\s+ahead\.?$/i,"")
                .replace(/\.$/,"")
                .slice(0,34);
    },

    renderWeather:function(reason){
      if(FreeTierIQ.free()){return false;}
      var a=S.cloudIntelligence.weather;
      if(this.isLifeSafetyActive()){
        Diagnostics.push("CloudIntelligence","WEATHER_YIELD_LIFE_SAFETY",{reason:reason||""});
        return false;
      }
      if(!a){
        WeatherRadarAuthority.setVisible(false,"cloud-no-weather-advisory");
        if(S.cloudIntelligence.connectivity==="offline"){setSignal("diqSigWeather","Weather offline");}
        else if(S.cloudIntelligence.connectivity==="degraded"){setSignal("diqSigWeather","Weather limited");}
        else{setSignal("diqSigWeather","Weather IQ ready");}
        return true;
      }
      var providerStatus=clean(a.providerStatus||"").toLowerCase();
      if(providerStatus&&providerStatus!=="ok"){
        setSignal("diqSigWeather",S.cloudIntelligence.connectivity==="offline"?"Weather offline":"Weather IQ ready");
        return true;
      }
      var condition=this.compactCondition(a),miles=this.milesAhead(a),eta=this.compactEta(a),duration=this.compactDuration(a),parts=[];
      var precipCondition=/rain|shower|thunder|snow|wintry|drizzle/.test(condition.toLowerCase());

      if(precipCondition && eta){
        parts.push(condition+" "+eta);
      }else{
        parts.push(condition);
      }

      /* Cloud advisory can reinforce local rain only when the event is
         essentially at the vehicle. It must not turn on windshield rain
         for weather that is still far ahead. */
      if(miles!==null && miles<=2 && WeatherRadarAuthority.weatherTextHasPrecip((a&&a.message)||condition)){
        var cloudText=clean((a&&a.message)||condition).toLowerCase();
        AtmosphericRainAuthority.apply(
          /heavy|thunder/.test(cloudText)?"heavy":(/moderate/.test(cloudText)?"moderate":"light"),
          "cloud-near-vehicle"
        );
      }else if(miles!==null && miles>2 && !S.weatherSafety.currentForecast){
        AtmosphericRainAuthority.apply("off","cloud-weather-ahead-only");
      }

      WeatherRadarAuthority.setVisible(
        WeatherRadarAuthority.weatherTextHasPrecip((a&&a.message)||condition),
        "cloud-weather"
      );
      if(miles!==null){parts.push(miles<10?(Math.max(1,Math.round(miles))+" mi"):(Math.round(miles)+" mi"));}
      if(duration){parts.push(duration.replace(/^for\s+/i,""));}
      else if(eta && !precipCondition){parts.push(eta);}
      setSignal("diqSigWeather",parts.join(" • "));
      Diagnostics.push("CloudIntelligence","WEATHER_RENDERED",{reason:reason||"",severity:a.severity||"",milesAhead:miles,eta:a.etaToEventSeconds,duration:duration,provider:a.provider||"",status:a.providerStatus||""});
      return true;
    },

    trafficMilesAhead:function(advisory){
      return this.milesAhead(advisory);
    },

    trafficDelayMinutes:function(advisory){
      if(!advisory||advisory.delaySeconds==null){return null;}
      var sec=Number(advisory.delaySeconds);
      return isFinite(sec)&&sec>=0?Math.max(0,Math.round(sec/60)):null;
    },

    /* Guardrail: the cloud estimates its own shaped-route travel time, while
       the trip HUD uses the original route model. An unexplained discrepancy
       must not be presented as a verified added delay or used to offer a
       potentially mismatched alternative. This does NOT change the HUD ETA. */
    trafficEtaAudit:function(advisory){
      var meta=routeMetadata();
      var remain=RouteAuthority.remainingMiles();
      var totalMiles=meta.totalDistanceMiles||S.route.totalMiles;
      var hudSeconds=(meta.totalSeconds!=null&&totalMiles>0)?
        Math.max(0,Number(meta.totalSeconds)*(remain/Math.max(totalMiles,0.001))):null;
      var cloudCurrent=(advisory&&advisory.currentDurationSeconds!=null)?Number(advisory.currentDurationSeconds):null;
      var cloudTypical=(advisory&&advisory.typicalDurationSeconds!=null)?Number(advisory.typicalDurationSeconds):null;
      var cloudDelay=(advisory&&advisory.delaySeconds!=null)?Number(advisory.delaySeconds):null;
      var deviceSent=(advisory&&advisory.deviceRemainingEtaSeconds!=null)?Number(advisory.deviceRemainingEtaSeconds):null;
      var reason="",comparable=false;
      if(cloudCurrent!==null&&!isFinite(cloudCurrent)){cloudCurrent=null;}
      if(cloudTypical!==null&&!isFinite(cloudTypical)){cloudTypical=null;}
      if(cloudDelay!==null&&!isFinite(cloudDelay)){cloudDelay=null;}
      if(hudSeconds!==null&&!isFinite(hudSeconds)){hudSeconds=null;}
      if(deviceSent!==null&&!isFinite(deviceSent)){deviceSent=null;}
      if(cloudCurrent!==null&&cloudCurrent<=0){reason="invalid-current-duration";}
      else if(cloudTypical!==null&&cloudTypical<=0){reason="invalid-typical-duration";}
      else if(cloudDelay!==null&&cloudCurrent!==null&&cloudDelay>cloudCurrent+30){reason="delay-exceeds-cloud-trip";}
      else if(cloudDelay!==null&&cloudCurrent!==null&&cloudTypical!==null&&
              Math.abs((cloudCurrent-cloudTypical)-cloudDelay)>90){reason="cloud-duration-inconsistent";}
      else if(hudSeconds!==null&&hudSeconds>=60&&cloudDelay!==null&&
              cloudDelay>hudSeconds*1.1&&cloudDelay>hudSeconds+300){
        reason="delay-greater-than-hud-trip";
      }
      else if(hudSeconds!==null&&hudSeconds>=60&&cloudCurrent!==null&&
              cloudCurrent>hudSeconds*1.5&&cloudCurrent-hudSeconds>600){
        /* Example: HUD 23 min with a separate +31 min cloud delay. Until
           source routes can be reconciled, display congestion but not +31. */
        reason="cloud-vs-hud-unreconciled";
      }else if(hudSeconds!==null&&hudSeconds>=60&&cloudCurrent!==null&&
               hudSeconds>cloudCurrent*1.5&&hudSeconds-cloudCurrent>600){
        reason="hud-vs-cloud-unreconciled";
      }else if(cloudCurrent===null||cloudTypical===null){
        reason="provider-baseline-unavailable";
      }else{comparable=true;}
      return {
        trusted:comparable,reason:reason,
        hudSeconds:hudSeconds==null?null:Math.round(hudSeconds),
        cloudCurrentSeconds:cloudCurrent,cloudTypicalSeconds:cloudTypical,
        cloudDelaySeconds:cloudDelay,deviceSentEtaSeconds:deviceSent,
        remainingMiles:Math.round(remain*10)/10
      };
    },

    trafficSafeDelayMinutes:function(advisory){
      return this.trafficEtaAudit(advisory).trusted?this.trafficDelayMinutes(advisory):null;
    },

    trafficSavingsMinutes:function(advisory){
      var min=Number(advisory&&advisory.timeSavedMinutes);
      if(isFinite(min)&&min>=0){return Math.round(min);}
      var sec=Number(advisory&&advisory.timeSavedSeconds);
      return isFinite(sec)&&sec>=0?Math.max(0,Math.round(sec/60)):0;
    },

    trafficLabel:function(advisory){
      if(!advisory){return "Traffic watch";}
      var status=clean(advisory.providerStatus||"").toLowerCase();
      if(status&&status!=="ok"){
        return S.cloudIntelligence.connectivity==="offline"?"Traffic offline":"Traffic watch";
      }
      var delay=this.trafficSafeDelayMinutes(advisory);
      var closure=!!advisory.closureAhead;
      var label=clean(advisory.worstCongestionLabel||"").toLowerCase();
      var severity=clean(advisory.severity||"").toLowerCase();
      // V235: keep the delay visible in the wider V144 traffic pill. Full
      // distance and explanatory message remain available on tap.
      var heading=closure?"Closure":
        (label==="severe"||label==="heavy"||severity==="high"?"Heavy traffic":
        (label==="moderate"||severity==="medium"?"Moderate traffic":
        (delay!=null&&delay>0?"Traffic ahead":"Traffic clear")));
      var audit=this.trafficEtaAudit(advisory);
      if(!audit.trusted){
        return heading==="Traffic clear"?"Traffic monitoring":heading;
      }
      return delay!=null&&delay>0 ? heading+" • +"+delay+" min" : heading;
    },

    trafficDetailText:function(advisory){
      if(!advisory){return "Live traffic is still loading.";}
      var parts=[],audit=this.trafficEtaAudit(advisory),delay=this.trafficSafeDelayMinutes(advisory),
          miles=this.trafficMilesAhead(advisory),savings=this.trafficSavingsMinutes(advisory);
      if(!audit.trusted){
        parts.push("Traffic detected; delay estimate is being checked against your ETA.");
        if(miles!==null&&miles>=0){parts.push("Congestion about "+(Math.round(miles*10)/10)+" mi ahead.");}
        return parts.join(" ");
      }
      if(clean(advisory.message||"")){parts.push(clean(advisory.message));}
      if(delay!=null&&delay>0){parts.push("Estimated added time: "+delay+" min (already included in cloud traffic travel time).");}
      if(miles!==null&&miles>=0){parts.push("Traffic begins about "+(Math.round(miles*10)/10)+" mi ahead.");}
      if(advisory.rerouteSuggested&&savings>0){parts.push("Alternative estimate: save "+savings+" min.");}
      return parts.length?parts.join(" "):"No significant delay currently reported.";
    },

    installTrafficDetailTap:function(){
      var pill=byId("diqSigTraffic");
      if(!pill||pill.__diqTrafficTapInstalled){return;}
      pill.__diqTrafficTapInstalled=true;
      // Tapping the text (including its pill's empty space) reveals the full
      // advisory via an existing toast. No new overlay obscures the map.
      var host=pill.closest?pill.closest(".diqSignal"):pill;
      if(!host){host=pill;}
      host.style.cursor="pointer";
      host.setAttribute("role","button");
      host.setAttribute("aria-label","Traffic details");
      host.addEventListener("click",function(){
        var a=S.cloudIntelligence.traffic;
        Diagnostics.push("TrafficIQ","DETAIL_OPEN",{delayMinutes:CloudIntelligencePresentation.trafficDelayMinutes(a)});
        var message=CloudIntelligencePresentation.trafficDetailText(a);
        // Existing toast is deliberately short; the pill's title exposes full
        // text for accessibility/Web Inspector without creating a modal.
        toast(message.length>72?message.slice(0,69)+"…":message);
      });
    },

    shouldResurfaceTrafficPrompt:function(advisory){
      var saved=S.cloudIntelligence.trafficSuppressed;
      if(!saved){return true;}
      var delay=this.trafficDelayMinutes(advisory);
      var savings=this.trafficSavingsMinutes(advisory);
      if(delay!=null&&saved.delayMinutes!=null&&delay>=saved.delayMinutes+5){return true;}
      if(savings>=Number(saved.savingsMinutes||0)+3){return true;}
      return false;
    },

    removeTrafficPrompt:function(reason){
      var el=byId("diqTrafficReroutePrompt");
      if(el&&el.parentNode){try{el.parentNode.removeChild(el);}catch(e){}}
      S.cloudIntelligence.trafficPrompt=null;
      Diagnostics.push("TrafficIQ","PROMPT_HIDDEN",{reason:reason||""});
      return true;
    },

    trafficRouteFeature:function(advisory){
      var alt=advisory&&advisory.alternativeRoute;
      var g=alt&&alt.geometry;
      if(!g||g.type!=="LineString"||!Array.isArray(g.coordinates)||g.coordinates.length<2){return null;}
      return {
        type:"Feature",
        properties:{
          source:"drive-iq-traffic-iq",
          durationSeconds:finite(Number(alt.durationSeconds))?Number(alt.durationSeconds):null,
          distanceMeters:finite(Number(alt.distanceMeters))?Number(alt.distanceMeters):null
        },
        geometry:g
      };
    },

    validatedTrafficAlternative:function(advisory){
      if(!advisory || clean(advisory.providerStatus).toLowerCase()!=="ok" ||
         advisory.rerouteSuggested!==true || advisory.alternativeAvailable!==true ||
         !this.trafficEtaAudit(advisory).trusted){return null;}
      if(S.cloudIntelligence.trafficRouteSignature!==S.route.signature){return null;}
      if(!S.session.routeActive||S.arrival.arrived||S.route.replacing||S.reroute.busy){return null;}
      if(!S.location.raw || now()-num(S.location.raw.ts)>15000){return null;}
      var expires=Date.parse(advisory.expiresAt||"");
      if(!isFinite(expires) || expires<=now() || now()-S.cloudIntelligence.lastAt>5*60*1000){return null;}
      var alt=advisory.alternativeRoute,feature=this.trafficRouteFeature(advisory);
      if(!alt||!feature||alt.guidanceReady!==true || !Array.isArray(alt.instructions)||alt.instructions.length<2){return null;}
      var coords=feature.geometry.coordinates,fix=S.location.raw,dest=RouteAuthority.destinationFix(),last=coords[coords.length-1];
      // Reject duplicate routes, invalid coordinate arrays and routes whose
      // origin/destination no longer match the active drive.
      if(geometrySignature(feature)===S.route.signature){return null;}
      if(!coords.every(function(c){return Array.isArray(c)&&c.length>=2&&
          finite(Number(c[0]))&&finite(Number(c[1]))&&
          Number(c[0])>=-180&&Number(c[0])<=180&&
          Number(c[1])>=-90&&Number(c[1])<=90;})){return null;}
      // Do not install a proposed route from an origin the driver has passed.
      if(haversineMiles(fix,{lng:coords[0][0],lat:coords[0][1]})>0.40){return null;}
      if(!dest||haversineMiles(dest,{lng:last[0],lat:last[1]})>0.35){return null;}
      var verified=[],steps=alt.instructions,i,o,location,projection,idx,txt,cls,street,prior=-1;
      var epsilon=0.12;
      for(i=0;i<steps.length;i++){
        o=steps[i];location=o&&o.maneuver&&o.maneuver.location;
        txt=clean(o&&o.instruction||"");
        if(!txt||!Array.isArray(location)||location.length<2||
           !finite(Number(location[0]))||!finite(Number(location[1])) ||
           !finite(Number(o.distanceMeters))||!finite(Number(o.durationSeconds))){return null;}
        projection=nearestProjection({lng:Number(location[0]),lat:Number(location[1])},coords,null);
        if(!projection||projection.distMiles>epsilon){return null;}
        idx=nearestRouteIndexToPoint(coords,Number(location[0]),Number(location[1]));
        if(idx===null||idx<prior){return null;}
        prior=idx;
        cls=classifyInstruction(txt);
        if(clean(o.maneuver&&o.maneuver.type).toLowerCase()==="depart" ||
           (cls.type==="continue" && /\bdepart\b/i.test(txt))){continue;}
        if(cls.type==="arrival"||idx>=coords.length-1){continue;}
        street=bestInstructionStreet(o);
        verified.push({
          index:idx,distanceAlong:cumulativeMiles(coords,idx),
          type:cls.type,label:cls.label,icon:cls.icon,
          street:street,road:street,rawText:txt,
          streetSource:street?"mapbox-step":"",source:"verified-mapbox-traffic"
        });
      }
      // Require an actual matched instruction ahead, not just depart/arrive.
      if(!verified.length){return null;}
      feature.properties.diqTrafficVerifiedGuidance=true;
      feature.properties.diqTrafficManeuvers=verified;
      return {feature:feature,maneuvers:verified};
    },

    acceptTrafficReroute:function(){
      var a=S.cloudIntelligence.traffic,prepared=this.validatedTrafficAlternative(a);
      if(!prepared){
        Diagnostics.error("TrafficIQ","ACCEPT_BLOCKED_VALIDATION",{
          guidanceFlag:!!(a&&a.alternativeRoute&&a.alternativeRoute.guidanceReady),
          instructionCount:a&&a.alternativeRoute&&a.alternativeRoute.instructions?a.alternativeRoute.instructions.length:0,
          currentRouteSignature:S.route.signature,
          advisoryRouteSignature:S.cloudIntelligence.trafficRouteSignature
        });
        toast("Faster route expired or guidance unavailable");
        this.removeTrafficPrompt("guidance-not-verified");
        return false;
      }
      // Synchronous, driver-initiated in-place handoff. Maneuvers are embedded
      // in this SAME feature, so RouteAuthority.install rebuilds them together.
      // No navigation engine, map, or marker is recreated.
      var original={geo:S.route.geo,intel:S.route.intel,dest:S.route.destination};
      var alt=a.alternativeRoute;
      var nextIntel=Object.assign({},original.intel||{});
      if(finite(Number(alt.durationSeconds))&&Number(alt.durationSeconds)>0){
        nextIntel.durationSeconds=Number(alt.durationSeconds);
        nextIntel.duration=Number(alt.durationSeconds);
      }
      if(finite(Number(alt.distanceMeters))&&Number(alt.distanceMeters)>0){
        nextIntel.distanceMeters=Number(alt.distanceMeters);
        nextIntel.distance=Number(alt.distanceMeters);
        nextIntel.miles=Number(alt.distanceMeters)/1609.344;
        nextIntel.distanceMiles=nextIntel.miles;
      }
      // Old traffic estimates belong to the former route only.
      nextIntel.trafficDelaySeconds=0;
      nextIntel.source="traffic-iq-driver-accepted";
      try{
        if(!RouteAuthority.install(prepared.feature,nextIntel,original.dest,"traffic-iq-driver-accepted")){
          throw new Error("route-install-failed");
        }
        if(S.maneuver.list.length!==prepared.maneuvers.length ||
           !S.maneuver.instructionFetched ||
           S.maneuver.instructionRouteSig!==S.route.signature){
          throw new Error("verified-maneuver-install-mismatch");
        }
        if(!lsJson(LS.ROUTE_GEO,prepared.feature) || !lsJson(LS.ROUTE_INTEL,nextIntel)){
          throw new Error("route-persistence-failed");
        }
        var savedPayload=parse(lsGet(LS.DRIVE_PAYLOAD));
        if(savedPayload&&typeof savedPayload==="object"&&savedPayload.route){
          savedPayload.route=prepared.feature;
          savedPayload.route_intel=nextIntel;
          if(!lsJson(LS.DRIVE_PAYLOAD,savedPayload)){
            throw new Error("payload-persistence-failed");
          }
        }
        S.cloudIntelligence.trafficSuppressed={
          delayMinutes:this.trafficDelayMinutes(a),
          savingsMinutes:this.trafficSavingsMinutes(a),at:now()
        };
        S.cloudIntelligence.traffic=null;
        S.cloudIntelligence.trafficRouteSignature="";
        S.reroute.busy=false;S.reroute.lastAt=now();S.reroute.state="ROUTE_REPLACED";
        S.reroute.reason="traffic-iq-driver-accepted";
        RouteAuthority.updateProgress(S.location.raw);
        CameraAuthority.forceFollow();
        setSub("Faster route active");
        VoiceAuthority.speak("Faster route selected.","traffic-reroute-accepted");
        Diagnostics.push("TrafficIQ","GUIDED_REROUTE_ACCEPTED",{
          maneuverCount:prepared.maneuvers.length,savingsMinutes:this.trafficSavingsMinutes(a)
        });
        this.removeTrafficPrompt("driver-accepted");
        setSignal("diqSigTraffic","Faster route selected");
        sendCarPlaySnapshot("traffic-driver-accepted");
        window.setTimeout(function(){RerouteAuthority.reset("traffic-replacement-complete");},900);
        return true;
      }catch(e){
        // If a commit step fails, immediately restore the original route and
        // its instructions. Never leave a new line with old turn directions.
        try{
          if(original.geo){
            RouteAuthority.install(original.geo,original.intel,original.dest,"traffic-accept-rollback");
            lsJson(LS.ROUTE_GEO,original.geo);
            if(original.intel){lsJson(LS.ROUTE_INTEL,original.intel);}
            var saved=parse(lsGet(LS.DRIVE_PAYLOAD));
            if(saved&&typeof saved==="object"&&saved.route){
              saved.route=original.geo;
              saved.route_intel=original.intel;
              lsJson(LS.DRIVE_PAYLOAD,saved);
            }
          }
        }catch(recoverErr){
          Diagnostics.error("TrafficIQ","ROLLBACK_FAILED",{message:String(recoverErr&&recoverErr.message||recoverErr)});
        }
        Diagnostics.error("TrafficIQ","ACCEPT_FAILED",{message:String(e&&e.message||e)});
        toast("Route change unavailable; original route kept");
        return false;
      }
    },

    stayOnTrafficRoute:function(){
      var a=S.cloudIntelligence.traffic;
      S.cloudIntelligence.trafficSuppressed={
        delayMinutes:this.trafficDelayMinutes(a),
        savingsMinutes:this.trafficSavingsMinutes(a),
        at:now()
      };
      Diagnostics.push("TrafficIQ","STAY_ON_ROUTE",S.cloudIntelligence.trafficSuppressed);
      this.removeTrafficPrompt("stay-on-route");
      toast("Staying on current route");
      return true;
    },

    ensureTrafficPrompt:function(advisory){
      if(!advisory||!advisory.rerouteSuggested){this.removeTrafficPrompt("not-suggested");return false;}
      if(!this.trafficEtaAudit(advisory).trusted){this.removeTrafficPrompt("traffic-eta-unreconciled");return false;}
      if(!this.trafficRouteFeature(advisory)){this.removeTrafficPrompt("no-alt-geometry");return false;}
      if(!this.shouldResurfaceTrafficPrompt(advisory)){return false;}
      var activePrompt=byId("diqTrafficReroutePrompt");
      if(activePrompt&&S.cloudIntelligence.trafficPrompt&&
         Math.abs(this.trafficSavingsMinutes(advisory)-S.cloudIntelligence.trafficPrompt.savingsMinutes)<2){return true;}
      if(activePrompt){this.removeTrafficPrompt("refresh-savings");}

      var savings=this.trafficSavingsMinutes(advisory);
      if(savings<=0){return false;}

      var el=document.createElement("div");
      el.id="diqTrafficReroutePrompt";
      el.setAttribute("role","dialog");
      el.setAttribute("aria-label","Faster route available");
      el.style.position="fixed";
      el.style.top="calc(250px + var(--safe-top, 0px))";
      el.style.left="calc(14px + var(--safe-left, 0px))";
      el.style.right="calc(72px + var(--safe-right, 0px))";
      el.style.zIndex="96";
      el.style.padding="13px";
      el.style.borderRadius="22px";
      el.style.background="linear-gradient(180deg,rgba(5,12,22,.96),rgba(5,12,22,.88))";
      el.style.border="1px solid rgba(40,255,190,.34)";
      el.style.boxShadow="0 18px 42px rgba(0,0,0,.46),0 0 26px rgba(40,255,190,.12)";
      el.style.backdropFilter="blur(18px)";
      el.style.webkitBackdropFilter="blur(18px)";
      el.innerHTML=
        '<div style="font-size:8px;letter-spacing:1.4px;font-weight:1000;color:#b6ffeb;">TRAFFIC IQ</div>'+
        '<div style="margin-top:5px;font-size:17px;line-height:1.05;font-weight:1000;color:#fff;">Faster route available</div>'+
        '<div style="margin-top:4px;font-size:12px;font-weight:850;color:rgba(255,255,255,.74);">Save about '+savings+' min</div>'+
        '<div style="display:grid;grid-template-columns:1.18fr 1fr;gap:8px;margin-top:11px;">'+
          '<button id="diqTrafficTakeRoute" type="button" style="min-height:42px;border-radius:16px;border:1px solid rgba(182,255,235,.7);background:linear-gradient(135deg,#28ffbe,#00e5ff);color:#061018;font-weight:1000;font-size:10px;letter-spacing:.5px;">'+
          (this.validatedTrafficAlternative(advisory)?'TAKE FASTER ROUTE':'ROUTE INFO')+'</button>'+
          '<button id="diqTrafficStayRoute" type="button" style="min-height:42px;border-radius:16px;border:1px solid rgba(255,255,255,.18);background:rgba(255,255,255,.07);color:#fff;font-weight:1000;font-size:10px;letter-spacing:.5px;">STAY ON ROUTE</button>'+
        '</div>';

      document.body.appendChild(el);
      S.cloudIntelligence.trafficPrompt={shownAt:now(),savingsMinutes:savings};

      var self=this,take=byId("diqTrafficTakeRoute"),stay=byId("diqTrafficStayRoute");
      if(take){take.onclick=function(){
        if(!self.validatedTrafficAlternative(S.cloudIntelligence.traffic)){
          toast("Alternative route preview only; guidance unavailable");
          return false;
        }
        return self.acceptTrafficReroute();
      };}
      if(stay){stay.onclick=function(){return self.stayOnTrafficRoute();};}

      Diagnostics.push("TrafficIQ","PROMPT_SHOWN",{
        savingsMinutes:savings,
        delayMinutes:this.trafficDelayMinutes(advisory)
      });
      return true;
    },

    renderTraffic:function(reason){
      if(FreeTierIQ.free()){this.removeTrafficPrompt("free-tier");return false;}
      var a=S.cloudIntelligence.traffic;
      var expires=a&&a.expiresAt?Date.parse(a.expiresAt):NaN;
      if(a&&isFinite(expires)&&expires<=now()){
        S.cloudIntelligence.traffic=null;
        a=null;
      }
      if(!a){
        setSignal("diqSigTraffic",S.cloudIntelligence.connectivity==="offline"?"Traffic offline":"Traffic watch");
        this.removeTrafficPrompt("no-traffic-advisory");
        return true;
      }

      setSignal("diqSigTraffic",this.trafficLabel(a));
      this.installTrafficDetailTap();
      var trafficText=byId("diqSigTraffic");
      if(trafficText){trafficText.title=this.trafficDetailText(a);}

      var status=clean(a.providerStatus||"").toLowerCase();
      if(status!=="ok"){
        this.removeTrafficPrompt("provider-"+(status||"unknown"));
        return true;
      }

      var audit=this.trafficEtaAudit(a);
      if(!audit.trusted){
        this.removeTrafficPrompt("traffic-estimate-unreconciled");
        Diagnostics.push("TrafficIQ","ETA_AUDIT_HELD",audit);
      }else if(a.rerouteSuggested){
        this.ensureTrafficPrompt(a);
        var delay=this.trafficSafeDelayMinutes(a),savings=this.trafficSavingsMinutes(a);
        if(a.voiceEligible && !BriefingAuthority.imminentManeuver() &&
           (!S.weatherSafety.lastAlertAt||now()-S.weatherSafety.lastAlertAt>20000) &&
           now()-S.cloudIntelligence.lastTrafficVoiceAt>10*60*1000){
          var line="Traffic ahead";
          if(delay!=null&&delay>=5){line+=" is adding about "+delay+" minutes.";}
          else{line+=".";}
          if(savings>0){line+=" An alternative may save about "+savings+" minutes.";}
          VoiceAuthority.speak(line,"traffic-iq");
          S.cloudIntelligence.lastTrafficVoiceAt=now();
        }
      }else{
        this.removeTrafficPrompt("no-reroute-suggestion");
      }

      Diagnostics.push("TrafficIQ","RENDERED",{
        reason:reason||"",
        severity:a.severity||"",
        delayMinutes:this.trafficDelayMinutes(a),
        savingsMinutes:this.trafficSavingsMinutes(a),
        rerouteSuggested:!!a.rerouteSuggested,
        provider:a.provider||"",
        status:a.providerStatus||"",
        etaAudit:audit
      });
      return true;
    },

    receive:function(event){
      if(FreeTierIQ.free()){
        this.removeTrafficPrompt("free-tier");
        /* Severe safety advisories still use WeatherSafetyAuthority separately. */
        return false;
      }
      var detail=event&&event.detail!==undefined?event.detail:event;
      var envelope=this.normalizeEnvelope(detail);
      S.cloudIntelligence.response=envelope;
      S.cloudIntelligence.weather=this.weatherAdvisory(envelope);
      S.cloudIntelligence.traffic=this.trafficAdvisory(envelope);
      S.cloudIntelligence.trafficRouteSignature=S.route.signature;
      Diagnostics.push("TrafficIQ","INTELLIGENCE_EVENT",{
        advisoryCount:Array.isArray(envelope.advisories)?envelope.advisories.length:0,
        hasTraffic:!!S.cloudIntelligence.traffic,
        trafficStatus:S.cloudIntelligence.traffic&&S.cloudIntelligence.traffic.providerStatus||null,
        trafficDelaySeconds:S.cloudIntelligence.traffic&&S.cloudIntelligence.traffic.delaySeconds||null,
        alternativeGuidanceReady:!!(S.cloudIntelligence.traffic&&S.cloudIntelligence.traffic.alternativeRoute&&S.cloudIntelligence.traffic.alternativeRoute.guidanceReady),
        alternativeInstructionCount:S.cloudIntelligence.traffic&&S.cloudIntelligence.traffic.alternativeRoute&&
          S.cloudIntelligence.traffic.alternativeRoute.instructions?S.cloudIntelligence.traffic.alternativeRoute.instructions.length:0
      });
      S.cloudIntelligence.lastAt=now();
      this.renderWeather("intelligence-response");
      this.renderTraffic("intelligence-response");
      return true;
    },

    receiveConnectivity:function(event){
      var detail=event&&event.detail!==undefined?event.detail:event;
      var d=this.normalizeEnvelope(detail);
      var state=clean(d.state||d.connectivityState||d.connectivity||d.status||(typeof detail==="string"?detail:"")).toLowerCase();
      if(!state){state="idle";}
      S.cloudIntelligence.connectivity=state;
      this.renderWeather("connectivity-"+state);
      this.renderTraffic("connectivity-"+state);
      Diagnostics.push("CloudIntelligence","CONNECTIVITY",{state:state});
      return true;
    },

    hasFreshWeather:function(){
      var a=S.cloudIntelligence.weather;
      if(!a||!S.cloudIntelligence.lastAt){return false;}
      var expiry=Date.parse(a.expiresAt||"");
      if(isFinite(expiry)&&expiry<=now()){return false;}
      return now()-S.cloudIntelligence.lastAt<20*60*1000;
    },

    install:function(){
      if(this.installed){return true;}
      this.installed=true;S.cloudIntelligence.installed=true;
      var self=this;
      window.addEventListener("driveiq:intelligence-response",function(e){try{self.receive(e);}catch(err){Diagnostics.error("CloudIntelligence","RESPONSE_ERROR",{message:String(err&&err.message||err)});}},false);
      window.addEventListener("driveiq:intelligence-connectivity",function(e){try{self.receiveConnectivity(e);}catch(err){Diagnostics.error("CloudIntelligence","CONNECTIVITY_ERROR",{message:String(err&&err.message||err)});}},false);
      Diagnostics.push("CloudIntelligence","INSTALLED",{presentationOnly:true});
      return true;
    }
  };

  var WeatherSafetyAuthority = {
    refresh:function(reason){
      if(S.weatherSafety.busy||!S.session.active||!S.session.routeActive||S.arrival.arrived){return false;}
      var plan=routeSamplePlan(),self=this;
      if(!plan.length){return false;}
      S.weatherSafety.busy=true;
      Diagnostics.push("WeatherSafetyAuthority","REFRESH_START",{reason:reason||"",samples:plan.length});

      var forecastJobs=plan.map(function(s){
        return weatherPointForecast(s).catch(function(e){
          Diagnostics.push("WeatherSafetyAuthority","FORECAST_POINT_UNAVAILABLE",{message:String(e&&e.message||e),milesAhead:s.milesAhead});
          return null;
        });
      });
      var alertJobs=plan.map(function(s){
        return nwsAlertsForPoint(s).catch(function(e){
          Diagnostics.push("WeatherSafetyAuthority","NWS_POINT_UNAVAILABLE",{message:String(e&&e.message||e),milesAhead:s.milesAhead});
          return [];
        });
      });

      Promise.all([Promise.all(forecastJobs),Promise.all(alertJobs)]).then(function(results){
        var forecasts=results[0].filter(Boolean),alertGroups=results[1],alerts=[],impacts=[],seen={},i,j,a;
        for(i=0;i<forecasts.length;i++){if(forecasts[i].impact){impacts.push(forecasts[i]);}}
        for(i=0;i<alertGroups.length;i++){
          for(j=0;j<alertGroups[i].length;j++){
            a=alertGroups[i][j];
            if(!a||seen[a.id]){continue;}
            seen[a.id]=1;alerts.push(a);
          }
        }
        alerts.sort(function(a,b){return num(a.sample&&a.sample.milesAhead,9999)-num(b.sample&&b.sample.milesAhead,9999);});
        impacts.sort(function(a,b){
          var sa=num(a.impact&&a.impact.score,0),sb=num(b.impact&&b.impact.score,0);
          if(sb!==sa){return sb-sa;}
          return num(a.sample&&a.sample.milesAhead,9999)-num(b.sample&&b.sample.milesAhead,9999);
        });
        S.weatherSafety.severe=alerts;S.weatherSafety.impacts=impacts;S.weatherSafety.lastAt=now();S.weatherSafety.busy=false;
        S.weatherSafety.currentForecast=forecasts[0]||null;
        AtmosphericRainAuthority.syncCurrentForecast(
          S.weatherSafety.currentForecast,
          "weather-refresh"
        );
        self.handleSevere(alerts);
        if(!alerts.length){self.handleImpact(impacts,forecasts);}
        Diagnostics.push("WeatherSafetyAuthority","REFRESH_READY",{reason:reason||"",severe:alerts.length,impacts:impacts.length});
      }).catch(function(e){
        S.weatherSafety.busy=false;
        Diagnostics.error("WeatherSafetyAuthority","REFRESH_FAILED",{message:String(e&&e.message||e)});
      });
      return true;
    },

    severeKey:function(a){
      return clean((a&&a.id)||((a&&a.event)||"warning")+"|"+Math.round(num(a&&a.sample&&a.sample.milesAhead,0)));
    },

    handleSevere:function(alerts){
      if(!alerts||!alerts.length){return false;}
      var a=alerts[0],key=this.severeKey(a),t=now(),miles=num(a.sample&&a.sample.milesAhead,0);
      if(key===S.weatherSafety.lastSevereKey&&t-S.weatherSafety.lastSevereAt<CFG.weatherSevereRepeatMs){return false;}
      S.weatherSafety.lastSevereKey=key;S.weatherSafety.lastSevereAt=t;S.weatherSafety.lastAlertAt=t;

      var where=miles>1?(" about "+Math.round(miles)+" miles ahead"):" ahead";
      var line="";
      if(a.kind==="tornado"){
        line="Tornado warning"+where+" on your route. Follow official emergency guidance and seek a safe location. Do not continue into the warned area.";
      }else if(a.kind==="flash_flood"){
        line="Flash flood warning"+where+" on your route. Do not enter flooded roads. Follow official emergency guidance.";
      }else if(a.kind==="severe_thunderstorm"){
        line="Severe thunderstorm warning"+where+" on your route. Conditions may be dangerous. Follow official emergency guidance.";
      }else{
        line=a.event+where+" on your route. Follow official emergency guidance and avoid the warned area if possible.";
      }

      WeatherRadarAuthority.setVisible(true,"severe-weather");
      setSignal("diqSigWeather","SEVERE WEATHER");
      setSub(a.event+" • route warning");
      toast(a.event+" ahead");
      VoiceAuthority.speak(line,"weather-safety-severe");
      Diagnostics.error("WeatherSafetyAuthority","LIFE_SAFETY_ALERT",{event:a.event,milesAhead:miles,source:a.source});

      var detail={type:"lifeSafetyWeather",event:a.event,kind:a.kind,milesAhead:miles,headline:a.headline,instruction:a.instruction,source:a.source,routePoint:a.sample};
      try{window.dispatchEvent(new CustomEvent("driveiq:weather-safety",{detail:detail}));}catch(e1){}
      try{if(window.DriveIQNative&&typeof window.DriveIQNative.weatherSafety==="function"){window.DriveIQNative.weatherSafety(detail);}}catch(e2){}
      try{if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQWeatherSafety){window.webkit.messageHandlers.driveIQWeatherSafety.postMessage(detail);}}catch(e3){}
      return true;
    },

    handleImpact:function(impacts,forecasts){
      if(!impacts||!impacts.length){
        if(typeof CloudIntelligencePresentation!=="undefined"&&CloudIntelligencePresentation.hasFreshWeather()){
          CloudIntelligencePresentation.renderWeather("local-weather-clear");
          return false;
        }
        renderNormalRouteWeather(forecasts||[]);
        return false;
      }
      var x=impacts[0],impact=x.impact,miles=num(x.sample&&x.sample.milesAhead,0),t=now();
      var key=clean(impact.type+"|"+Math.round(miles/10)*10);
      WeatherRadarAuthority.setVisible(
        /rain|precip|thunder|snow|wintry/.test(clean(impact.type+" "+impact.label).toLowerCase()),
        "weather-impact"
      );
      setSignal("diqSigWeather",impact.label+" ahead");
      if(key===S.weatherSafety.lastImpactKey&&t-S.weatherSafety.lastImpactAt<CFG.weatherImpactRepeatMs){return false;}
      if(BriefingAuthority.imminentManeuver()){return false;}
      if(impact.level!=="high"&&miles>80){return false;}

      var traffic=BriefingAuthority.trafficSnapshot(),where=miles>1?(" about "+Math.round(miles)+" miles ahead"):" ahead",line="";
      if(impact.type==="thunderstorm"){
        line="Thunderstorms are possible"+where+" on your route.";
      }else if(impact.type==="visibility"){
        line="Low visibility or fog is possible"+where+".";
      }else if(impact.type==="snow"){
        line="Snow or wintry weather is possible"+where+" on your route.";
      }else if(impact.type==="wind"){
        line="Strong winds are possible"+where+" on your route.";
      }else{
        line="Rain may affect driving"+where+".";
      }
      if(traffic.known&&traffic.delayMinutes>=5){
        line+=" Traffic is currently adding about "+traffic.delayMinutes+" minutes.";
      }
      line+=" Drive IQ will keep watching conditions.";

      S.weatherSafety.lastImpactKey=key;S.weatherSafety.lastImpactAt=t;S.weatherSafety.lastAlertAt=t;
      VoiceAuthority.speak(line,"weather-safety-impact");
      Diagnostics.push("WeatherSafetyAuthority","IMPACT_ALERT",{type:impact.type,milesAhead:miles,delayMinutes:traffic.delayMinutes});
      return true;
    }
  };

  /* ==========================================================================
     REROUTE AUTHORITY — STATE MACHINE, GEOMETRY SWAP IN PLACE ONLY
  ========================================================================== */
  var RerouteAuthority = {
    observe:function(raw,projection){
      if(!S.session.routeActive||S.arrival.arrived||S.reroute.busy){return;}
      var mph=num(raw&&raw.speedMph),dist=projection?num(projection.distMiles,Infinity):Infinity,t=now();
      if(mph<3){if(S.reroute.state!=="ON_ROUTE"){this.reset("stationary");}return;}
      if(dist<CFG.offRouteSuspectMiles){if(S.reroute.state!=="ON_ROUTE"){this.reset("rejoined");}return;}
      if(dist>=CFG.offRouteSuspectMiles&&S.reroute.state==="ON_ROUTE"){
        S.reroute.state="SUSPECTED_OFF_ROUTE";S.reroute.suspectAt=t;Diagnostics.push("RerouteAuthority","SUSPECTED",{miles:dist});return;
      }
      if(S.reroute.state==="SUSPECTED_OFF_ROUTE" && dist>=CFG.offRouteConfirmMiles && t-S.reroute.suspectAt>=CFG.offRouteConfirmMs){
        S.reroute.state="CONFIRMED_OFF_ROUTE";Diagnostics.push("RerouteAuthority","CONFIRMED",{miles:dist});this.request("off-route");
      }
    },
    reset:function(reason){S.reroute.state="ON_ROUTE";S.reroute.suspectAt=0;S.reroute.busy=false;S.reroute.reason="";Diagnostics.push("RerouteAuthority","RESET",{reason:reason||""});},
    noteRouteInstalled:function(){if(S.reroute.busy||S.reroute.state!=="ON_ROUTE"){S.reroute.state="ROUTE_REPLACED";S.reroute.busy=false;S.reroute.lastAt=now();Diagnostics.push("RerouteAuthority","ROUTE_REPLACED",{});window.setTimeout(function(){RerouteAuthority.reset("replacement-complete");},1000);}},
    request:function(reason){
      if(S.reroute.busy||now()-S.reroute.lastAt<CFG.rerouteCooldownMs){return false;}
      var start=S.location.raw||S.location.canonical,dest=RouteAuthority.destinationFix();if(!start||!dest){return false;}
      S.reroute.busy=true;S.reroute.state="REROUTING";S.reroute.reason=reason||"reroute";Diagnostics.push("RerouteAuthority","REQUEST",{reason:reason||""});setSub("Finding another way");
      var handled=false;
      try{if(window.DriveIQNative&&typeof window.DriveIQNative.requestReroute==="function"){window.DriveIQNative.requestReroute({origin:{lat:start.lat,lng:start.lng},destination:dest,source:"v200-complete-complete",reason:reason||"off-route"});handled=true;}}catch(e1){}
      try{if(!handled&&window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQReroute){window.webkit.messageHandlers.driveIQReroute.postMessage({origin:{lat:start.lat,lng:start.lng},destination:dest,source:"v200-complete-complete",reason:reason||"off-route"});handled=true;}}catch(e2){}
      if(handled){return true;}
      if(GEOAPIFY_KEY){
        var url="https://api.geoapify.com/v1/routing?waypoints="+encodeURIComponent(start.lat+","+start.lng+"|"+dest.lat+","+dest.lng)+"&mode=drive&format=geojson&details=instruction_details&apiKey="+encodeURIComponent(GEOAPIFY_KEY);
        fetch(url).then(function(r){return r.ok?r.json():Promise.reject(new Error("HTTP "+r.status));}).then(function(data){if(data&&routeCoords(data).length){lsJson(LS.ROUTE_GEO,data);RouteAuthority.install(data,S.route.intel,S.route.destination,"reroute-api");RerouteAuthority.noteRouteInstalled();setSub("Route updated");VoiceAuthority.speak("Route updated.","reroute");}else{throw new Error("empty route");}}).catch(function(e){S.reroute.busy=false;S.reroute.state="CONFIRMED_OFF_ROUTE";Diagnostics.error("RerouteAuthority","FAILED",{message:String(e&&e.message||e)});setSub("Stay on your route while I keep checking");});
        return true;
      }
      S.reroute.busy=false;Diagnostics.error("RerouteAuthority","NO_REROUTE_BRIDGE",{});return false;
    }
  };

  /* ==========================================================================
     V223 ARRIVAL HANDOFF — SCAN / TURN AROUND / RETURN HOME / END DRIVE

     Arrival is a mode transition, not End Drive. We retire active maneuver UI,
     preserve the live GPS/map session, and offer the locked next actions:
       1. Scan Around Me -> Adventure/nearby discovery centered here.
       2. Turn Around -> route back toward the trip start.
       3. Return Home -> route to saved Home, or ask the native shell to set Home.
       4. End Drive -> the only action that tears down the drive session.
  ========================================================================== */
  function hideActiveManeuverCard(){
    var ids=["diqTurnCard","diqManeuverCard","diqTurn","diqNextTurn"],i,el;
    for(i=0;i<ids.length;i++){
      el=byId(ids[i]);
      if(el){try{el.style.display="none";el.setAttribute("aria-hidden","true");}catch(e){}}
    }
  }

  function removeArrivalHandoff(){
    var el=byId("diqV222ArrivalHandoff");
    if(el&&el.parentNode){try{el.parentNode.removeChild(el);}catch(e){}}
  }

  function requestAdventureMode(){
    var f=S.location.canonical||S.location.raw||RouteAuthority.destinationFix();
    var detail={source:"drive-iq-arrival",mode:"adventure",center:f?{lat:f.lat,lng:f.lng}:null,destination:RouteAuthority.destinationFix(),skin:S.preferences.skin};
    Diagnostics.push("ArrivalAuthority","SCAN_AROUND",detail);
    try{if(window.DriveIQNative&&typeof window.DriveIQNative.adventureMode==="function"){window.DriveIQNative.adventureMode(detail);return true;}}catch(e1){}
    try{if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQAdventureMode){window.webkit.messageHandlers.driveIQAdventureMode.postMessage(detail);return true;}}catch(e2){}
    try{window.dispatchEvent(new CustomEvent("driveiq:adventure",{detail:detail}));}catch(e3){}
    /* Web fallback keeps the driver in the stable drive session and opens discovery. */
    openStopSheet();
    return true;
  }

  function readSavedHome(){
    var keys=[LS.HOME,LS.HOME_FALLBACK_1,LS.HOME_FALLBACK_2,LS.HOME_FALLBACK_3],i,raw,obj,lat,lng,label;
    for(i=0;i<keys.length;i++){
      raw=lsGet(keys[i]);
      if(!raw){continue;}
      obj=parse(raw);
      if(!obj){continue;}

      lat=Number(
        obj.lat!=null?obj.lat:
        obj.latitude!=null?obj.latitude:
        obj.coordinate&&obj.coordinate.lat!=null?obj.coordinate.lat:
        obj.location&&obj.location.lat!=null?obj.location.lat:
        NaN
      );
      lng=Number(
        obj.lng!=null?obj.lng:
        obj.lon!=null?obj.lon:
        obj.longitude!=null?obj.longitude:
        obj.coordinate&&obj.coordinate.lng!=null?obj.coordinate.lng:
        obj.location&&obj.location.lng!=null?obj.location.lng:
        NaN
      );

      if(finite(lat)&&finite(lng)){
        label=clean(obj.label||obj.name||obj.title||obj.address||"Home");
        return {lat:lat,lng:lng,label:label||"Home"};
      }
    }
    return null;
  }

  function routeFromArrivalTo(target,reason){
    var f=S.location.raw||S.location.canonical;
    if(!f||!target||!finite(Number(target.lat))||!finite(Number(target.lng))){
      toast("Route unavailable");
      return false;
    }

    var detail={
      source:"drive-iq-arrival",
      origin:{lat:Number(f.lat),lng:Number(f.lng)},
      destination:{lat:Number(target.lat),lng:Number(target.lng),label:clean(target.label||"Destination")},
      reason:reason||"arrival-route",
      skin:S.preferences.skin
    };

    Diagnostics.push("ArrivalAuthority","ROUTE_REQUEST",detail);

    /*
      Native authority first. Existing reroute bridge already understands an
      origin + destination payload and can install fresh route geometry.
    */
    try{
      if(window.DriveIQNative&&typeof window.DriveIQNative.requestReroute==="function"){
        window.DriveIQNative.requestReroute(detail);
        removeArrivalHandoff();
        return true;
      }
    }catch(e1){}

    try{
      if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQReroute){
        window.webkit.messageHandlers.driveIQReroute.postMessage(detail);
        removeArrivalHandoff();
        return true;
      }
    }catch(e2){}

    /*
      Web fallback: ask Geoapify for a fresh legal driving route. This keeps
      Turn Around / Return Home functional even without a native bridge.
    */
    if(GEOAPIFY_KEY){
      var url="https://api.geoapify.com/v1/routing?waypoints="+
        encodeURIComponent(String(f.lat)+","+String(f.lng)+"|"+String(target.lat)+","+String(target.lng))+
        "&mode=drive&format=geojson&details=instruction_details&apiKey="+encodeURIComponent(GEOAPIFY_KEY);

      setSub(reason==="return-home"?"Routing home…":"Turning around…");

      fetch(url).then(function(r){
        if(!r.ok){throw new Error("HTTP "+r.status);}
        return r.json();
      }).then(function(data){
        if(!data||!routeCoords(data).length){throw new Error("empty route");}

        S.arrival.arrived=false;
        S.arrival.announced=false;
        S.arrival.nearSince=0;
        S.arrival.slowSince=0;
        S.arrival.parkedSince=0;
        S.carplay.arrivalSent=false;

        lsJson(LS.ROUTE_GEO,data);
        lsJson(LS.DEST,target);

        RouteAuthority.install(data,null,target,reason||"arrival-route");
        DriveSession.phase("DEPARTING",reason||"arrival-route");
        S.camera.mode="FOLLOW";
        CameraAuthority.forceFollow();
        removeArrivalHandoff();
        HUD.paint();
        sendCarPlaySnapshot(reason||"arrival-route");
        VoiceAuthority.speak(
          reason==="return-home" ? "Routing home." : "Turning around.",
          "arrival-route"
        );
      }).catch(function(e){
        Diagnostics.error("ArrivalAuthority","ROUTE_FAILED",{
          reason:reason||"arrival-route",
          message:String(e&&e.message||e)
        });
        setSub("Choose another option");
        toast("Couldn’t build that route");
      });

      return true;
    }

    toast("Route service unavailable");
    return false;
  }

  function requestTurnAround(){
    var first=S.route.coords&&S.route.coords.length?S.route.coords[0]:null;
    if(!first){toast("Turn around unavailable");return false;}

    var target={lat:Number(first[1]),lng:Number(first[0]),label:"Trip start"};
    var f=S.location.raw||S.location.canonical;
    var detail={
      source:"drive-iq-arrival",
      origin:f?{lat:f.lat,lng:f.lng}:null,
      destination:target,
      turnAround:true,
      returnTrip:true,
      skin:S.preferences.skin
    };

    Diagnostics.push("ArrivalAuthority","TURN_AROUND",detail);

    try{
      if(window.DriveIQNative&&typeof window.DriveIQNative.turnAround==="function"){
        window.DriveIQNative.turnAround(detail);
        removeArrivalHandoff();
        return true;
      }
    }catch(e1){}

    try{
      if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQTurnAround){
        window.webkit.messageHandlers.driveIQTurnAround.postMessage(detail);
        removeArrivalHandoff();
        return true;
      }
    }catch(e2){}

    /* Backward-compatible native bridge. */
    try{
      if(window.DriveIQNative&&typeof window.DriveIQNative.returnTrip==="function"){
        window.DriveIQNative.returnTrip(detail);
        removeArrivalHandoff();
        return true;
      }
    }catch(e3){}

    try{
      if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQReturnTrip){
        window.webkit.messageHandlers.driveIQReturnTrip.postMessage(detail);
        removeArrivalHandoff();
        return true;
      }
    }catch(e4){}

    try{window.dispatchEvent(new CustomEvent("driveiq:turnaround",{detail:detail}));}catch(e5){}
    return routeFromArrivalTo(target,"turn-around");
  }

  function requestReturnHome(){
    var home=readSavedHome();
    var f=S.location.raw||S.location.canonical;
    var detail={
      source:"drive-iq-arrival",
      origin:f?{lat:f.lat,lng:f.lng}:null,
      destination:home,
      returnHome:true,
      promptToSetHome:!home,
      skin:S.preferences.skin
    };

    Diagnostics.push("ArrivalAuthority","RETURN_HOME",detail);

    try{
      if(window.DriveIQNative&&typeof window.DriveIQNative.returnHome==="function"){
        window.DriveIQNative.returnHome(detail);
        if(home){removeArrivalHandoff();}
        return true;
      }
    }catch(e1){}

    try{
      if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQReturnHome){
        window.webkit.messageHandlers.driveIQReturnHome.postMessage(detail);
        if(home){removeArrivalHandoff();}
        return true;
      }
    }catch(e2){}

    try{window.dispatchEvent(new CustomEvent("driveiq:returnhome",{detail:detail}));}catch(e3){}

    if(home){
      return routeFromArrivalTo(home,"return-home");
    }

    toast("Set Home in My Drive IQ first");
    setSub("Home isn’t set yet");
    return false;
  }

  /* Backward-compatible public name. */
  function requestReturnTrip(){
    return requestTurnAround();
  }

  function showArrivalHandoff(dest){
    removeArrivalHandoff();
    var p=drivePalette(),card=document.createElement("div");
    card.id="diqV222ArrivalHandoff";
    card.setAttribute("role","dialog");
    card.setAttribute("aria-label","Arrival options");
    card.style.cssText=
      "position:fixed;left:16px;right:16px;bottom:calc(22px + env(safe-area-inset-bottom,0px));z-index:2147482600;"+
      "border-radius:26px;padding:17px;background:linear-gradient(180deg,rgba(5,18,30,.985),rgba(2,8,16,.985));"+
      "border:1px solid "+p.route+";box-shadow:0 24px 70px rgba(0,0,0,.55),0 0 30px "+p.glowSoft+";color:#fff;"+
      "font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;";
    card.innerHTML=
      '<div style="font-size:11px;font-weight:1000;letter-spacing:1.8px;color:'+p.route+';">ARRIVED</div>'+
      '<div style="margin-top:5px;font-size:22px;font-weight:1000;line-height:1.05;">'+escapeHtml((dest&&dest.label)||"Destination")+'</div>'+
      '<div style="margin-top:6px;font-size:13px;font-weight:750;color:rgba(255,255,255,.68);">What would you like to do next?</div>'+
      '<div style="display:grid;grid-template-columns:1.15fr 1fr;gap:9px;margin-top:14px;">'+
        '<button id="diqV223ScanAround" type="button" style="min-height:54px;border:0;border-radius:17px;background:'+p.route+';color:#061018;font-weight:1000;font-size:13px;">SCAN AROUND ME</button>'+
        '<button id="diqV223TurnAround" type="button" style="min-height:54px;border:1px solid '+p.route+';border-radius:17px;background:rgba(255,255,255,.06);color:#fff;font-weight:1000;font-size:13px;">TURN AROUND</button>'+
      '</div>'+
      '<button id="diqV223ReturnHome" type="button" style="width:100%;min-height:50px;margin-top:9px;border:1px solid '+p.destination+';border-radius:16px;background:rgba(255,255,255,.055);color:#fff;font-weight:1000;font-size:13px;">RETURN HOME</button>'+
      '<button id="diqV223EndDrive" type="button" style="width:100%;min-height:46px;margin-top:9px;border:1px solid rgba(255,255,255,.13);border-radius:15px;background:rgba(255,255,255,.04);color:rgba(255,255,255,.82);font-weight:900;font-size:12px;">END DRIVE</button>';
    try{document.body.appendChild(card);}catch(e){return false;}
    var scan=byId("diqV223ScanAround"),turn=byId("diqV223TurnAround"),home=byId("diqV223ReturnHome"),end=byId("diqV223EndDrive");
    if(scan){scan.onclick=requestAdventureMode;}
    if(turn){turn.onclick=requestTurnAround;}
    if(home){home.onclick=requestReturnHome;}
    if(end){end.onclick=function(){DriveSession.end("arrival-end-drive");};}
    return true;
  }

  /* V238: Destination proximity is never inferred from an ETA alone.
     Only fresh, accurate GPS may commit an arrival; route progress must also
     be near completion. Parked-nearby requires an explicit driver tap. */
  var ArrivalAuthority = {
    gpsReady:function(){
      var raw=S.location.raw;
      return !!(raw && finite(Number(raw.lat)) && finite(Number(raw.lng)) &&
        finite(Number(raw.ts)) && now()-Number(raw.ts)<=CFG.rawFixFreshMs &&
        finite(Number(raw.accuracy)) && Number(raw.accuracy)>0 && Number(raw.accuracy)<=50);
    },
    distance:function(){
      var dest=RouteAuthority.destinationFix(),fix=S.location.raw;
      return dest&&fix?haversineMiles(fix,dest):Infinity;
    },
    complete:function(reason){
      if(S.arrival.arrived||!S.session.routeActive||!this.gpsReady()){return false;}
      var dest=RouteAuthority.destinationFix(),d=this.distance();
      if(!dest||RouteAuthority.progressRatio()<.90){return false;}
      if(reason==="parked-nearby"){
        if(d>.15||num(S.location.raw.speedMph)>CFG.arrivalStopMph||
           !S.arrival.parkedSince||now()-S.arrival.parkedSince<6000){return false;}
      }else if(d>CFG.arrivalNearMiles||!S.arrival.slowSince||now()-S.arrival.slowSince<CFG.arrivalDwellMs){
        return false;
      }
      S.arrival.arrived=true;
      S.carplay.arrivalSent=false;
      DriveSession.phase("ARRIVED",reason||"destination-dwell");
      /* Arrival handoff never ends the GPS/map session. */
      DriveSession.setRouteActive(false);
      S.maneuver.current=null;
      hideActiveManeuverCard();
      setSub("You’ve arrived");
      showArrivalHandoff(dest);
      ArrivalIQPresentation.paint();
      Diagnostics.push("ArrivalAuthority","ARRIVED",{distanceMiles:d,handoff:true,reason:reason||"destination-dwell"});
      VoiceAuthority.speak("You’ve arrived.","arrival");
      sendCarPlaySnapshot("arrival");
      return true;
    },
    confirmParked:function(){
      if(!this.complete("parked-nearby")){
        toast("Stop near your destination to confirm arrival");
        return false;
      }
      return true;
    },
    tick:function(){
      if(S.arrival.arrived||!S.session.routeActive){return;}
      if(!this.gpsReady()){
        S.arrival.nearSince=0;
        S.arrival.slowSince=0;
        S.arrival.parkedSince=0;
        return;
      }
      var d=this.distance(),mph=Math.max(0,num(S.location.raw.speedMph)),t=now(),progress=RouteAuthority.progressRatio();
      if(progress>=.80&&d<=.50&&S.session.phase!=="FINAL_APPROACH"){
        DriveSession.phase(d<=.15?"FINAL_APPROACH":"APPROACHING","destination-proximity");
      }
      if(progress>=.80&&d<=.15){DriveSession.phase("FINAL_APPROACH","destination-proximity");}
      /* A nearby lot is NOT an automatic arrival. Offer an optional parked confirmation. */
      if(progress>=.90&&d<=.15&&mph<=CFG.arrivalStopMph){
        if(!S.arrival.parkedSince){S.arrival.parkedSince=t;}
      }else{S.arrival.parkedSince=0;}
      if(d<=CFG.arrivalNearMiles){
        if(!S.arrival.nearSince){S.arrival.nearSince=t;}
        if(mph<=CFG.arrivalStopMph){
          if(!S.arrival.slowSince){S.arrival.slowSince=t;}
          this.complete("destination-dwell");
        }else{S.arrival.slowSince=0;}
      }else{S.arrival.nearSince=0;S.arrival.slowSince=0;}
    }
  };


  function firstFiniteValue(obj,keys){
    if(!obj){return null;}
    for(var i=0;i<keys.length;i++){
      var v=obj[keys[i]];
      if(v!==null&&v!==undefined&&finite(Number(v))){return Number(v);}
    }
    return null;
  }

  function routeMetadata(){
    var intel=S.route.intel||{},geo=S.route.geo||{},props={};
    try{
      if(geo.features&&geo.features[0]&&geo.features[0].properties){props=geo.features[0].properties||{};}
      else if(geo.properties){props=geo.properties||{};}
    }catch(e){}

    var totalSeconds=firstFiniteValue(intel,["durationSeconds","duration_seconds","time","duration","seconds"]);
    if(totalSeconds==null){totalSeconds=firstFiniteValue(props,["time","duration","durationSeconds","duration_seconds"]);}

    var totalDistanceMiles=firstFiniteValue(intel,["miles","distanceMiles","distance_miles"]);
    if(totalDistanceMiles==null){
      var meters=firstFiniteValue(intel,["distance","distanceMeters","distance_meters"]);
      if(meters==null){meters=firstFiniteValue(props,["distance","distanceMeters","distance_meters"]);}
      if(meters!=null){totalDistanceMiles=meters/1609.344;}
    }
    if(totalDistanceMiles==null||totalDistanceMiles<=0){totalDistanceMiles=S.route.totalMiles;}

    return {totalSeconds:totalSeconds,totalDistanceMiles:totalDistanceMiles};
  }

  function ensureTripHud(){
    var sub=byId("diqRouteIntelSub"),trip=byId("diqV204Trip");
    if(!sub||!sub.parentNode){return null;}
    if(trip){return trip;}

    trip=document.createElement("div");
    trip.id="diqV204Trip";
    trip.style.cssText="display:flex;align-items:center;gap:8px;margin-top:8px;min-width:0;flex-wrap:wrap;color:#fff;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;line-height:1.15;";
    trip.innerHTML=
      '<span style="font-size:10px;font-weight:950;letter-spacing:.12em;color:rgba(255,255,255,.58);">ETA</span>'+
      '<b id="diqV204Arrival" style="font-size:13px;font-weight:1000;">--</b>'+
      '<i style="opacity:.28;">•</i>'+
      '<b id="diqV204RemainTime" style="font-size:13px;font-weight:1000;">--</b>'+
      '<i style="opacity:.28;">•</i>'+
      '<b id="diqV204RemainMiles" style="font-size:13px;font-weight:1000;">--</b>';

    try{sub.parentNode.appendChild(trip);}catch(e){return null;}
    return trip;
  }

  /* ==========================================================================
     V238 ARRIVAL IQ PRESENTATION — READ-ONLY EXCEPT DRIVER CONFIRMATION

     The normal maneuver card, route, GPS, reroute, camera, and silver sedan stay
     untouched. This is a compact optional final-approach card only.
  ========================================================================== */
  var ArrivalIQPresentation = {
    paint:function(){
      if(FreeTierIQ.free()){var c=byId("diqArrivalIQCard");if(c){c.hidden=true;c.style.display="none";}return;}
      var pill=byId("diqSigArrival"),card=byId("diqArrivalIQCard"),
          distance=byId("diqArrivalIQDistance"),name=byId("diqArrivalIQName"),
          hint=byId("diqArrivalIQHint"),button=byId("diqArrivalIQParked");
      if(button&&!button.getAttribute("data-diq-arrival-bound")){
        button.setAttribute("data-diq-arrival-bound","1");
        button.addEventListener("click",function(){ArrivalAuthority.confirmParked();},false);
      }
      if(S.arrival.arrived){
        if(pill){pill.textContent="Arrived";}
        if(card){card.hidden=true;card.style.display="none";}
        return;
      }
      if(!S.session.routeActive||!ArrivalAuthority.gpsReady()||
         RouteAuthority.progressRatio()<.80){
        if(pill){pill.textContent=S.session.routeActive?"Arrival IQ watching":"Arrival IQ ready";}
        if(card){card.hidden=true;card.style.display="none";}
        return;
      }
      var d=ArrivalAuthority.distance(),dest=RouteAuthority.destinationFix();
      if(!isFinite(d)){
        if(pill){pill.textContent="Arrival IQ watching";}
        if(card){card.hidden=true;card.style.display="none";}
        return;
      }
      if(d>.50){
        if(pill){pill.textContent="Arrival • "+fmtDistance(RouteAuthority.remainingMiles());}
        if(card){card.hidden=true;card.style.display="none";}
        return;
      }
      if(pill){pill.textContent=(d<=.15?"Final approach • ":"Approaching • ")+fmtDistance(d);}
      if(!card){return;}
      /* Only show the compact nearby panel during the final .3 mile. */
      if(d>.30){card.hidden=true;card.style.display="none";return;}
      card.hidden=false;
      card.style.display="block";
      if(distance){distance.textContent=fmtDistance(d)+" to destination";}
      if(name){name.textContent=(dest&&dest.label)||"Your destination";}
      var parked=!!(S.arrival.parkedSince&&now()-S.arrival.parkedSince>=6000);
      if(button){
        button.hidden=!(d<=.15&&parked);
        button.style.display=d<=.15&&parked?"block":"none";
      }
      if(hint){
        hint.textContent=d>.15?"Stay on your route. Arrival options appear when you reach your destination.":
          parked?"Parked nearby? Confirm arrival if you have reached your destination.":
          "Near your destination. Stop safely before confirming arrival.";
      }
    }
  };

  /* ==========================================================================
     HUD AUTHORITY — RENDER ONLY
  ========================================================================== */
  var HUD = {
    paint:function(){
      var remain=RouteAuthority.remainingMiles(),meta=routeMetadata(),seconds=null,ratio=RouteAuthority.progressRatio();
      var totalSeconds=meta.totalSeconds,totalMiles=meta.totalDistanceMiles||S.route.totalMiles;

      if(totalSeconds!=null&&totalMiles>0){
        seconds=Math.max(0,Number(totalSeconds)*(remain/Math.max(totalMiles,0.001)));
      }

      setText("diqDistance",fmtDistance(remain));
      setText("diqRemainingDistance",fmtDistance(remain));
      setText("diqMilesRemaining",fmtDistance(remain));

      ensureTripHud();
      setText("diqV204RemainMiles",fmtDistance(remain));

      if(seconds!=null){
        var eta=new Date(now()+seconds*1000);
        var etaText=eta.toLocaleTimeString([], {hour:"numeric",minute:"2-digit"});
        var timeText=fmtTime(seconds);

        setText("diqTime",timeText);
        setText("diqRemainingTime",timeText);
        setText("diqEta",etaText);
        setText("diqArrivalTime",etaText);
        setText("diqV204Arrival",etaText);
        setText("diqV204RemainTime",timeText);
      }else{
        setText("diqV204Arrival","--");
        setText("diqV204RemainTime","--");
      }

      setSignal("diqSigRoute",S.reroute.state==="ON_ROUTE"?"Route IQ active":S.reroute.state.replace(/_/g," "));
      ArrivalIQPresentation.paint();

      /* HUD is render-only; diagnostics remain available in snapshot. */
    }
  };

  /* ==========================================================================
     CARPLAY / NATIVE SNAPSHOT — READ-ONLY EXPORT
  ========================================================================== */
  function carPlayManeuver(){
    var m=S.maneuver.current;
    if(!m){return null;}

    /*
      If the phone-side current maneuver is a generic unnamed "continue",
      let CarPlay surface the next useful turn when one is available soon.
      This is export-only and does not mutate ManeuverAuthority state.
    */
    if(m.type==="continue"&&!m.street){
      var list=S.maneuver.list||[];
      var start=Math.max(0,S.maneuver.activeIndex||0);
      var i,n,delta;
      for(i=start+1;i<list.length;i++){
        n=list[i];
        if(!isUsefulManeuver(n)){continue;}
        delta=Number(n.distanceAlong||0)-Number(S.route.progressMiles||0);
        if(delta<0){continue;}
        if(delta>2.0){break;}
        if(n.type!=="continue" || !!n.street){
          return n;
        }
      }
    }

    return m;
  }

  // V271 COHERENT CARPLAY EXPORT: read-only copy of this active route's truth.
  // The phone keeps all map, camera, reroute and maneuver ownership unchanged.
  function carPlayPayload(source){
    var f=S.location.canonical||S.location.raw;
    var m=carPlayManeuver(),d=RouteAuthority.destinationFix(),p=drivePalette();
    var active=!!S.session.routeActive&&!S.arrival.arrived;
    var mode=S.arrival.arrived?"arrival":(active?"guidance":"freeDrive");
    var remain=active?RouteAuthority.remainingMiles():null;
    var meta=routeMetadata(),total=meta.totalDistanceMiles||S.route.totalMiles;
    var seconds=(active&&meta.totalSeconds!=null&&total>0)?
      Math.max(0,Number(meta.totalSeconds)*(remain/Math.max(total,0.001))):null;
    var eta=seconds!=null?new Date(now()+seconds*1000).toLocaleTimeString([], {hour:"numeric",minute:"2-digit"}):"--";
    var list=active?(S.maneuver.list||[]):[];
    var index=m?list.indexOf(m):-1;
    if(index<0){index=Math.max(0,num(S.maneuver.activeIndex,0));}
    var nextText=m?clean(m.rawText||""):"";
    if(!nextText&&m){nextText=clean(m.label||"Continue")+(m.street?" onto "+clean(m.street):"");}
    var maneuverRows=active?list.slice(0,512).map(function(step){
      var idx=Math.round(num(step.index,-1));
      var c=idx>=0&&idx<S.route.coords.length?S.route.coords[idx]:null;
      var road=clean(step.street||step.road||"");
      var instruction=clean(step.rawText||"") || (clean(step.label||"Continue")+(road?" onto "+road:""));
      var row={
        instruction:instruction,
        distanceText:fmtDistance(Math.max(0,num(step.distanceAlong)-num(S.route.progressMiles))),
        maneuverType:clean(step.type||""),
        action:clean(step.label||""),
        roadName:road
      };
      if(c&&finite(Number(c[0]))&&finite(Number(c[1]))){
        row.longitude=Number(c[0]);row.latitude=Number(c[1]);
      }
      return row;
    }):[];
    var out={
      type:S.arrival.arrived?"arrived":"driving",
      mode:mode,carPlayMode:mode,
      source:"v271-coherent-carplay-"+(source||"snapshot"),
      timestamp:now(),driveId:S.session.id,
      accessTier:FreeTierIQ.tier,phase:S.session.phase,tripPhase:S.session.phase.toLowerCase(),
      routeActive:active,freeDrive:!active&&!S.arrival.arrived,
      lat:f?f.lat:null,lng:f?f.lng:null,
      vehicleLatitude:f?f.lat:null,vehicleLongitude:f?f.lng:null,
      heading:VehicleMarkerAuthority.heading(),
      speedMph:num((S.location.raw||{}).speedMph),
      speedLimitMph:finite(Number(S.route.speedLimitMph))?Number(S.route.speedLimitMph):null,
      progress:active?RouteAuthority.progressRatio():0,
      progressPercent:active?Math.round(RouteAuthority.progressRatio()*100):0,
      remainingMiles:remain,
      distance:active?fmtDistance(remain):"--",eta:active?eta:"--",
      destination:active?d:null,
      destinationName:active?clean((d&&(d.name||d.label||d.address))||((S.route.destination||{}).name)||"Active Drive"):"",
      destinationLatitude:active&&d?d.lat:null,destinationLongitude:active&&d?d.lng:null,
      nextInstruction:active&&m?nextText:"",
      maneuverAction:active&&m?clean(m.label||""):"",
      maneuverType:active&&m?clean(m.type||""):"",
      roadName:active&&m?clean(m.street||m.road||""):"",
      currentStreetName:active&&m?clean(m.street||m.road||""):"",
      distanceToManeuver:active&&m?fmtDistance(Math.max(0,num(m.distanceAlong)-num(S.route.progressMiles))):"--",
      maneuverIndex:index,
      routeManeuvers:maneuverRows,
      maneuver:(active&&m)?{type:m.type,label:m.label,street:m.street,distanceMiles:Math.max(0,num(m.distanceAlong)-num(S.route.progressMiles))}:null,
      rerouteState:active?S.reroute.state:"IDLE",
      arrived:S.arrival.arrived,
      theme:{skin:S.preferences.skin,route:p.route,destination:p.destination,glow:p.glow},
      browse:{enabled:!FreeTierIQ.free(),categories:["fuel","food","coffee","restroom","parking","grocery","hotel","shopping","local favorites"]},
      audioPolicy:{voiceOnlyWhenNeeded:true,stopIqSilent:true,freeDriveSilent:true,ownsMediaTransport:false}
    };
    // Poll extraction asks for source=public only once every five seconds.
    // GPS/heartbeat reports remain compact; they do not resend full geometry.
    if(source==="public"&&active&&S.route.coords.length>1){out.routeCoordinates=S.route.coords;}
    return out;
  }
  function sendCarPlaySnapshot(source){
    source=source||"snapshot";
    /*
      V217 ARRIVAL LATCH:
      Once arrival is committed, send exactly one ARRIVED snapshot and then hold
      that state until End Drive or a new drive. Heartbeat/GPS snapshots after
      arrival are suppressed so native CarPlay cannot fall into a repeated
      "Waiting on navigation" announcement loop.
    */
    if(S.arrival.arrived){
      if(source!=="arrival"){return false;}
      if(S.carplay.arrivalSent){return false;}
    }
    var p=carPlayPayload(source),sent=false;
    try{if(window.DriveIQNative){if(typeof window.DriveIQNative.driving==="function"){window.DriveIQNative.driving(p);sent=true;}else if(typeof window.DriveIQNative.carPlay==="function"){window.DriveIQNative.carPlay(p);sent=true;}if(typeof window.DriveIQNative.carPlaySnapshot==="function"){window.DriveIQNative.carPlaySnapshot(p);sent=true;}}}catch(e1){}
    try{if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQDriving){window.webkit.messageHandlers.driveIQDriving.postMessage(p);sent=true;}}catch(e2){}
    if(sent&&S.arrival.arrived&&source==="arrival"){S.carplay.arrivalSent=true;}
    return sent;
  }
  function sendCarPlayIdle(source){
    var p={type:"endDrive",source:"v200-complete-complete-"+(source||"end"),timestamp:now(),driveId:S.session.id};
    try{if(window.DriveIQNative&&typeof window.DriveIQNative.endDrive==="function"){window.DriveIQNative.endDrive(p);}else if(window.DriveIQNative&&typeof window.DriveIQNative.carPlay==="function"){window.DriveIQNative.carPlay(p);}}catch(e){}
  }

  /* ==========================================================================
     STOP / SCAN UI — BROWSE CHANGES CAMERA ONLY, NEVER VEHICLE STATE
  ========================================================================== */
  function ensureStopSheet(){
    var existing=byId("diqStopSheet");
    if(existing){return existing;}

    /*
      V202: Stop Along the Way must not depend on Shopify HTML already containing
      a sheet. The previous runtime silently did nothing when #diqStopSheet was
      absent. Build one deterministic sheet here, then let the passive Find Stops
      asset enhance the exact same IDs/classes if it is loaded.
    */
    var sheet=document.createElement("div");
    sheet.id="diqStopSheet";
    sheet.className="diqSheet";
    sheet.hidden=true;
    sheet.setAttribute("role","dialog");
    sheet.setAttribute("aria-modal","true");
    sheet.setAttribute("aria-label","Stop Along the Way");

    sheet.innerHTML=
      '<div class="diqV202StopBackdrop" data-diq-stop-close="1"></div>'+
      '<div class="diqV202StopPanel">'+
        '<div class="diqV202StopHead">'+
          '<div><div class="diqV202StopKicker">STOP IQ</div><div class="diqV202StopTitle">What do you need?</div>'+
          '<div class="diqV202StopSub">Browse nearby anytime — with or without an active route.</div></div>'+
          '<button type="button" id="diqStopSheetClose" class="diqV202StopClose" aria-label="Close Stop IQ">×</button>'+
        '</div>'+
        '<div class="diqStopGrid">'+
          '<button type="button" class="diqStopChip" data-stop="fuel">⛽ Fuel</button>'+
          '<button type="button" class="diqStopChip" data-stop="food">🍔 Food</button>'+
          '<button type="button" class="diqStopChip" data-stop="coffee">☕ Coffee</button>'+
          '<button type="button" class="diqStopChip" data-stop="restroom">🚻 Restroom</button>'+
          '<button type="button" class="diqStopChip" data-stop="parking">🅿️ Parking</button>'+
          '<button type="button" class="diqStopChip" data-stop="grocery">🛒 Grocery</button>'+
          '<button type="button" class="diqStopChip" data-stop="hotel">🛏️ Hotel</button>'+
          '<button type="button" class="diqStopChip" data-stop="wellness">🥤 Wellness</button>'+
          '<button type="button" class="diqStopChip" data-stop="shopping">🛍️ Shopping</button>'+
          '<button type="button" class="diqStopChip" data-stop="local favorites">★ Local Favorites</button>'+
        '</div>'+
        '<div id="diqStopResults" class="diqV202StopResults"></div>'+
      '</div>';

    try{document.body.appendChild(sheet);}catch(e1){
      Diagnostics.error("StopIQ","SHEET_CREATE_FAILED",{message:String(e1&&e1.message||e1)});
      return null;
    }

    if(!byId("diqV202StopSheetStyle")){
      var style=document.createElement("style");
      style.id="diqV202StopSheetStyle";
      style.textContent=
        '#diqStopSheet{position:fixed;inset:0;z-index:2147483000;display:none;align-items:flex-end;justify-content:center;padding:16px 16px calc(18px + env(safe-area-inset-bottom,0px));font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;}'+
        '#diqStopSheet.on,#diqStopSheet.show{display:flex!important;}'+
        '#diqStopSheet .diqV202StopBackdrop{position:absolute;inset:0;background:rgba(0,5,10,.54);backdrop-filter:blur(7px);-webkit-backdrop-filter:blur(7px);}'+
        '#diqStopSheet .diqV202StopPanel{position:relative;width:min(680px,100%);max-height:min(72vh,720px);overflow:auto;border-radius:28px;padding:18px;background:linear-gradient(180deg,rgba(5,18,30,.98),rgba(2,8,16,.98));border:1px solid rgba(40,255,190,.28);box-shadow:0 24px 80px rgba(0,0,0,.55),0 0 34px rgba(0,229,255,.14);color:#fff;}'+
        '#diqStopSheet .diqV202StopHead{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:16px;}'+
        '#diqStopSheet .diqV202StopKicker{font-size:11px;font-weight:1000;letter-spacing:2px;color:#54ffbd;}'+
        '#diqStopSheet .diqV202StopTitle{margin-top:5px;font-size:24px;line-height:1.05;font-weight:1000;}'+
        '#diqStopSheet .diqV202StopSub{margin-top:6px;font-size:13px;line-height:1.3;color:rgba(255,255,255,.66);font-weight:700;}'+
        '#diqStopSheet .diqV202StopClose{width:46px;height:46px;flex:0 0 auto;border-radius:50%;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.07);color:#fff;font-size:28px;line-height:1;}'+
        '#diqStopSheet .diqStopGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}'+
        '#diqStopSheet .diqStopChip{min-height:54px;border-radius:18px;border:1px solid rgba(40,255,190,.22);background:rgba(255,255,255,.06);color:#fff;font-size:15px;font-weight:900;}'+
        '#diqStopSheet .diqV202StopResults{margin-top:12px;}';
      try{document.head.appendChild(style);}catch(e2){}
    }

    /* Bind the elements we just created immediately. */
    var close=byId("diqStopSheetClose");
    if(close){close.onclick=closeStopSheet;}
    var backdrop=sheet.querySelector("[data-diq-stop-close]");
    if(backdrop){backdrop.onclick=closeStopSheet;}
    var chips=sheet.querySelectorAll(".diqStopChip"),i;
    for(i=0;i<chips.length;i++){
      chips[i].onclick=function(){stopIntent(this.getAttribute("data-stop")||"fuel");};
    }

    Diagnostics.push("StopIQ","SHEET_CREATED",{source:"v202-runtime"});
    return sheet;
  }

  function openStopSheet(){
    if(FreeTierIQ.free()){toast("Stop IQ is available with Drive IQ Plus");return false;}
    var el=ensureStopSheet();
    if(!el){
      toast("Stop IQ unavailable");
      Diagnostics.error("StopIQ","SHEET_MISSING",{});
      return false;
    }

    el.hidden=false;
    try{el.classList.add("show");el.classList.add("on");}catch(e){}

    /*
      The passive Find Stops asset contains the richer brand/search/district UI.
      Once the common sheet exists, ask it to enhance/open that same sheet.
      This call is UI-only; Find Stops V200 is not allowed to own the route.
    */
    try{
      if(window.DRIVE_IQ_FIND_STOPS &&
         typeof window.DRIVE_IQ_FIND_STOPS.openStopIq==="function"){
        window.DRIVE_IQ_FIND_STOPS.openStopIq();
      }
    }catch(e2){
      Diagnostics.error("StopIQ","FIND_STOPS_UI_BRIDGE_ERROR",{message:String(e2&&e2.message||e2)});
    }

    Diagnostics.push("StopIQ","SHEET_OPENED",{});
    return true;
  }

  function closeStopSheet(){
    var el=byId("diqStopSheet");
    if(!el){return false;}
    try{el.classList.remove("show");el.classList.remove("on");}catch(e){}
    el.hidden=true;
    Diagnostics.push("StopIQ","SHEET_CLOSED",{});
    return true;
  }

  function stopIntent(type){
    closeStopSheet();
    var q=clean(type||"fuel");

    /*
      V206 SCAN MARKER AUTHORITY:
      Every scan category uses the same built-in StopIQ search + GeoJSON
      Mapbox layer renderer. Do NOT delegate marker rendering to the passive
      Find Stops asset; mixed renderers were able to create screen-space /
      DOM-style POIs that visually floated as the pitched camera moved.

      Browse may unlock the camera, but POIs remain geographic map features.
    */
    toast("Scanning for "+q);
    Diagnostics.push("StopIQ","UNIFIED_SCAN",{query:q});

    return StopIQ.search(q);
  }


  /* ==========================================================================
     STOP IQ — ONE SEARCH AUTHORITY, NO CAMERA/ROUTE OWNERSHIP
     Search can browse and render POIs. It cannot mutate route/session lifecycle.
  ========================================================================== */
  function stopCategory(query){
    var q=clean(query).toLowerCase();
    if(/coffee|cafe|espresso/.test(q)){return "catering.cafe";}
    if(/food|restaurant|breakfast|lunch|dinner/.test(q)){return "catering.restaurant";}
    if(/fuel|gas|station/.test(q)){return "service.vehicle.fuel";}
    if(/parking/.test(q)){return "parking";}
    if(/hotel|motel|lodging/.test(q)){return "accommodation";}
    if(/grocery|grocer/.test(q)){return "commercial.supermarket";}
    if(/pharmacy|drugstore/.test(q)){return "healthcare.pharmacy";}
    if(/restroom|toilet|bathroom/.test(q)){return "amenity.toilet";}
    if(/wellness|spa|beauty/.test(q)){return "commercial.health_and_beauty";}
    if(/shopping|shop|store/.test(q)){return "commercial";}
    if(/scenic|attraction|sightseeing/.test(q)){return "tourism.attraction";}
    if(/local favorites|favorite|favourite/.test(q)){return "catering.restaurant";}
    return "commercial";
  }
  function clearStopMarkers(){
    var i;
    for(i=0;i<S.stop.markers.length;i++){
      try{S.stop.markers[i].remove();}catch(e){}
    }
    S.stop.markers=[];
    if(S.map.loaded&&S.map.instance){
      try{
        var src=S.map.instance.getSource("diq-v200-stop-results");
        if(src&&src.setData){src.setData({type:"FeatureCollection",features:[]});}
      }catch(e2){}
    }
  }
  function ensureStopLayer(){
    var m=S.map.instance;
    if(!S.map.loaded||!m){return;}

    try{
      if(!m.getSource("diq-v200-stop-results")){
        m.addSource("diq-v200-stop-results",{type:"geojson",data:{type:"FeatureCollection",features:[]}});
      }

      if(!m.getLayer("diq-v200-stop-results")){
        m.addLayer({
          id:"diq-v200-stop-results",
          slot:ThreeDWorldAuthority.layerSlot("stop")||undefined,
          type:"circle",
          source:"diq-v200-stop-results",
          paint:{
            "circle-radius":["interpolate",["linear"],["zoom"],10,6,14,9,17,12],
            "circle-color":"#ffffff",
            "circle-stroke-color":drivePalette().route,
            "circle-stroke-width":3,
            "circle-opacity":1,
            "circle-pitch-alignment":"map",
            "circle-pitch-scale":"map"
          }
        });
      }

      /*
        V203: show the business/place name on the map. The old built-in StopIQ
        only rendered anonymous circles, so tapping food pins gave the driver
        no idea which restaurant was selected.
      */
      if(!m.getLayer("diq-v203-stop-label")){
        m.addLayer({
          id:"diq-v203-stop-label",
          slot:ThreeDWorldAuthority.layerSlot("stop")||undefined,
          type:"symbol",
          source:"diq-v200-stop-results",
          layout:{
            "text-field":["get","name"],
            "text-size":["interpolate",["linear"],["zoom"],10,10,14,12,17,14],
            "text-offset":[0,1.15],
            "text-anchor":"top",
            "text-max-width":12,
            "text-allow-overlap":false,
            "text-pitch-alignment":"map",
            "text-rotation-alignment":"map"
          },
          paint:{
            "text-color":"#ffffff",
            "text-halo-color":"#061018",
            "text-halo-width":2
          }
        });
      }

      if(!m.getLayer("diq-v203-stop-hit")){
        m.addLayer({
          id:"diq-v203-stop-hit",
          slot:ThreeDWorldAuthority.layerSlot("stop")||undefined,
          type:"circle",
          source:"diq-v200-stop-results",
          paint:{
            "circle-radius":["interpolate",["linear"],["zoom"],10,24,14,30,17,36],
            "circle-color":"rgba(0,0,0,0)",
            "circle-opacity":0.01
          }
        });
      }

      bindStopMapClicks();
    }catch(e){
      Diagnostics.error("StopIQ","LAYER_ERROR",{message:String(e&&e.message||e)});
    }
  }

  function bindStopMapClicks(){
    var m=S.map.instance;
    if(!m||S.stop.clicksBound){return;}

    try{
      m.on("click","diq-v203-stop-hit",function(e){
        var props=e&&e.features&&e.features[0]?e.features[0].properties||{}:{};
        var idx=Number(props.index);
        var place=(idx>=0&&idx<S.stop.results.length)?S.stop.results[idx]:null;

        if(place){
          StopIQ.showPlaceCard(place);
        }
      });

      m.on("mouseenter","diq-v203-stop-hit",function(){
        try{m.getCanvas().style.cursor="pointer";}catch(e1){}
      });

      m.on("mouseleave","diq-v203-stop-hit",function(){
        try{m.getCanvas().style.cursor="";}catch(e2){}
      });

      S.stop.clicksBound=true;
      Diagnostics.push("StopIQ","MAP_CLICKS_BOUND",{});
    }catch(e){
      Diagnostics.error("StopIQ","MAP_CLICK_BIND_FAILED",{message:String(e&&e.message||e)});
    }
  }

  function removeStopPlaceCard(){
    var card=S.stop.card||byId("diqV203StopPlaceCard");
    if(card&&card.parentNode){
      try{card.parentNode.removeChild(card);}catch(e){}
    }
    S.stop.card=null;
    S.stop.selected=null;
  }

  function stopQueryVariants(query){
    var q=clean(query||"").toLowerCase(),out=[],seen={};

    function add(v){
      v=clean(v);
      if(!v||seen[v.toLowerCase()]){return;}
      seen[v.toLowerCase()]=1;
      out.push(v);
    }

    add(q);

    if(/pharmacy|drugstore/.test(q)){
      add("pharmacy");
      add("drugstore");
      add("prescription pharmacy");
    }else if(/fuel|gas|station/.test(q)){
      add("gas station");
      add("fuel");
    }else if(/coffee|cafe|espresso/.test(q)){
      add("coffee");
      add("cafe");
    }else if(/food|restaurant|breakfast|lunch|dinner/.test(q)){
      add("restaurant");
      add("food");
    }else if(/parking/.test(q)){
      add("parking");
      add("parking lot");
    }else if(/grocery|grocer/.test(q)){
      add("grocery");
      add("supermarket");
    }else if(/hotel|motel|lodging/.test(q)){
      add("hotel");
      add("motel");
    }else if(/restroom|toilet|bathroom/.test(q)){
      add("public restroom");
      add("toilet");
    }else if(/wellness|spa|beauty/.test(q)){
      add("wellness");
      add("spa");
    }else if(/shopping|shop|store/.test(q)){
      add("shopping");
      add("store");
    }else if(/scenic|attraction|sightseeing/.test(q)){
      add("attraction");
      add("scenic");
    }

    return out.slice(0,3);
  }

  function stopDistanceMiles(place,fix){
    if(!place||!fix||!finite(Number(place.lat))||!finite(Number(place.lng))){return Infinity;}
    return haversineMiles(
      {lat:Number(fix.lat),lng:Number(fix.lng)},
      {lat:Number(place.lat),lng:Number(place.lng)}
    );
  }

  function stopIdentity(place){
    var n=clean(place&&place.name).toLowerCase();
    var a=clean(place&&place.address).toLowerCase();
    var lat=finite(Number(place&&place.lat))?Number(place.lat).toFixed(4):"";
    var lng=finite(Number(place&&place.lng))?Number(place.lng).toFixed(4):"";
    return n+"|"+a+"|"+lat+"|"+lng;
  }

  function mergeStopCandidates(groups,fix){
    var seen={},out=[],i,j,p,key;

    for(i=0;i<groups.length;i++){
      for(j=0;j<(groups[i]||[]).length;j++){
        p=groups[i][j];
        if(!p||!finite(Number(p.lat))||!finite(Number(p.lng))){continue;}
        p.distanceMiles=stopDistanceMiles(p,fix);

        /* Hard radius guard: never surface stale/far POIs from fallback search. */
        if(p.distanceMiles*1609.344>CFG.stopSearchRadiusMeters){continue;}

        key=stopIdentity(p);
        if(seen[key]){continue;}
        seen[key]=1;
        out.push(p);
      }
    }

    out.sort(function(a,b){
      var ad=finite(Number(a.distanceMiles))?Number(a.distanceMiles):Infinity;
      var bd=finite(Number(b.distanceMiles))?Number(b.distanceMiles):Infinity;
      if(ad!==bd){return ad-bd;}
      return clean(a.name).localeCompare(clean(b.name));
    });

    return out.slice(0,CFG.stopMaxResults);
  }

  function geoapifyStopCategorySearch(query,fix){
    var cat=stopCategory(query);
    var url="https://api.geoapify.com/v2/places?categories="+encodeURIComponent(cat)+
      "&filter=circle:"+encodeURIComponent(String(fix.lng)+","+String(fix.lat)+","+String(CFG.stopSearchRadiusMeters))+
      "&bias=proximity:"+encodeURIComponent(String(fix.lng)+","+String(fix.lat))+
      "&limit="+encodeURIComponent(String(CFG.stopCandidateLimit))+
      "&apiKey="+encodeURIComponent(GEOAPIFY_KEY);

    return fetch(url).then(function(r){
      if(!r.ok){throw new Error("HTTP "+r.status);}
      return r.json();
    }).then(function(data){
      var f=data&&data.features?data.features:[],out=[],i,p;
      for(i=0;i<f.length;i++){
        p=normalizePlace(f[i]);
        if(p.lat!=null&&p.lng!=null){p.discoverySource="category";out.push(p);}
      }
      return out;
    }).catch(function(e){
      Diagnostics.error("StopIQ","CATEGORY_SEARCH_FAILED",{query:query,message:String(e&&e.message||e)});
      return [];
    });
  }

  function geoapifyStopTextSearch(query,fix){
    var variants=stopQueryVariants(query),jobs=[],i;

    for(i=0;i<variants.length;i++){
      (function(term){
        var url="https://api.geoapify.com/v1/geocode/search?text="+encodeURIComponent(term)+
          "&bias=proximity:"+encodeURIComponent(String(fix.lng)+","+String(fix.lat))+
          "&limit="+encodeURIComponent(String(CFG.stopFallbackLimit))+
          "&format=geojson&apiKey="+encodeURIComponent(GEOAPIFY_KEY);

        jobs.push(fetch(url).then(function(r){
          if(!r.ok){throw new Error("HTTP "+r.status);}
          return r.json();
        }).then(function(data){
          var f=data&&data.features?data.features:[],out=[],j,p;
          for(j=0;j<f.length;j++){
            p=normalizePlace(f[j]);
            if(p.lat!=null&&p.lng!=null){p.discoverySource="text:"+term;out.push(p);}
          }
          return out;
        }).catch(function(e){
          Diagnostics.error("StopIQ","TEXT_SEARCH_FAILED",{query:term,message:String(e&&e.message||e)});
          return [];
        }));
      })(variants[i]);
    }

    return Promise.all(jobs).then(function(groups){
      var out=[],g,j;
      for(g=0;g<groups.length;g++){
        for(j=0;j<groups[g].length;j++){out.push(groups[g][j]);}
      }
      return out;
    });
  }

  function normalizePlace(feature){
    var p=feature&&feature.properties?feature.properties:{},g=feature&&feature.geometry?feature.geometry:{},c=g.coordinates||[];
    return {
      name:clean(p.name||p.address_line1||p.formatted||"Nearby stop"),
      address:clean(p.address_line2||p.formatted||""),
      lat:finite(Number(p.lat))?Number(p.lat):(finite(Number(c[1]))?Number(c[1]):null),
      lng:finite(Number(p.lon))?Number(p.lon):(finite(Number(c[0]))?Number(c[0]):null),
      categories:p.categories||[],
      raw:feature
    };
  }
  var StopIQ = {
    search:function(query){
      if(FreeTierIQ.free()){toast("Stop IQ is available with Drive IQ Plus");return false;}
      var q=clean(query||"fuel"),fix=S.location.canonical||S.location.raw;
      if(S.stop.busy){return false;}
      if(!fix){toast("GPS needed to search nearby");return false;}
      if(!GEOAPIFY_KEY){
        Diagnostics.error("StopIQ","NO_GEOAPIFY_KEY",{});
        try{window.dispatchEvent(new CustomEvent("driveiq:stopscan",{detail:{type:q,fix:fix,route:S.route.geo}}));}catch(e){}
        return false;
      }
      removeStopPlaceCard();
      clearStopMarkers();
      S.stop.busy=true;S.stop.query=q;S.stop.results=[];
      setSub("Stop IQ • scanning "+q);
      /* Stop IQ has its own subtitle; Traffic IQ owns the traffic card. */
      Diagnostics.push("StopIQ","SEARCH",{query:q});
      Promise.all([
        geoapifyStopCategorySearch(q,fix),
        geoapifyStopTextSearch(q,fix)
      ]).then(function(groups){
        var out=mergeStopCandidates(groups,fix);
        S.stop.results=out;S.stop.busy=false;
        StopIQ.render();
        /* Keep the live Traffic IQ card undisturbed after stop search. */
        setSub(out.length?"Stop IQ • "+out.length+" nearest":"Stop IQ • no nearby matches");
        /*
          V208 VOICE FIREWALL:
          Scan/status chatter must NEVER enter the navigation voice channel.
          ManeuverAuthority is the only normal driving guidance producer.
          Stop IQ remains visual only so a POI scan cannot steal, suppress,
          cancel, or wedge turn-by-turn speech.
        */
        try{
          var cpBrowse={type:"browseResults",mode:S.session.routeActive?"guidance":"freeDrive",query:q,results:out.map(function(x){return {name:x.name,address:x.address,lat:x.lat,lng:x.lng,distanceMiles:x.distanceMiles};}),theme:{skin:S.preferences.skin,route:drivePalette().route},timestamp:now()};
          if(window.DriveIQNative&&typeof window.DriveIQNative.carPlayBrowse==="function"){window.DriveIQNative.carPlayBrowse(cpBrowse);}
          else if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQCarPlayBrowse){window.webkit.messageHandlers.driveIQCarPlayBrowse.postMessage(cpBrowse);}
        }catch(carPlayBrowseErr){Diagnostics.error("StopIQ","CARPLAY_BROWSE_EXPORT_FAILED",{message:String(carPlayBrowseErr&&carPlayBrowseErr.message||carPlayBrowseErr)});}
        Diagnostics.push("StopIQ","RESULTS",{
          query:q,
          count:out.length,
          nearestMiles:out.length?Math.round(Number(out[0].distanceMiles||0)*10)/10:null,
          sources:out.map(function(x){return x.discoverySource||"";})
        });
      }).catch(function(e){
        S.stop.busy=false;
        setSub("Stop IQ unavailable");
        Diagnostics.error("StopIQ","SEARCH_FAILED",{message:String(e&&e.message||e)});
      });
      return true;
    },
    render:function(shouldFit){
      var m=S.map.instance,out=S.stop.results,i,features=[];
      if(shouldFit==null){shouldFit=(S.camera.mode==="FREE");}
      else{shouldFit=!!shouldFit&&S.camera.mode==="FREE";}
      clearStopMarkers();
      if(!S.map.loaded||!m){return;}
      ensureStopLayer();
      for(i=0;i<out.length;i++){
        features.push({type:"Feature",properties:{name:out[i].name,address:out[i].address,index:i,distanceMiles:out[i].distanceMiles,source:out[i].discoverySource||""},geometry:{type:"Point",coordinates:[out[i].lng,out[i].lat]}});
      }
      try{
        var src=m.getSource("diq-v200-stop-results");
        if(src&&src.setData){src.setData({type:"FeatureCollection",features:features});}
      }catch(e){}
      if(out.length&&shouldFit){
        try{
          var b=new mapboxgl.LngLatBounds();
          var f=S.location.canonical||S.location.raw;
          if(f){b.extend([f.lng,f.lat]);}
          for(i=0;i<out.length;i++){b.extend([out[i].lng,out[i].lat]);}
          m.fitBounds(b,{padding:{top:100,right:55,bottom:230,left:55},duration:450,maxZoom:14.7});
        }catch(e2){}
      }
      this.renderList();
    },
    renderList:function(){
      var panel=byId("diqStopResults")||byId("diqNeedResults")||byId("diqMiniResult");
      if(!panel){return;}
      var html="",i,r;
      for(i=0;i<S.stop.results.length;i++){
        r=S.stop.results[i];
        html+='<button type="button" class="diqV200StopResult" data-diq-stop-index="'+String(i)+'" style="display:block;width:100%;text-align:left;padding:12px 14px;margin:8px 0;border-radius:14px;border:1px solid rgba(0,245,255,.25);background:rgba(5,15,24,.92);color:white;">'+
          '<strong>'+escapeHtml(r.name)+'</strong><br><span style="opacity:.72;font-size:12px;">'+
          escapeHtml((finite(Number(r.distanceMiles))?(Math.round(Number(r.distanceMiles)*10)/10)+" mi • ":"")+(r.address||""))+
          '</span></button>';
      }
      panel.innerHTML=html||"No nearby matches.";
      var buttons=panel.querySelectorAll("[data-diq-stop-index]");
      for(i=0;i<buttons.length;i++){
        buttons[i].onclick=function(){
          var idx=Number(this.getAttribute("data-diq-stop-index")),place=S.stop.results[idx];
          if(place){StopIQ.showPlaceCard(place);}
        };
      }
    },
    showPlaceCard:function(place){
      if(!place){return false;}

      removeStopPlaceCard();
      S.stop.selected=place;

      var card=document.createElement("div");
      card.id="diqV203StopPlaceCard";
      card.style.cssText=
        "position:fixed;left:18px;right:18px;bottom:calc(154px + env(safe-area-inset-bottom,0px));"+
        "z-index:2147482500;border-radius:24px;padding:15px 16px;"+
        "background:linear-gradient(180deg,rgba(5,18,30,.98),rgba(2,8,16,.98));"+
        "border:1px solid rgba(40,255,190,.32);box-shadow:0 22px 58px rgba(0,0,0,.48),0 0 30px rgba(0,229,255,.14);"+
        "color:#fff;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;";

      card.innerHTML=
        '<div style="display:flex;align-items:flex-start;gap:12px;">'+
          '<div style="width:46px;height:46px;flex:0 0 auto;border-radius:16px;display:flex;align-items:center;justify-content:center;background:rgba(40,255,190,.12);border:1px solid rgba(40,255,190,.22);font-size:22px;">📍</div>'+
          '<div style="min-width:0;flex:1;">'+
            '<div style="font-size:18px;line-height:1.1;font-weight:1000;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">'+escapeHtml(place.name||"Stop")+'</div>'+
            '<div style="margin-top:5px;font-size:12px;line-height:1.3;font-weight:750;color:rgba(255,255,255,.66);">'+escapeHtml(place.address||"Nearby stop")+'</div>'+
          '</div>'+
        '</div>'+
        '<div style="display:grid;grid-template-columns:1.35fr .65fr;gap:9px;margin-top:13px;">'+
          '<button type="button" id="diqV203AddStop" style="min-height:48px;border:0;border-radius:16px;background:linear-gradient(135deg,#28ffbe,#00e5ff);color:#061018;font-size:13px;font-weight:1000;">ADD TO ROUTE</button>'+
          '<button type="button" id="diqV203CloseStop" style="min-height:48px;border:1px solid rgba(255,255,255,.14);border-radius:16px;background:rgba(255,255,255,.06);color:#fff;font-size:13px;font-weight:1000;">CLOSE</button>'+
        '</div>';

      try{document.body.appendChild(card);}catch(e1){return false;}
      S.stop.card=card;

      var add=byId("diqV203AddStop");
      if(add){add.onclick=function(){StopIQ.select(place);removeStopPlaceCard();};}

      var close=byId("diqV203CloseStop");
      if(close){close.onclick=removeStopPlaceCard;}

      setSub((place.name||"Stop")+" • selected");
      Diagnostics.push("StopIQ","PLACE_CARD",{name:place.name,address:place.address||""});
      return true;
    },

    select:function(place){
      if(!place){return false;}
      Diagnostics.push("StopIQ","SELECT",{name:place.name});
      return AddStopAuthority.request(place,"stop-iq");
    }
  };
  function escapeHtml(v){
    return String(v==null?"":v).replace(/[&<>"']/g,function(ch){
      return {"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[ch];
    });
  }


  /* ==========================================================================
     V241 ADD STOP ROUTE AUTHORITY — ONE REAL ROUTE MUTATION OWNER

     Explicit driver confirmation is required upstream. Once confirmed:
     GPS -> selected stop -> ORIGINAL final destination is rebuilt as one route.
     RouteAuthority installs the geometry in place; no page reload/map rebuild.
     ========================================================================== */
  // V15.4B — pilot request status is presentation/acknowledgement only.
  // Only AddStopAuthority installs/routes; this stores its verified outcome.
  var NativeStopPilot = {token:"", state:"idle", message:"", name:"", at:0};
  function nativeStopPilotFinish(source, state, message){
    var prefix="native-stop-v154b:";
    if(typeof source!=="string" || source.indexOf(prefix)!==0){return;}
    var token=source.slice(prefix.length);
    if(token && NativeStopPilot.token===token){
      NativeStopPilot.state=state;
      NativeStopPilot.message=String(message||"").slice(0,180);
      NativeStopPilot.at=Date.now();
    }
  }

  var AddStopAuthority = {
    busy:false,
    lastKey:"",
    lastAt:0,
    listenersInstalled:false,

    // ASK IQ owns the UI. This route authority reports only verified outcomes.
    notifyAskRouteOutcome:function(ok,askMeta){
      if(!askMeta || !askMeta.handoffId){return;}
      try{
        var ask=window.DRIVE_IQ_ASK;
        if(ask && typeof ask.resolveRouteHandoff==="function"){
          ask.resolveRouteHandoff(!!ok,{handoffId:askMeta.handoffId});
        }
      }catch(e){
        Diagnostics.error("AddStopAuthority","ASK_ACK_FAILED",{message:String(e&&e.message||e)});
      }
    },

    normalize:function(place){
      if(!place){return null;}
      var lat=Number(
        place.lat!=null?place.lat:
        (place.latitude!=null?place.latitude:
        (place.location&&place.location.latitude))
      );
      var lng=Number(
        place.lng!=null?place.lng:
        (place.longitude!=null?place.longitude:
        (place.location&&place.location.longitude))
      );
      if(!finite(lat)||!finite(lng)){return null;}
      return {
        name:clean(place.name||place.label||place.displayName||"Stop"),
        address:clean(place.address||place.formattedAddress||place.formatted||""),
        lat:lat,
        lng:lng,
        placeId:clean(place.googlePlaceId||place.placeId||place.id||""),
        source:clean(place.source||place.sourceProvider||"ask-iq")
      };
    },

    key:function(place){
      return Number(place.lat).toFixed(5)+"|"+Number(place.lng).toFixed(5);
    },

    clearDiscovery:function(){
      try{clearStopMarkers();}catch(e0){}
      try{removeStopPlaceCard();}catch(e1){}
      try{closeStopSheet();}catch(e2){}

      /* Passive Find Stops asset uses these DOM markers. Remove only POI markers,
         never the persistent vehicle marker. */
      try{
        var nodes=document.querySelectorAll(".diqDomStopMarker"),i;
        for(i=0;i<nodes.length;i++){
          if(nodes[i]&&nodes[i].parentNode){
            nodes[i].parentNode.removeChild(nodes[i]);
          }
        }
      }catch(e3){}

      try{
        var mini=byId("diqStopMiniCard");
        if(mini&&mini.parentNode){mini.parentNode.removeChild(mini);}
      }catch(e4){}

      try{
        var m=S.map.instance,src;
        if(m){
          src=m.getSource("diq-stops");
          if(src&&src.setData){src.setData({type:"FeatureCollection",features:[]});}
          src=m.getSource("diq-v200-stop-results");
          if(src&&src.setData){src.setData({type:"FeatureCollection",features:[]});}
        }
      }catch(e5){}

      try{
        window.dispatchEvent(new CustomEvent("driveiq:stop-route-installed",{
          detail:{source:"drive-mode-add-stop-authority"}
        }));
      }catch(e6){}
    },

    nextIntel:function(feature,stop){
      var old=S.route.intel||{};
      var props=feature&&feature.properties?feature.properties:{};
      var out=Object.assign({},old);
      var seconds=Number(
        props.time!=null?props.time:
        (props.duration!=null?props.duration:props.durationSeconds)
      );
      var meters=Number(
        props.distance!=null?props.distance:
        (props.distanceMeters!=null?props.distanceMeters:props.distance_meters)
      );

      if(finite(seconds)&&seconds>0){
        out.durationSeconds=seconds;
        out.duration=seconds;
        out.time=seconds;
      }
      if(finite(meters)&&meters>0){
        out.distanceMeters=meters;
        out.distance=meters;
        out.miles=meters/1609.344;
        out.distanceMiles=meters/1609.344;
      }

      out.trafficDelaySeconds=0;
      out.source="add-stop-route-authority";
      out.addedStop={
        name:stop.name,
        address:stop.address,
        lat:stop.lat,
        lng:stop.lng,
        placeId:stop.placeId||""
      };
      return out;
    },

    persist:function(feature,intel,dest,stop){
      if(!lsJson(LS.ROUTE_GEO,feature)){throw new Error("route-persistence-failed");}
      if(!lsJson(LS.ROUTE_INTEL,intel)){throw new Error("route-intel-persistence-failed");}
      try{lsJson("diq_added_stop_v1",stop);}catch(e0){}

      var payload=parse(lsGet(LS.DRIVE_PAYLOAD));
      if(payload&&typeof payload==="object"){
        payload.route=feature;
        payload.route_intel=intel;
        payload.destination=dest;
        payload.added_stop=stop;
        if(!lsJson(LS.DRIVE_PAYLOAD,payload)){
          throw new Error("drive-payload-persistence-failed");
        }
      }
      return true;
    },

    verify:function(feature,stop){
      var coords=routeCoords(feature);
      if(coords.length<2){return false;}
      var p=nearestProjection({lat:stop.lat,lng:stop.lng},coords,null);
      return !!(p&&finite(Number(p.distMiles))&&Number(p.distMiles)<=0.18);
    },

    request:function(rawPlace,source,askMeta){
      var self=this;
      var stop=this.normalize(rawPlace);
      var fix=S.location.raw||S.location.canonical;
      var dest=RouteAuthority.destinationFix();
      var key;

      if(!stop){
        toast("Stop location unavailable");
        Diagnostics.error("AddStopAuthority","INVALID_STOP",{source:source||""});
        return false;
      }
      if(!fix||!finite(Number(fix.lat))||!finite(Number(fix.lng))){
        toast("GPS is not ready yet");
        Diagnostics.error("AddStopAuthority","NO_GPS",{stop:stop.name});
        return false;
      }
      if(!dest||!finite(Number(dest.lat))||!finite(Number(dest.lng))){
        toast("Final destination unavailable");
        Diagnostics.error("AddStopAuthority","NO_FINAL_DESTINATION",{stop:stop.name});
        return false;
      }
      if(!GEOAPIFY_KEY){
        toast("Route update unavailable");
        Diagnostics.error("AddStopAuthority","NO_ROUTING_KEY",{stop:stop.name});
        return false;
      }

      key=this.key(stop);
      if(this.busy){return false;}
      if(key===this.lastKey&&now()-this.lastAt<6000){
        Diagnostics.push("AddStopAuthority","DUPLICATE_IGNORED",{stop:stop.name});
        return true;
      }

      this.busy=true;
      this.lastKey=key;
      this.lastAt=now();

      setSub("Adding "+stop.name+" to your route");
      setSignal("diqSigRoute","Adding stop");
      toast("Updating route…");

      Diagnostics.push("AddStopAuthority","REQUEST",{
        stop:stop.name,
        source:source||"",
        finalDestination:dest.label||"Destination"
      });

      var waypoints=
        String(fix.lat)+","+String(fix.lng)+"|"+
        String(stop.lat)+","+String(stop.lng)+"|"+
        String(dest.lat)+","+String(dest.lng);

      var url=
        "https://api.geoapify.com/v1/routing?waypoints="+
        encodeURIComponent(waypoints)+
        "&mode=drive&format=geojson&details=instruction_details&apiKey="+
        encodeURIComponent(GEOAPIFY_KEY);

      fetch(url,{
        method:"GET",
        headers:{Accept:"application/json"},
        cache:"no-store"
      })
      .then(function(r){
        if(!r.ok){throw new Error("HTTP "+r.status);}
        return r.json();
      })
      .then(function(data){
        var feature=
          data&&Array.isArray(data.features)&&data.features.length
            ? data.features[0]
            : data;

        if(!feature||routeCoords(feature).length<2){
          throw new Error("empty-waypoint-route");
        }
        if(!self.verify(feature,stop)){
          throw new Error("route-does-not-pass-through-stop");
        }

        var intel=self.nextIntel(feature,stop);
        var originalDest=S.route.destination||parse(lsGet(LS.DEST))||dest;

        /* Keep original final destination authoritative. */
        if(!RouteAuthority.install(
          feature,
          intel,
          originalDest,
          "explicit-add-stop"
        )){
          throw new Error("route-install-failed");
        }

        self.persist(feature,intel,originalDest,stop);

        /* A new route is a new progress calculation, not arrival. */
        S.arrival.nearSince=0;
        S.arrival.slowSince=0;
        S.arrival.parkedSince=0;
        S.arrival.arrived=false;
        S.arrival.announced=false;
        S.carplay.arrivalSent=false;

        RerouteAuthority.reset("stop-route-installed");
        RouteAuthority.updateProgress(S.location.raw||S.location.canonical);
        self.clearDiscovery();
        CameraAuthority.forceFollow();
        HUD.paint();
        sendCarPlaySnapshot("stop-route-installed");

        setSub("Stop added • "+stop.name);
        setSignal("diqSigRoute","Route IQ active");
        toast(stop.name+" added ✓");

        nativeStopPilotFinish(source,"installed",stop.name+" is on the route.");
        Diagnostics.push("AddStopAuthority","INSTALLED",{
          stop:stop.name,
          routePoints:S.route.coords.length,
          routeMiles:S.route.totalMiles,
          finalDestination:RouteAuthority.destinationFix()
        });

        try{
          window.dispatchEvent(new CustomEvent("driveiq:route-stop-added",{
            detail:{
              stop:stop,
              destination:RouteAuthority.destinationFix(),
              route:S.route.geo,
              source:"drive-mode-add-stop-authority"
            }
          }));
        }catch(e7){}

        // V15.5C: Verify that the installed route REMAINS the active route and
        // was persisted after the storage poll. A successful fetch alone is not
        // enough to tell Ask IQ that a stop was added.
        self.busy=false;
        if(askMeta&&askMeta.handoffId){
          window.setTimeout(function(){
            var liveStop=S.route.intel&&S.route.intel.addedStop;
            var disk=parse(lsGet(LS.ROUTE_GEO));
            var diskIntel=parse(lsGet(LS.ROUTE_INTEL));
            var sameStop=!!(liveStop&&diskIntel&&diskIntel.addedStop&&
              Math.abs(Number(liveStop.lat)-stop.lat)<0.00001&&
              Math.abs(Number(liveStop.lng)-stop.lng)<0.00001&&
              Math.abs(Number(diskIntel.addedStop.lat)-stop.lat)<0.00001&&
              Math.abs(Number(diskIntel.addedStop.lng)-stop.lng)<0.00001);
            var valid=!!(S.session.routeActive&&sameStop&&
              self.verify(S.route.geo,stop)&&self.verify(disk,stop)&&
              geometrySignature(disk)===S.route.signature);
            Diagnostics.push("AddStopAuthority",valid?"ASK_POST_INSTALL_VERIFIED":"ASK_POST_INSTALL_NOT_VERIFIED",{
              stop:stop.name,routePoints:S.route.coords.length,
              liveSignature:S.route.signature,storedSignature:geometrySignature(disk)
            });
            self.notifyAskRouteOutcome(valid,askMeta);
          },1600);
        }
      })
      .catch(function(err){
        self.busy=false;
        setSub("Could not add stop • current route unchanged");
        setSignal("diqSigRoute","Route IQ active");
        toast("Could not add stop");
        nativeStopPilotFinish(source,"failed","Route update failed. Existing route was retained.");
        Diagnostics.error("AddStopAuthority","FAILED",{
          stop:stop.name,
          source:source||"",
          message:String(err&&err.message||err)
        });
        self.notifyAskRouteOutcome(false,askMeta);
      });

      return true;
    },

    onAskConfirmed:function(event){
      var d=event&&event.detail?event.detail:{};
      var place=d.place||d.stop||null;
      if(!place){return;}

      /* Drive Mode is the only route owner. Prevent the passive Find Stops
         listener from also firing a native/no-op add-stop path. */
      try{if(event.stopImmediatePropagation){event.stopImmediatePropagation();}}catch(e0){}
      // Carry ASK's unique handoff token through the asynchronous route install.
      // An unrelated Stop IQ route update must never close ASK IQ.
      var accepted=this.request(place,"ask-iq-confirmed",d.meta||null);
      if(!accepted){this.notifyAskRouteOutcome(false,d.meta||null);}
    },

    onAddStopEvent:function(event){
      var d=event&&event.detail?event.detail:null;
      if(!d){return;}
      try{if(event.stopImmediatePropagation){event.stopImmediatePropagation();}}catch(e0){}
      this.request(d,"driveiq:addstop");
    },

    installListeners:function(){
      if(this.listenersInstalled){return true;}
      this.listenersInstalled=true;

      /* Capture=true intentionally gives Drive Mode route authority first shot. */
      window.addEventListener(
        "driveiq:ask-route-confirmed",
        this.onAskConfirmed.bind(this),
        true
      );
      window.addEventListener(
        "driveiq:addstop",
        this.onAddStopEvent.bind(this),
        true
      );

      Diagnostics.push("AddStopAuthority","LISTENERS_INSTALLED",{});
      return true;
    }
  };

  /* Install immediately so later deferred ASK/Find Stops assets cannot outrank
     the Drive Mode route owner in the event order. */
  AddStopAuthority.installListeners();

  /* ==========================================================================
     DRIVER SIGNAL / INCIDENT IQ — ONE INCIDENT READER
  ========================================================================== */
  function incidentArray(key){
    var x=parse(lsGet(key));
    return Array.isArray(x)?x:[];
  }
  function incidentAlive(e){
    if(!e){return false;}
    var exp=e.expires_at||e.expiresAt||e.expiration;
    if(!exp){return true;}
    var t=Date.parse(exp);
    return !finite(t)||t>now();
  }
  function incidentPoint(e){
    var lat=Number(e&&e.lat),lng=Number(e&&e.lng);
    return finite(lat)&&finite(lng)?{lat:lat,lng:lng}:null;
  }
  function incidentType(e){
    return clean((e&&e.type)||"").toLowerCase().replace(/\s+/g,"_");
  }
  function isClosure(e){
    var t=incidentType(e);
    return /road_closed|closure|closed|exit_blocked|blocked_road/.test(t);
  }
  function routeDistanceToPoint(point){
    if(!point||S.route.coords.length<2){return Infinity;}
    var p=nearestProjection(point,S.route.coords,null);
    return p?p.distMiles:Infinity;
  }
  function avoidanceParam(closures){
    var out=[],i,p;
    for(i=0;i<closures.length;i++){
      p=incidentPoint(closures[i]);
      if(p){out.push("location:"+String(p.lat)+","+String(p.lng));}
    }
    return out.join("|");
  }
  var IncidentIQ = {
    refresh:function(reason){
      var all=incidentArray(LS.CONSTRUCTION_EVENTS).concat(incidentArray(LS.USER_ROAD_REPORTS));
      var active=[],closures=[],i,e,p,d,conf,radius;
      for(i=0;i<all.length;i++){
        e=all[i];if(!incidentAlive(e)){continue;}
        p=incidentPoint(e);if(!p){continue;}
        d=routeDistanceToPoint(p);
        radius=num(e.radius_miles,0.75);
        if(d<=radius){
          active.push(e);
          conf=num(e.confidence,100);
          if(isClosure(e)&&conf>=CFG.closureMinConfidence){closures.push(e);}
        }
      }
      S.incidents.events=active;S.incidents.closures=closures;
      /* V234: real cloud traffic owns this card when fresh. Incident IQ still
         detects closures, but may not overwrite a verified live advisory. */
      var live=S.cloudIntelligence.traffic;
      var liveFresh=!!(live&&S.cloudIntelligence.lastAt&&
        now()-S.cloudIntelligence.lastAt<20*60*1000&&
        (!live.expiresAt||!isFinite(Date.parse(live.expiresAt))||Date.parse(live.expiresAt)>now()));
      if(liveFresh){
        CloudIntelligencePresentation.renderTraffic("incident-poll");
      }else{
        setSignal("diqSigTraffic",closures.length?"Closure ahead":(active.length?"Road issue watch":"Traffic watch"));
      }
      Diagnostics.push("IncidentIQ","REFRESH",{reason:reason||"",active:active.length,closures:closures.length});
      if(closures.length){this.defendClosure(closures);}
      return active;
    },
    defendClosure:function(closures){
      var key=[],i,p,t=now();
      for(i=0;i<closures.length;i++){
        p=incidentPoint(closures[i]);if(p){key.push(p.lat.toFixed(5)+","+p.lng.toFixed(5));}
      }
      key=key.join("|");
      if(!key){return false;}
      if(key===S.incidents.lastClosureKey&&t-S.incidents.lastClosureAt<CFG.closureRetryMs){return false;}
      S.incidents.lastClosureKey=key;S.incidents.lastClosureAt=t;S.reroute.closureKey=key;
      setSub("Road closed ahead • finding another way");
      VoiceAuthority.speak("Road closed ahead. Finding another way.","closure-defense");
      Diagnostics.push("IncidentIQ","CLOSURE_DEFENSE",{count:closures.length,key:key});
      return RerouteAuthority.request("road-closure",closures);
    },
    report:function(type,note){
      var f=S.location.raw||S.location.canonical;
      if(!f){toast("GPS needed to report");return null;}
      var reports=incidentArray(LS.USER_ROAD_REPORTS);
      var ttl=/road_closed|exit_blocked/.test(type)?96:72;
      var report={
        id:"diq_report_"+String(now()),type:clean(type||"road_issue"),
        note:clean(note||""),lat:f.lat,lng:f.lng,confidence:100,
        radius_miles:/road_closed|exit_blocked/.test(type)?1.0:0.55,
        created_at:new Date().toISOString(),
        expires_at:new Date(now()+ttl*3600000).toISOString(),
        source:"driver_signal_iq"
      };
      reports.push(report);lsJson(LS.USER_ROAD_REPORTS,reports.slice(-200));
      Diagnostics.push("IncidentIQ","USER_REPORT",{type:report.type});
      try{
        if(window.DriveIQNative&&typeof window.DriveIQNative.roadReport==="function"){window.DriveIQNative.roadReport(report);}
      }catch(e){}
      this.refresh("user-report");
      toast("Road report saved");
      return report;
    }
  };

  /* ==========================================================================
     CLEAN REROUTE EXTENSION — OPTIONAL CLOSURE AVOIDANCE
  ========================================================================== */
  RerouteAuthority.request = function(reason,closures){
    if(S.reroute.busy||now()-S.reroute.lastAt<CFG.rerouteCooldownMs){return false;}
    if(S.arrival.arrived){return false;}
    var start=S.location.raw||S.location.canonical,dest=RouteAuthority.destinationFix();
    if(!start||!dest){return false;}
    S.reroute.busy=true;S.reroute.state="REROUTING";S.reroute.reason=reason||"reroute";
    Diagnostics.push("RerouteAuthority","REQUEST",{reason:reason||"",closureCount:closures?closures.length:0});
    setSub(reason==="road-closure"?"Road closed ahead • finding another way":"Finding another way");
    var payload={origin:{lat:start.lat,lng:start.lng},destination:dest,source:"v200-complete",reason:reason||"off-route"};
    if(closures&&closures.length){payload.avoid=closures.map(function(e){var p=incidentPoint(e);return p?{lat:p.lat,lng:p.lng}:null;}).filter(Boolean);}
    var handled=false;
    try{if(window.DriveIQNative&&typeof window.DriveIQNative.requestReroute==="function"){window.DriveIQNative.requestReroute(payload);handled=true;}}catch(e1){}
    try{if(!handled&&window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQReroute){window.webkit.messageHandlers.driveIQReroute.postMessage(payload);handled=true;}}catch(e2){}
    if(handled){return true;}
    if(!GEOAPIFY_KEY){S.reroute.busy=false;Diagnostics.error("RerouteAuthority","NO_REROUTE_PROVIDER",{});return false;}
    var url="https://api.geoapify.com/v1/routing?waypoints="+encodeURIComponent(start.lat+","+start.lng+"|"+dest.lat+","+dest.lng)+
      "&mode=drive&format=geojson&details=instruction_details";
    if(closures&&closures.length){
      var avoid=avoidanceParam(closures);
      if(avoid){url+="&avoid="+encodeURIComponent(avoid);}
    }
    url+="&apiKey="+encodeURIComponent(GEOAPIFY_KEY);
    fetch(url).then(function(r){
      if(!r.ok){throw new Error("HTTP "+r.status);}
      return r.json();
    }).then(function(data){
      if(!data||!routeCoords(data).length){throw new Error("empty route");}
      /* Verify a closure-avoid reroute does not still pass directly through the blocked points. */
      if(closures&&closures.length){
        var newCoords=routeCoords(data),bad=false,i,pj,pp;
        for(i=0;i<closures.length;i++){
          pp=incidentPoint(closures[i]);if(!pp){continue;}
          pj=nearestProjection(pp,newCoords,null);
          if(pj&&pj.distMiles<=0.06){bad=true;break;}
        }
        if(bad){
          throw new Error("replacement still intersects closure");
        }
      }
      lsJson(LS.ROUTE_GEO,data);
      RouteAuthority.install(data,S.route.intel,S.route.destination,reason==="road-closure"?"closure-reroute":"reroute-api");
      S.reroute.busy=false;S.reroute.lastAt=now();S.reroute.state="ROUTE_REPLACED";
      CameraAuthority.forceFollow();
      setSub(reason==="road-closure"?"Closure avoided • safer route active":"Route updated");
      VoiceAuthority.speak(reason==="road-closure"?"Road closure avoided. Safer route active.":"Route updated.","reroute");
      Diagnostics.push("RerouteAuthority","INSTALLED",{reason:reason||""});
      window.setTimeout(function(){RerouteAuthority.reset("replacement-complete");},900);
    }).catch(function(e){
      S.reroute.busy=false;S.reroute.state="CONFIRMED_OFF_ROUTE";
      setSub(reason==="road-closure"?"Closure reroute unavailable • stay clear":"Stay on your route while I keep checking");
      Diagnostics.error("RerouteAuthority","FAILED",{reason:reason||"",message:String(e&&e.message||e)});
    });
    return true;
  };

  /* ==========================================================================
     V263 PLUS TEASER IQ — FIRST CLEAN NATIVE TRIP
     Soft sell only. Never starts StoreKit automatically.
  ========================================================================== */
  var PlusTeaserIQ = {
    overlayId:"diqV263PlusTeaserOverlay",
    styleId:"diqV263PlusTeaserStyle",

    nativeShell:function(){
      try{ if(window.DriveIQNative){return true;} }catch(e0){}
      try{
        var mh=window.webkit&&window.webkit.messageHandlers;
        return !!(mh&&(mh.driveIQUpgrade||mh.driveIQSubscribe||mh.driveIQEntitlement||mh.driveIQDriving));
      }catch(e1){return false;}
    },

    track:function(name,data){
      data=data||{};
      data.source="drive_mode_plus_teaser";
      data.completed_trips=num(lsGet(LS.COMPLETED_TRIPS),0);
      try{
        if(typeof window.gtag==="function"){window.gtag("event",name,data);}
        else{
          window.dataLayer=window.dataLayer||[];
          window.dataLayer.push(Object.assign({event:name},data));
        }
      }catch(e){}
      Diagnostics.push("PlusTeaserIQ","TRACK",{event:name,data:data});
      return true;
    },

    eligible:function(){
      var debugTest=(window.DRIVE_IQ_NATIVE_DEBUG_TEST===true);
      if(!this.nativeShell()){return false;}
      if(!debugTest && FreeTierIQ.entitled()){return false;}
      if(num(lsGet(LS.COMPLETED_TRIPS),0)!==1){return false;}
      if(!debugTest && lsGet(LS.PLUS_TEASER_SHOWN)==="1"){return false;}
      return true;
    },

    injectStyle:function(){
      if(byId(this.styleId)){return;}
      var s=document.createElement("style");
      s.id=this.styleId;
      s.textContent=
        "#"+this.overlayId+"{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:flex-end;justify-content:center;padding:18px;padding-bottom:calc(18px + env(safe-area-inset-bottom,0px));background:rgba(2,8,18,.48);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px)}"+
        "#"+this.overlayId+" .c{width:min(100%,520px);padding:22px;border-radius:28px;background:linear-gradient(155deg,rgba(4,18,30,.98),rgba(3,10,19,.98));border:1px solid rgba(255,255,255,.14);box-shadow:0 24px 70px rgba(0,0,0,.55);color:#fff;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}"+
        "#"+this.overlayId+" .k{font-size:10px;font-weight:1000;letter-spacing:1.5px;color:#00e5ff;text-transform:uppercase}"+
        "#"+this.overlayId+" h2{margin:9px 0 0;font-size:27px;line-height:1.05;font-weight:1000;letter-spacing:-.6px}"+
        "#"+this.overlayId+" .s{margin-top:10px;font-size:14px;line-height:1.4;font-weight:700;color:rgba(255,255,255,.72)}"+
        "#"+this.overlayId+" .b{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:16px}"+
        "#"+this.overlayId+" .i{padding:10px 11px;border-radius:15px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.09);font-size:11px;font-weight:900}"+
        "#"+this.overlayId+" .p{width:100%;min-height:55px;margin-top:18px;border:0;border-radius:18px;background:linear-gradient(135deg,#28ffbe,#00e5ff);color:#061018;font-size:14px;font-weight:1000}"+
        "#"+this.overlayId+" .l{width:100%;min-height:46px;margin-top:8px;border:0;border-radius:16px;background:rgba(255,255,255,.07);color:rgba(255,255,255,.82);font-size:12px;font-weight:900}"+
        "#"+this.overlayId+" .f{margin-top:10px;text-align:center;font-size:10px;color:rgba(255,255,255,.48)}";
      try{document.head.appendChild(s);}catch(e){}
    },

    close:function(reason){
      var o=byId(this.overlayId);
      if(o&&o.parentNode){o.parentNode.removeChild(o);}
      this.track("plus_teaser_dismissed",{reason:reason||"later"});
      DestructiveFirewall.navigate(START_URL,"PlusTeaserIQ.close");
      return true;
    },

    openUpgrade:function(){
      lsSet(LS.PLUS_TEASER_TAPPED,"1");

      var payload={
        action:"presentUpgradeSheet",
        source:"first-trip-plus-teaser",
        reason:"first-trip-plus-teaser",
        isGate:false,
        completedTrips:num(lsGet(LS.COMPLETED_TRIPS),0),
        intent:{
          source:"first_trip_plus_teaser",
          completedTrips:num(lsGet(LS.COMPLETED_TRIPS),0)
        }
      };

      lsJson(LS.UPGRADE_INTENT,payload.intent);
      this.track("plus_teaser_tapped",{trigger:"first_clean_completed_trip"});

      /*
        V267: Use the known native WKScriptMessageHandler directly.
        Do NOT call generic DriveIQNative.showUpgrade here because older web
        bridge aliases may navigate to a subscription URL or external surface.
        driveIQUpgrade is registered by SLXWebViewController and only presents
        the native Drive IQ plan chooser.
      */
      try{
        if(window.webkit &&
           window.webkit.messageHandlers &&
           window.webkit.messageHandlers.driveIQUpgrade){

          Diagnostics.push("PlusTeaserIQ","NATIVE_PAYWALL_POST",payload);

          window.webkit.messageHandlers.driveIQUpgrade.postMessage(payload);

          var o=byId(this.overlayId);
          if(o&&o.parentNode){o.parentNode.removeChild(o);}

          return true;
        }
      }catch(nativeErr){
        Diagnostics.error("PlusTeaserIQ","NATIVE_PAYWALL_POST_FAILED",{
          message:String(nativeErr&&nativeErr.message||nativeErr)
        });
      }

      /*
        Native shell should always have driveIQUpgrade. Keep a safe fallback
        to presentUpgradeSheet only; never navigate away from the app.
      */
      try{
        if(window.DriveIQNative &&
           typeof window.DriveIQNative.presentUpgradeSheet==="function"){

          Diagnostics.push("PlusTeaserIQ","NATIVE_PAYWALL_FALLBACK",payload);
          window.DriveIQNative.presentUpgradeSheet(payload);

          var o2=byId(this.overlayId);
          if(o2&&o2.parentNode){o2.parentNode.removeChild(o2);}

          return true;
        }
      }catch(fallbackErr){
        Diagnostics.error("PlusTeaserIQ","NATIVE_PAYWALL_FALLBACK_FAILED",{
          message:String(fallbackErr&&fallbackErr.message||fallbackErr)
        });
      }

      toast("Drive IQ Plus is unavailable right now.");
      Diagnostics.error("PlusTeaserIQ","NO_NATIVE_PAYWALL_BRIDGE",{});
      return false;
    },

    show:function(){
      if(byId(this.overlayId)){return true;}
      this.injectStyle();
      var o=document.createElement("div");
      o.id=this.overlayId;
      o.setAttribute("role","dialog");
      o.setAttribute("aria-modal","true");
      o.innerHTML=
        '<div class="c">'+
          '<div class="k">FIRST DRIVE COMPLETE</div>'+
          '<h2>You just used Drive IQ. Now unlock what makes it different.</h2>'+
          '<div class="s">Drive IQ Plus adds the intelligence layer that watches the road ahead while you drive.</div>'+
          '<div class="b">'+
            '<div class="i">Weather IQ ahead</div>'+
            '<div class="i">Traffic IQ reroute choices</div>'+
            '<div class="i">ASK while driving</div>'+
            '<div class="i">Arrival IQ + premium drive tools</div>'+
          '</div>'+
          '<button class="p" id="diqV263PlusTeaserPrimary" type="button">Try Drive IQ Plus</button>'+
          '<button class="l" id="diqV263PlusTeaserLater" type="button">Maybe Later</button>'+
          '<div class="f">See available plans and introductory offers from Apple.</div>'+
        '</div>';
      document.body.appendChild(o);

      var self=this;
      var p=byId("diqV263PlusTeaserPrimary");
      var l=byId("diqV263PlusTeaserLater");
      if(p){p.onclick=function(){self.openUpgrade();};}
      if(l){l.onclick=function(){self.close("maybe_later");};}

      lsSet(LS.PLUS_TEASER_SHOWN,"1");
      this.track("plus_teaser_shown",{trigger:"first_clean_completed_trip"});
      Diagnostics.push("PlusTeaserIQ","SHOWN",{completedTrips:num(lsGet(LS.COMPLETED_TRIPS),0)});
      return true;
    },

    maybeShowAfterFirstTrip:function(reason){
      if(!this.eligible()){
        Diagnostics.push("PlusTeaserIQ","SKIPPED",{
          reason:reason||"",
          native:this.nativeShell(),
          entitled:FreeTierIQ.entitled(),
          completedTrips:num(lsGet(LS.COMPLETED_TRIPS),0)
        });
        return false;
      }
      return this.show();
    }
  };

  /* ==========================================================================
     V242 APP INSTALL IQ — FIRST CLEAN WEB TRIP -> APP STORE

     PURPOSE
     - Let website visitors actually experience Drive IQ first.
     - After their FIRST genuine completed trip, offer the iPhone app.
     - Never show this prompt inside the native Drive IQ shell.
     - Never interrupt an active drive.
     - Show only once per browser profile.

     A "clean completion" is still owned by ReviewIQ:
       - at least CFG.reviewMinDriveMs
       - at least CFG.reviewMinProgress
  ========================================================================== */
  var AppInstallIQ = {
    overlayId:"diqV242AppInstallOverlay",
    styleId:"diqV242AppInstallStyle",

    nativeShell:function(){
      try{
        if(window.DriveIQNative){return true;}
      }catch(e0){}

      try{
        var mh=window.webkit&&window.webkit.messageHandlers;
        if(!mh){return false;}

        /*
          Safari may expose window.webkit, so only count it as native when one
          of Drive IQ's actual WKWebView bridges exists.
        */
        return !!(
          mh.driveIQNavigate ||
          mh.driveIQVoice ||
          mh.driveIQDriving ||
          mh.driveIQReview ||
          mh.driveIQUpgrade ||
          mh.driveIQReroute ||
          mh.driveIQCarPlayBrowse
        );
      }catch(e1){
        return false;
      }
    },

    iPhoneWeb:function(){
      try{
        var ua=String(navigator.userAgent||"");
        var platform=String(navigator.platform||"");
        var touch=Number(navigator.maxTouchPoints||0);

        if(/iPhone|iPod/i.test(ua)){return true;}
        if(/iPad/i.test(ua)){return true;}

        /* Modern iPadOS can identify itself as MacIntel. */
        if(platform==="MacIntel" && touch>1){return true;}
      }catch(e){}
      return false;
    },

    track:function(name,data){
      data=data||{};
      data.source="drive_mode_first_trip";
      data.completed_trips=num(lsGet(LS.COMPLETED_TRIPS),0);

      try{
        if(typeof window.gtag==="function"){
          window.gtag("event",name,data);
          return true;
        }
      }catch(e1){}

      try{
        window.dataLayer=window.dataLayer||[];
        window.dataLayer.push(Object.assign({event:name},data));
        return true;
      }catch(e2){}

      return false;
    },

    eligible:function(){
      if(this.nativeShell()){return false;}
      if(!this.iPhoneWeb()){return false;}
      if(num(lsGet(LS.COMPLETED_TRIPS),0)!==1){return false;}
      if(lsGet(LS.APP_INSTALL_PROMPT_SHOWN)==="1"){return false;}
      if(lsGet(LS.APP_INSTALL_PROMPT_ACCEPTED)==="1"){return false;}
      return true;
    },

    injectStyle:function(){
      if(byId(this.styleId)){return;}

      var style=document.createElement("style");
      style.id=this.styleId;
      style.textContent=[
        "#"+this.overlayId+"{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:18px;background:rgba(0,8,13,.78);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);}",
        "#"+this.overlayId+" .diqV242Card{width:min(430px,100%);border:1px solid rgba(0,245,255,.30);border-radius:24px;background:linear-gradient(180deg,rgba(9,25,34,.98),rgba(3,12,18,.99));box-shadow:0 24px 80px rgba(0,0,0,.58),0 0 42px rgba(0,245,255,.12);padding:22px;color:#fff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}",
        "#"+this.overlayId+" .diqV242Top{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;}",
        "#"+this.overlayId+" .diqV242Kicker{font-size:11px;font-weight:900;letter-spacing:.16em;color:#78f7ff;text-transform:uppercase;margin-bottom:8px;}",
        "#"+this.overlayId+" .diqV242Title{font-size:26px;line-height:1.08;font-weight:900;letter-spacing:-.025em;margin:0;}",
        "#"+this.overlayId+" .diqV242Close{width:44px;height:44px;flex:0 0 44px;border-radius:14px;border:1px solid rgba(255,255,255,.13);background:rgba(255,255,255,.06);color:#fff;font-size:26px;line-height:1;cursor:pointer;}",
        "#"+this.overlayId+" .diqV242Sub{margin:13px 0 18px;color:rgba(255,255,255,.76);font-size:15px;line-height:1.48;}",
        "#"+this.overlayId+" .diqV242Proof{display:flex;gap:8px;flex-wrap:wrap;margin:0 0 18px;}",
        "#"+this.overlayId+" .diqV242Chip{padding:8px 10px;border-radius:999px;border:1px solid rgba(255,255,255,.10);background:rgba(255,255,255,.05);font-size:11px;font-weight:800;color:rgba(255,255,255,.88);}",
        "#"+this.overlayId+" .diqV242Primary,#"+this.overlayId+" .diqV242Secondary{width:100%;min-height:52px;border-radius:16px;font-size:15px;font-weight:900;cursor:pointer;display:flex;align-items:center;justify-content:center;text-decoration:none;box-sizing:border-box;}",
        "#"+this.overlayId+" .diqV242Primary{border:0;background:linear-gradient(135deg,#83fbff,#20e7f3);color:#031014;box-shadow:0 10px 30px rgba(0,245,255,.18);}",
        "#"+this.overlayId+" .diqV242Secondary{margin-top:10px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.055);color:#fff;}",
        "#"+this.overlayId+" .diqV242Fine{margin-top:12px;text-align:center;color:rgba(255,255,255,.46);font-size:11px;line-height:1.4;}",
        "@media(max-width:420px){#"+this.overlayId+"{padding:12px;}#"+this.overlayId+" .diqV242Card{padding:18px;border-radius:21px;}#"+this.overlayId+" .diqV242Title{font-size:23px;}}"
      ].join("");
      document.head.appendChild(style);
    },

    continueWeb:function(source){
      var overlay=byId(this.overlayId);
      if(overlay&&overlay.parentNode){
        try{overlay.parentNode.removeChild(overlay);}catch(e0){}
      }

      this.track("diq_first_trip_install_dismiss",{
        action:source||"continue_web"
      });

      DestructiveFirewall.navigate(START_URL,"AppInstallIQ.continueWeb");
    },

    openAppStore:function(){
      lsSet(LS.APP_INSTALL_PROMPT_ACCEPTED,"1");
      this.track("app_store_click",{
        button_location:"first_trip_drive_complete",
        app_store_url:APP_STORE_URL
      });

      try{
        window.location.href=APP_STORE_URL;
        return true;
      }catch(e){
        Diagnostics.error("AppInstallIQ","APP_STORE_NAV_FAILED",{
          message:String(e&&e.message||e)
        });
        return false;
      }
    },

    show:function(){
      if(byId(this.overlayId)){return true;}

      this.injectStyle();

      var overlay=document.createElement("div");
      overlay.id=this.overlayId;
      overlay.setAttribute("role","dialog");
      overlay.setAttribute("aria-modal","true");
      overlay.setAttribute("aria-labelledby","diqV242AppInstallTitle");

      overlay.innerHTML=
        '<div class="diqV242Card">'+
          '<div class="diqV242Top">'+
            '<div>'+
              '<div class="diqV242Kicker">FIRST DRIVE COMPLETE</div>'+
              '<h2 class="diqV242Title" id="diqV242AppInstallTitle">Keep Drive IQ with you.</h2>'+
            '</div>'+
            '<button class="diqV242Close" id="diqV242AppInstallClose" type="button" aria-label="Continue on web">&times;</button>'+
          '</div>'+
          '<div class="diqV242Sub">You finished your first drive. Get the Drive IQ app for the full iPhone + CarPlay experience.</div>'+
          '<div class="diqV242Proof">'+
            '<span class="diqV242Chip">Traffic IQ</span>'+
            '<span class="diqV242Chip">Weather IQ</span>'+
            '<span class="diqV242Chip">Stop IQ</span>'+
            '<span class="diqV242Chip">CarPlay</span>'+
          '</div>'+
          '<button class="diqV242Primary" id="diqV242AppInstallPrimary" type="button">Get Drive IQ on the App Store</button>'+
          '<button class="diqV242Secondary" id="diqV242AppInstallLater" type="button">Continue on Web</button>'+
          '<div class="diqV242Fine">Your next drive is one tap away.</div>'+
        '</div>';

      document.body.appendChild(overlay);

      var self=this;
      var primary=byId("diqV242AppInstallPrimary");
      var later=byId("diqV242AppInstallLater");
      var close=byId("diqV242AppInstallClose");

      if(primary){primary.onclick=function(){self.openAppStore();};}
      if(later){later.onclick=function(){self.continueWeb("continue_web");};}
      if(close){close.onclick=function(){self.continueWeb("close");};}

      lsSet(LS.APP_INSTALL_PROMPT_SHOWN,"1");

      this.track("diq_first_trip_install_prompt",{
        trigger:"first_clean_completed_trip"
      });

      Diagnostics.push("AppInstallIQ","PROMPT_SHOWN",{
        completedTrips:num(lsGet(LS.COMPLETED_TRIPS),0),
        url:APP_STORE_URL
      });

      try{primary&&primary.focus();}catch(eFocus){}
      return true;
    },

    maybePromptAfterFirstTrip:function(reason){
      if(!this.eligible()){
        Diagnostics.push("AppInstallIQ","SKIPPED",{
          reason:reason||"",
          native:this.nativeShell(),
          iphoneWeb:this.iPhoneWeb(),
          completedTrips:num(lsGet(LS.COMPLETED_TRIPS),0),
          shown:lsGet(LS.APP_INSTALL_PROMPT_SHOWN)==="1"
        });
        return false;
      }

      return this.show();
    }
  };

  /* ==========================================================================
     REVIEW IQ — ONLY AFTER A CLEAN COMPLETED DRIVE
  ========================================================================== */
  var ReviewIQ = {
    recordCleanCompletion:function(reason){
      if(S.review.completed){return false;}
      var age=now()-num(S.session.startedAt),progress=RouteAuthority.progressRatio(),id=clean(S.session.id);
      if(age<CFG.reviewMinDriveMs||progress<CFG.reviewMinProgress){return false;}
      if(id&&lsGet(LS.REVIEW_LAST_DRIVE_ID)===id){return false;}
      S.review.completed=true;
      var count=num(lsGet(LS.COMPLETED_TRIPS),0)+1;
      lsSet(LS.COMPLETED_TRIPS,count);lsSet(LS.REVIEW_LAST_DRIVE_ID,id);
      FounderTripTelemetry.record("trip_completed",id,{milestone:"clean_completion"});
      if(count===3){FounderTripTelemetry.record("trip_3_reached",id,{milestone:"third_completed_trip"});}
      if(count===5){FounderTripTelemetry.record("trip_5_reached",id,{milestone:"fifth_completed_trip"});}
      Diagnostics.push("ReviewIQ","COMPLETED_TRIP",{count:count,reason:reason||""});
      if(lsGet(LS.REVIEW_PROMPT_REQUESTED)==="1"){return true;}
      if(count>=2){
        lsSet(LS.REVIEW_PROMPT_REQUESTED,"1");
        window.setTimeout(function(){ReviewIQ.request();},700);
      }
      return true;
    },
    request:function(){
      if(S.session.active&&S.session.routeActive&&!S.session.ended){return false;}
      try{
        if(window.DriveIQNative&&typeof window.DriveIQNative.requestReview==="function"){
          window.DriveIQNative.requestReview({source:"v200-complete",completedTrips:num(lsGet(LS.COMPLETED_TRIPS),0)});
          S.review.requested=true;return true;
        }
      }catch(e1){}
      try{
        if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQReview){
          window.webkit.messageHandlers.driveIQReview.postMessage({source:"v200-complete"});
          S.review.requested=true;return true;
        }
      }catch(e2){}
      return false;
    }
  };

  /* ==========================================================================
     UPGRADE / ENTITLEMENT BRIDGE — PRESENTATION ONLY
  ========================================================================== */
  /* V240: tier selects presentation/features, NEVER navigation authority.
     Existing active drives keep their original tier until End Drive. */
  /* V272: website Shopify Plus is server-verified. Never treat browser
     localStorage's PLUS_ACTIVE flag as proof of a paid web subscription.
     Do not modify StoreKit/native entitlement authority or active routes. */
  var WebPlusIQ = {
    active:false, checked:false, inFlight:false,
    nativeShell:function(){return AppInstallIQ.nativeShell();},
    verify:function(){
      if(this.nativeShell() || this.inFlight){return;}
      var token="";
      try{token=String(window.sessionStorage.getItem("diq_web_session_v1")||"");}catch(e){}
      if(!/^[a-zA-Z0-9_-]{32,200}$/.test(token)){this.active=false;this.checked=true;return;}
      this.inFlight=true;
      var self=this;
      fetch("https://driveiq-subscription-api-85238371012.us-central1.run.app/v1/entitlement",{
        method:"GET",cache:"no-store",headers:{Authorization:"Bearer "+token}
      }).then(function(r){if(!r.ok){throw Error("entitlement_unavailable");}return r.json();})
      .then(function(data){
        self.checked=true;self.active=!!(data&&data.active===true);
        /* A verified server-side paid upgrade may enable Plus presentation
           without touching route, camera, guidance or CarPlay ownership.
           Never automatically downgrade an active drive on network failure. */
        if(self.active && FreeTierIQ.tier==="free"){
          FreeTierIQ.tier=FreeTierIQ.desired();
          lsSet(LS.ACCESS_TIER,FreeTierIQ.tier);
          FreeTierIQ.paint("verified-web-plus");
          UpgradeIQ.sync();
        }
      }).catch(function(){self.checked=true;self.active=false;})
      .then(function(){self.inFlight=false;});
    }
  };
  var FreeTierIQ = {
    tier:"plus",locked:false,reported:false,
    count:function(){
      var n=Number(lsGet(LS.COMPLETED_TRIPS)||0);
      return finite(n)&&n>=0?Math.floor(n):0;
    },
    entitled:function(){
      return WebPlusIQ.nativeShell()
        ? (lsGet(LS.PLUS_ACTIVE)==="1"||lsGet(LS.FOUNDING_DRIVER)==="1")
        : WebPlusIQ.active;
    },
    desired:function(){return this.entitled()||this.count()<5?"plus":"free";},
    init:function(){
      var cached=clean(lsGet(LS.ACCESS_TIER));
      /* On entry, prefer current access data. During this drive, don't flip. */
      this.tier=this.desired();this.locked=true;
      lsSet(LS.ACCESS_TIER,this.tier);
      this.paint("boot");
      Diagnostics.push("FreeTierIQ","SESSION_TIER",{tier:this.tier,count:this.count(),cachedTier:cached});
    },
    free:function(){return this.tier==="free";},
    paint:function(reason){
      var free=this.free();
      try{document.body.setAttribute("data-diq-access-tier",this.tier);}catch(e){}
      var pill=byId("diqModePill");
      if(pill&&free){pill.textContent="DRIVE IQ FREE";}
      if(pill&&!free&&reason==="verified-web-plus"){pill.textContent="DRIVE IQ PLUS";}
      if(!byId("diqFreeTierStyle")){
        var css=document.createElement("style");css.id="diqFreeTierStyle";
        css.textContent='body[data-diq-access-tier="free"] #diqVibeBtn,'+
          'body[data-diq-access-tier="free"] #diqSigArrival,'+
          'body[data-diq-access-tier="free"] #diqSigRoute,'+
          'body[data-diq-access-tier="free"] #diqArrivalIQCard{display:none!important;}'+
          'body[data-diq-access-tier="free"] #diqModePill{background:rgba(10,30,42,.9)!important;color:#e2eff3!important;border-color:rgba(210,230,239,.25)!important;}';
        try{document.head.appendChild(css);}catch(e2){}
      }
      Diagnostics.push("FreeTierIQ","PAINT",{tier:this.tier,reason:reason||""});
    },
    upgrade:function(){return UpgradeIQ.present("free-tier-upgrade");}
  };
  var UpgradeIQ = {
    plusActive:function(){return FreeTierIQ.entitled();},
    sync:function(){
      var active=this.plusActive();
      try{document.body.setAttribute("data-diq-plus",active?"1":"0");}catch(e){}
      Diagnostics.push("UpgradeIQ","SYNC",{active:active,pending:lsGet(LS.PLUS_PENDING)==="1"});
      return active;
    },
    present:function(source){
      var payload={source:source||"drive-mode",intent:parse(lsGet(LS.UPGRADE_INTENT))||null};
      if(!WebPlusIQ.nativeShell()){
        if(S.session.routeActive && !S.session.ended){
          toast("End your drive before upgrading.");return false;
        }
        try{window.location.assign("/pages/web-upgrades");return true;}catch(webErr){return false;}
      }

      try{
        if(typeof window.gtag==="function"){
          window.gtag("event","paywall_requested",{source:payload.source,completed_trips:FreeTierIQ.count()});
        }else{
          window.dataLayer=window.dataLayer||[];
          window.dataLayer.push({event:"paywall_requested",source:payload.source,completed_trips:FreeTierIQ.count()});
        }
      }catch(trackErr){}

      Diagnostics.push("UpgradeIQ","PAYWALL_REQUESTED",{source:payload.source,completedTrips:FreeTierIQ.count()});

      try{
        if(window.DriveIQNative&&typeof window.DriveIQNative.showUpgrade==="function"){
          window.DriveIQNative.showUpgrade(payload);return true;
        }
      }catch(e1){}

      try{
        if(window.DriveIQNative&&typeof window.DriveIQNative.presentUpgradeSheet==="function"){
          window.DriveIQNative.presentUpgradeSheet(payload);return true;
        }
      }catch(e2){}

      try{
        if(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.driveIQUpgrade){
          window.webkit.messageHandlers.driveIQUpgrade.postMessage(payload);return true;
        }
      }catch(e3){}

      return false;
    }
  };

  /* ==========================================================================
     ACTIVE-ROUTE LIFECYCLE TRIPWIRE
     This cannot stop an external/native process from killing a WKWebView, but it
     guarantees this runtime never intentionally reloads or navigates while active.
  ========================================================================== */
  window.addEventListener("beforeunload",function(e){
    if(S.session.active&&S.session.routeActive&&!S.session.ended){
      Diagnostics.error("Firewall","UNEXPECTED_BEFOREUNLOAD",{phase:S.session.phase});
      try{
        if(window.DriveIQNative&&typeof window.DriveIQNative.diagnostic==="function"){
          window.DriveIQNative.diagnostic({type:"unexpected_beforeunload",snapshot:snapshot(),timestamp:now()});
        }
      }catch(err){}
      try{e.preventDefault();e.returnValue="";}catch(err2){}
      return "";
    }
  },false);

  /* ==========================================================================
     COMPATIBILITY BRIDGES — KEEP EXISTING HTML/NATIVE INTEGRATIONS WORKING
  ========================================================================== */
  window.DRIVE_IQ_SIGNAL_BRIDGE = {
    report:function(type,note){return IncidentIQ.report(type,note);},
    refresh:function(){return IncidentIQ.refresh("signal-bridge");},
    state:function(){return {events:S.incidents.events,closures:S.incidents.closures};}
  };
  window.DriveIQRerouteAuthority = {
    request:function(reason){return RerouteAuthority.request(reason||"public");},
    state:function(){return S.reroute.state;},
    reset:function(reason){return RerouteAuthority.reset(reason||"public");}
  };
  window.DriveIQV144DriverArrow = {
    version:"V200_COMPLETE",
    get:function(){return S.vehicle.marker;},
    paint:function(){return VehicleMarkerAuthority.paint();},
    heading:function(){return VehicleMarkerAuthority.heading();}
  };
  window.DriveIQScanAroundMe = function(query){return StopIQ.search(query||"fuel");};


  /* ==========================================================================
     UI WIRING
  ========================================================================== */
  /* ==========================================================================
     VIBE PICKER — NON-DESTRUCTIVE LIVE THEME CONTROL
  ========================================================================== */
  function syncVibeButtons(){
    var buttons=document.querySelectorAll("[data-diq-vibe]"),i,skin=PreferenceAuthority.normalizeSkin(S.preferences.skin||PreferenceAuthority.readSkin());
    for(i=0;i<buttons.length;i++){
      try{
        if(PreferenceAuthority.normalizeSkin(buttons[i].getAttribute("data-diq-vibe"))===skin){buttons[i].classList.add("on");}
        else{buttons[i].classList.remove("on");}
      }catch(e){}
    }
  }

  function closeVibeSheet(){
    var el=byId("diqVibeSheet");
    if(el){try{el.classList.remove("on");el.setAttribute("aria-hidden","true");}catch(e){}}
    return true;
  }

  function openVibeSheet(){
    var el=byId("diqVibeSheet");
    if(!el){return false;}
    try{
      closeStopSheet();
      var report=byId("diqReportSheet");
      if(report){report.classList.remove("on");}
    }catch(e1){}
    syncVibeButtons();
    try{el.classList.add("on");el.setAttribute("aria-hidden","false");}catch(e2){}
    return true;
  }

  function chooseVibe(skin){
    skin=PreferenceAuthority.normalizeSkin(skin);
    /* Explicit in-drive change must update the Garage authority too. */
    lsSet(LS.GARAGE_SKIN,skin);
    PreferenceAuthority.applySkin(skin,"drive-mode-vibe");
    try{window.dispatchEvent(new CustomEvent("driveiq:themechange",{detail:{skin:skin,theme:skin,source:"drive-mode-vibe"}}));}catch(e1){}
    syncVibeButtons();
    toast("Vibe • "+skin.replace(/_/g," ").replace(/\b\w/g,function(c){return c.toUpperCase();}));
    window.setTimeout(closeVibeSheet,240);
    return skin;
  }

  function wireButtons(){
    var vibeBtn=byId("diqVibeBtn"),vibeClose=byId("diqVibeClose"),vibes=document.querySelectorAll("[data-diq-vibe]"),vi;
    if(vibeBtn){vibeBtn.onclick=function(){var sheet=byId("diqVibeSheet");if(sheet&&sheet.classList.contains("on")){closeVibeSheet();}else{openVibeSheet();}};}
    if(vibeClose){vibeClose.onclick=closeVibeSheet;}
    for(vi=0;vi<vibes.length;vi++){vibes[vi].onclick=function(){chooseVibe(this.getAttribute("data-diq-vibe")||"cockpit");};}
    syncVibeButtons();
    var el,chips,i;
    el=byId("diqAudio");if(el){el.onclick=function(){VoiceAuthority.toggle();};}
    el=byId("diqFollow");if(el){el.onclick=function(){CameraAuthority.forceFollow();};}
    el=byId("diqFollowLock");if(el){el.onclick=function(){CameraAuthority.forceFollow();};}
    el=byId("diqOverview");
    if(el){
      el.onclick=null;
      el.style.display="none";
      el.setAttribute("aria-hidden","true");
      el.setAttribute("tabindex","-1");
      Diagnostics.push("UI","OVERVIEW_REMOVED",{id:"diqOverview"});
    }
    el=byId("diqZoomIn");if(el){el.onclick=function(){CameraAuthority.enterFree("zoom-in-button",true);try{S.map.instance.zoomTo(S.map.instance.getZoom()+.8,{duration:200});}catch(e){}};}
    el=byId("diqZoomOut");if(el){el.onclick=function(){CameraAuthority.enterFree("zoom-out-button",true);try{S.map.instance.zoomTo(S.map.instance.getZoom()-.8,{duration:200});}catch(e){}};}
    ensureStopSheet();
    el=byId("diqFindStops");if(el){el.onclick=function(e){if(e&&e.preventDefault){e.preventDefault();}return openStopSheet();};}
    var stopEntries=document.querySelectorAll('[data-diq-action="find-stops"],.diqFindStops,[aria-label*="Stop Along"],[aria-label*="Find Stops"]');
    for(i=0;i<stopEntries.length;i++){stopEntries[i].onclick=function(e){if(e&&e.preventDefault){e.preventDefault();}return openStopSheet();};}
    el=byId("diqStopSheetClose");if(el){el.onclick=closeStopSheet;}
    el=byId("diqEndDrive");if(el){el.onclick=function(){DriveSession.end("user-end-drive");};}
    el=byId("diqBack");if(el){el.onclick=function(){if(S.session.active&&S.session.routeActive){toast("End Drive to leave navigation");Diagnostics.push("Firewall","BACK_BLOCKED",{});return false;}return DestructiveFirewall.navigate(START_URL,"back-button");};}
    chips=document.querySelectorAll(".diqStopChip");for(i=0;i<chips.length;i++){chips[i].onclick=function(){stopIntent(this.getAttribute("data-stop")||this.getAttribute("data-category")||"fuel");};}
    el=byId("diqNeedSomethingBtn");if(el){el.onclick=function(){StopIQ.search("fuel");};}
    el=byId("diqRoadIssue");if(el){el.onclick=function(){IncidentIQ.report("road_issue","");};}
    el=byId("diqReportRoadIssue");if(el){el.onclick=function(){IncidentIQ.report("road_issue","");};}
    var ups=document.querySelectorAll("[data-diq-upgrade],.diqUpgradeBtn");
    for(i=0;i<ups.length;i++){ups[i].onclick=function(){UpgradeIQ.present("drive-mode-button");};}
  }

  /* ==========================================================================
     NON-DESTRUCTIVE LIFECYCLE RECOVERY
  ========================================================================== */
  function installLifecycleRecovery(){
    var timer=null;
    function recover(reason){
      if(timer){window.clearTimeout(timer);}
      timer=window.setTimeout(function(){
        timer=null;
        MapAuthority.resize(reason);
        RouteAuthority.ensureLayers();
        ThemePaintAuthority.paint("recovery-"+reason);
        RouteAuthority.paint();
        RouteAuthority.paintDestination();
        if(S.session.routeActive&&!S.session.ended){
          RouteAuthority.ensureVisible("recovery-"+reason);
        }
        VehicleMarkerAuthority.paint();
        if(S.camera.mode==="FOLLOW"){CameraAuthority.follow(true);}
        Diagnostics.push("Recovery","NON_DESTRUCTIVE",{reason:reason});
      },180);
    }
    window.addEventListener("resize",function(){recover("resize");},false);
    window.addEventListener("orientationchange",function(){recover("orientationchange");},false);
    window.addEventListener("pageshow",function(){recover("pageshow");},false);
    window.addEventListener("focus",function(){recover("focus");},false);
    document.addEventListener("visibilitychange",function(){if(!document.hidden){recover("visibilitychange");}},false);
    if(window.visualViewport){window.visualViewport.addEventListener("resize",function(){recover("visualViewport");},false);}
  }

  /* ==========================================================================
     HEARTBEATS — OBSERVE, NEVER REBUILD
  ========================================================================== */
  function startObservers(){
    if(!S.timers.routeWatch){
      S.timers.routeWatch=window.setInterval(function(){
        try{RouteAuthority.pollStorage();}
        catch(e){Diagnostics.error("RouteAuthority","WATCH_ERROR",{message:String(e&&e.message||e)});}
      },CFG.routeWatchMs);
    }

    if(!S.timers.heartbeat){S.timers.heartbeat=window.setInterval(function(){HUD.paint();if(!S.arrival.arrived){sendCarPlaySnapshot("heartbeat");}},1500);}
    if(!S.timers.progress){S.timers.progress=window.setInterval(function(){
      /* GPS is authoritative; this timer only repaints read-only UI and preserves marker visibility. */
      VehicleMarkerAuthority.paint();HUD.paint();
      var raw=S.location.raw;if(raw&&now()-raw.ts>CFG.rawFixFreshMs){setGps("WAITING");Diagnostics.push("LocationAuthority","STALE_HOLD",{ageMs:now()-raw.ts});}
    },1000);}
    if(!S.timers.incident){S.timers.incident=window.setInterval(function(){IncidentIQ.refresh("interval");},CFG.incidentPollMs);}
    if(!S.timers.weatherSafety){S.timers.weatherSafety=window.setInterval(function(){try{WeatherSafetyAuthority.refresh("interval");}catch(e){Diagnostics.error("WeatherSafetyAuthority","WATCH_ERROR",{message:String(e&&e.message||e)});}},CFG.weatherSafetyPollMs);}
  }

  /* ==========================================================================
     PUBLIC DIAGNOSTIC / COMPATIBILITY API
  ========================================================================== */
  function snapshot(){
    return {build:window.DRIVE_IQ_DRIVE_MODE_BUILD,session:{id:S.session.id,active:S.session.active,routeActive:S.session.routeActive,phase:S.session.phase},map:{created:S.map.created,loaded:S.map.loaded},vehicle:{created:S.vehicle.created,heading:VehicleMarkerAuthority.heading()},location:S.location.canonical,route:{signature:S.route.signature,points:S.route.coords.length,progress:RouteAuthority.progressRatio(),progressRatio:RouteAuthority.progressRatio(),remainingMiles:RouteAuthority.remainingMiles(),speedLimitMph:S.route.speedLimitMph,speedLimitRoad:S.speedLimit.road,speedLimitConfidence:S.speedLimit.confidence,speedLimitSource:S.speedLimit.source},maneuver:{current:S.maneuver.current,count:S.maneuver.list.length,instructionFetch:S.maneuver.instructionFetch,instructionFetched:S.maneuver.instructionFetched,instructionError:S.maneuver.instructionError},voice:{enabled:S.voice.enabled,lastText:S.voice.lastText,lastAt:S.voice.lastAt},camera:{mode:S.camera.mode,lastBearing:S.camera.lastBearing},reroute:{state:S.reroute.state,busy:S.reroute.busy,reason:S.reroute.reason},arrival:{arrived:S.arrival.arrived},stop:{busy:S.stop.busy,query:S.stop.query,count:S.stop.results.length,selected:S.stop.selected?{name:S.stop.selected.name,address:S.stop.selected.address}:null},incidents:{active:S.incidents.events.length,closures:S.incidents.closures.length},weatherSafety:{severe:S.weatherSafety.severe.length,impacts:S.weatherSafety.impacts.length,lastAt:S.weatherSafety.lastAt},preferences:{skin:S.preferences.skin,voiceStyle:S.preferences.voiceStyle},lastEvents:Diagnostics.recent(25)};
  }

  window.DriveIQSpeedLimit = {
    status:function(){return SpeedLimitAuthority.status();},
    clear:function(){SpeedLimitAuthority.clear("manual-debug");return true;},
    refresh:function(){
      var raw=S.location.raw,p;
      if(!raw){return false;}
      p=RouteAuthority.updateProgress(raw);
      if(!p){return false;}
      S.speedLimit.lastQueryAt=0;
      return SpeedLimitAuthority.observe(raw,p);
    }
  };

  window.DriveIQTrustCore = {
    version:"V222_ARRIVAL_CARPLAY_FREE_DRIVE",
    state:S,
    snapshot:snapshot,
    diagnostics:function(n){return Diagnostics.recent(n||100);},
    follow:function(){CameraAuthority.forceFollow();},
    speak:function(text){return VoiceAuthority.speak(text,"manual");},
    reroute:function(reason){return RerouteAuthority.request(reason||"manual");},
    scan:function(query){return StopIQ.search(query||"fuel");},
    addStop:function(place,source){return AddStopAuthority.request(place,source||"trust-core-api");},
    report:function(type,note){return IncidentIQ.report(type||"road_issue",note||"");},
    preferences:function(){return {skin:S.preferences.skin,voiceStyle:S.preferences.voiceStyle,vehicleMarker:S.preferences.vehicleMarker,vehicle3d:readSelected3DVehicle(),accessTier:FreeTierIQ.tier};},
    setSkin:function(skin){return PreferenceAuthority.applySkin(skin,"trust-core-api");},
    setVoiceStyle:function(style){return PreferenceAuthority.applyVoice(style,"trust-core-api");},
    setVehicleMarker:function(marker){return PreferenceAuthority.applyVehicleMarker(marker,"trust-core-api");},
    streetNames:function(){
      return S.maneuver.list.map(function(m){return {label:m.label,street:m.street||"",distanceAlong:m.distanceAlong,source:m.source,streetSource:m.streetSource||"",rawText:m.rawText||""};});
    },
    rehydrateStreetNames:function(){
      S.maneuver.instructionFetched=false;
      S.maneuver.instructionFetch=false;
      S.maneuver.instructionRouteSig="";
      return hydrateInstructionManeuvers("manual-street-name-rehydrate");
    },
    endDrive:function(){DriveSession.end("public-api");},
    scanAroundArrival:function(){return requestAdventureMode();},
    turnAround:function(){return requestTurnAround();},
    returnHome:function(){return requestReturnHome();},
    returnTrip:function(){return requestReturnTrip();},
    route:function(){return S.route.geo;},
    fix:function(){return S.location.canonical||S.location.raw;}
  };

  window.DRIVE_IQ_DRIVE_MODE = {
    version:"v270.2-gesture-input-fix",
    follow:function(){CameraAuthority.forceFollow();},
    forceFollow:function(){CameraAuthority.forceFollow();},
    getRoute:function(){return S.route.geo;},
    getMap:function(){return S.map.instance;},
    getFix:function(){return S.location.canonical||S.location.raw;},
    getDestination:function(){return RouteAuthority.destinationFix();},
    getRouteIntel:function(){return S.route.intel;},
    speak:function(text){return VoiceAuthority.speak(text||"Drive IQ voice is ready.","public");},
    driveBriefing:function(){return BriefingAuthority.speak("public");},
    refreshWeatherSafety:function(){return WeatherSafetyAuthority.refresh("public");},
    getWeatherSafety:function(){return {severe:S.weatherSafety.severe,impacts:S.weatherSafety.impacts,lastAt:S.weatherSafety.lastAt};},
    getCloudIntelligence:function(){return {connectivity:S.cloudIntelligence.connectivity,lastAt:S.cloudIntelligence.lastAt,weather:S.cloudIntelligence.weather,traffic:S.cloudIntelligence.traffic,response:S.cloudIntelligence.response};},
    accessTier:function(){return {tier:FreeTierIQ.tier,completed:FreeTierIQ.count(),hasPlus:FreeTierIQ.entitled()};},
    trafficDebug:function(){return {build:window.DRIVE_IQ_DRIVE_MODE_BUILD,cloud:S.cloudIntelligence,etaAudit:CloudIntelligencePresentation.trafficEtaAudit(S.cloudIntelligence.traffic),trafficEvents:Diagnostics.events.filter(function(e){return e.owner==="TrafficIQ"||e.owner==="CloudIntelligence";}).slice(-20)};},
    turnByTurn:function(){ManeuverAuthority.tick();return S.maneuver;},
    requestReroute:function(reason){return RerouteAuthority.request(reason||"public");},
    scanAround:function(query){return StopIQ.search(query||"fuel");},
    // V15.4A: Read-only Stop IQ snapshot for the native cockpit pilot.
    // No searches, routing changes, camera commands, or session mutations.
    nativeStopSnapshot:function(){
      return {
        busy:!!S.stop.busy,
        query:String(S.stop.query||""),
        results:(S.stop.results||[]).slice(0,8).map(function(p){
          return {name:String(p.name||""),address:String(p.address||""),
            distanceMiles:Number.isFinite(Number(p.distanceMiles))?Number(p.distanceMiles):null,
            lat:Number.isFinite(Number(p.lat))?Number(p.lat):null,
            lng:Number.isFinite(Number(p.lng))?Number(p.lng):null};
        })
      };
    },

    // V15.4B: Only exact live Stop IQ results may be submitted. Explicit native
    // confirmation occurs upstream. Never accept arbitrary coordinates from Swift.
    nativeRequestStop:function(index,token,expectedQuery,expectedLat,expectedLng){
      token=String(token||"");
      if(!/^[A-Za-z0-9-]{8,80}$/.test(token)){return {ok:false,reason:"invalid-token"};}
      if(!S.session.active||!S.session.routeActive||S.session.ended||S.arrival.arrived){return {ok:false,reason:"route-not-active"};}
      if(S.stop.busy||AddStopAuthority.busy){return {ok:false,reason:"busy"};}
      var i=Number(index);
      if(!Number.isInteger(i)||i<0||i>=Math.min(8,S.stop.results.length)){return {ok:false,reason:"stale-selection"};}
      if(String(expectedQuery||"")!==String(S.stop.query||"")){return {ok:false,reason:"query-changed"};}
      var selected=S.stop.results[i];
      var lat=Number(selected&&selected.lat),lng=Number(selected&&selected.lng);
      if(!Number.isFinite(lat)||!Number.isFinite(lng)||Math.abs(lat-Number(expectedLat))>0.000001||Math.abs(lng-Number(expectedLng))>0.000001){
        return {ok:false,reason:"place-changed"};
      }
      if(NativeStopPilot.state==="pending"){return {ok:false,reason:"pending"};}
      if(AddStopAuthority.key(selected)===AddStopAuthority.lastKey && Date.now()-AddStopAuthority.lastAt<6000){
        return {ok:false,reason:"recent-duplicate"};
      }
      NativeStopPilot={token:token,state:"pending",message:"Updating route…",name:String(selected.name||"Stop"),at:Date.now()};
      var accepted=AddStopAuthority.request(selected,"native-stop-v154b:"+token);
      if(!accepted){NativeStopPilot.state="failed";NativeStopPilot.message="Navigation could not accept this stop.";}
      return {ok:!!accepted,state:NativeStopPilot.state,reason:NativeStopPilot.message};
    },
    nativeStopHandoffStatus:function(token){
      if(String(token||"")!==NativeStopPilot.token){return {state:"unknown"};}
      return {state:NativeStopPilot.state,message:NativeStopPilot.message,name:NativeStopPilot.name,at:NativeStopPilot.at};
    },
    // V15.5C: Read-only audit of the installed waypoint route, for native Ask IQ.
    // This never mutates Mapbox, reroutes, the camera, or GPS.
    nativeAskRouteAudit:function(){
      var intel=S.route.intel||{}, added=intel.addedStop||null;
      var persisted=parse(lsGet(LS.ROUTE_INTEL))||{};
      var savedStop=parse(lsGet("diq_added_stop_v1"))||null;
      var live=S.route.geo;
      var stored=parse(lsGet(LS.ROUTE_GEO));
      var liveSig=geometrySignature(live);
      var storedSig=geometrySignature(stored);
      var liveProjection=added?nearestProjection({lat:Number(added.lat),lng:Number(added.lng)},routeCoords(live),null):null;
      var storedProjection=added?nearestProjection({lat:Number(added.lat),lng:Number(added.lng)},routeCoords(stored),null):null;
      var liveDistance=liveProjection?Number(liveProjection.distMiles):null;
      var storedDistance=storedProjection?Number(storedProjection.distMiles):null;
      var matchingStop=!!(added&&savedStop&&Math.abs(Number(added.lat)-Number(savedStop.lat))<0.00001&&Math.abs(Number(added.lng)-Number(savedStop.lng))<0.00001);
      var verified=!!(S.session.routeActive&&added&&matchingStop&&liveSig&&liveSig===storedSig&&liveDistance!==null&&storedDistance!==null&&liveDistance<=0.18&&storedDistance<=0.18);
      return {verified:verified,active:!!S.session.routeActive,
        stop:added?{name:String(added.name||""),lat:Number(added.lat),lng:Number(added.lng)}:null,
        liveDistanceMiles:liveDistance,storedDistanceMiles:storedDistance,
        livePoints:S.route.coords.length,storedPoints:routeCoords(stored).length,
        signaturesMatch:!!(liveSig&&liveSig===storedSig),
        savedStopMatches:matchingStop,
        destination:RouteAuthority.destinationFix(),
        recentEvents:Diagnostics.events.filter(function(e){return e.owner==="AddStopAuthority"||e.owner==="RouteAuthority";}).slice(-12)};
    },
    addStop:function(place,source){return AddStopAuthority.request(place,source||"drive-mode-api");},
    openStopIq:function(){return openStopSheet();},
    closeStopIq:function(){return closeStopSheet();},
    reportRoadIssue:function(type,note){return IncidentIQ.report(type||"road_issue",note||"");},
    getPreferences:function(){return {skin:S.preferences.skin,voiceStyle:S.preferences.voiceStyle,vehicleMarker:S.preferences.vehicleMarker,vehicle3d:readSelected3DVehicle()};},
    setSkin:function(skin){return PreferenceAuthority.applySkin(skin,"drive-mode-api");},
    openVibe:function(){return openVibeSheet();},
    closeVibe:function(){return closeVibeSheet();},
    chooseVibe:function(skin){return chooseVibe(skin);},
    setVoiceStyle:function(style){return PreferenceAuthority.applyVoice(style,"drive-mode-api");},
    setVehicleMarker:function(marker){return PreferenceAuthority.applyVehicleMarker(marker,"drive-mode-api");},
    liveGeo:function(){return carPlayPayload("public");},
    sendLiveGeo:function(){return sendCarPlaySnapshot("public");},
    getCarPlayMode:function(){return S.arrival.arrived?"arrival":(S.session.routeActive?"guidance":"freeDrive");},
    scanAroundArrival:function(){return requestAdventureMode();},
    turnAround:function(){return requestTurnAround();},
    returnHome:function(){return requestReturnHome();},
    returnTrip:function(){return requestReturnTrip();},
    toast:toast
  };

  /* ==========================================================================
     RADAR IQ DRIVE MODE — ISOLATED WEB PILOT V1.1 — LEFT TRAFFIC HORIZON POSITIONING
     Opt-in only with ?radariq_lab=1 and eligible Drive IQ tier.
     Never modifies navigation map, camera, route, GPS watch, voice or CarPlay.
     Radar tile is a geographic pixel preview, NOT an aligned road map.
  ========================================================================== */
  var RadarIQWebPilot=(function(){
    var API="https://driveiq-intelligence-api-85238371012.us-central1.run.app";
    var REFRESH=300000, STALE=1800000;
    var enabled=false,open=false,busy=false,epoch=0,frameAt=null,lastCheck=0;
    var activeFrame="",lastFailure="",lastPosition=null,poll=null,clock=null;
    var shell=null,button=null,canvas=null,caption=null,detail=null;
    function message(t){if(detail)detail.textContent=t;}
    function coord(){
      var f=S.location.raw;
      if(!f||!finite(f.lat)||!finite(f.lng)||!finite(f.ts)||now()-f.ts>120000){return null;}
      return {lat:f.lat,lng:f.lng};
    }
    function tile(p,z){
      var n=Math.pow(2,z),lat=clamp(p.lat,-85.05112878,85.05112878);
      return {x:Math.floor((p.lng+180)/360*n),y:Math.floor((1-Math.asinh(Math.tan(lat*Math.PI/180))/Math.PI)/2*n)};
    }
    function ageLabel(){
      if(!caption)return;
      if(!frameAt){caption.textContent="DATA UNVERIFIED · frame time unavailable";return;}
      var age=Math.max(0,Math.floor((now()-Date.parse(frameAt))/60000));
      caption.textContent="Xweather provider frame: "+age+" min old"+(age>=30?" · STALE — NOT CURRENT":age>=15?" · AGING":" · PINNED TEST")+(lastFailure?" · UPDATE FAILED":"");
    }
    function view(p,bitmap){
      if(!canvas)return;
      var ctx=canvas.getContext("2d");if(!ctx)throw Error("Canvas unavailable");
      ctx.clearRect(0,0,256,256);
      ctx.fillStyle="#162630";ctx.fillRect(0,0,256,256);
      // This is a RAW center tile, not a registered basemap overlay.
      ctx.drawImage(bitmap,0,0,256,256);
      ctx.lineWidth=1;ctx.strokeStyle="rgba(157,206,214,.25)";
      [64,128,192].forEach(function(n){ctx.beginPath();ctx.moveTo(n,0);ctx.lineTo(n,256);ctx.moveTo(0,n);ctx.lineTo(256,n);ctx.stroke();});
      ctx.strokeStyle="#84e2e9";ctx.strokeRect(124,124,8,8);
    }
    async function getJson(url){
      var ctl=new AbortController(),tm=setTimeout(function(){ctl.abort();},12000);
      try{var r=await fetch(url,{cache:"no-store",mode:"cors",signal:ctl.signal});if(!r.ok)throw Error("manifest HTTP "+r.status);return await r.json();}
      finally{clearTimeout(tm);}
    }
    async function refresh(){
      if(!enabled||!open||busy||document.hidden||S.session.ended)return;
      var p=coord();if(!p){message("Waiting for a fresh GPS position. No navigation changes.");return;}
      // Repaint for movement only on the normal five-minute interval.
      busy=true;var run=++epoch;lastCheck=now();
      message("Checking timestamped radar frame…");
      try{
        var m=await getJson(API+"/v1/weather/xweather/frame-manifest");
        if(run!==epoch||!open)return;
        if(!m||m.ok!==true||m.anchorPinnedImageMatched!==true||!/^[0-9]{14}$/.test(String(m.frameId||"")))throw Error("frame manifest not verified");
        if(m.tileTemplate!=="/v1/weather/xweather/frame-tiles/"+m.frameId+"/{z}/{x}/{y}.png")throw Error("unexpected tile template");
        var time=Date.parse(m.providerResolvedValidTime);
        if(!Number.isFinite(time)||time>now()+60000||now()-time>STALE)throw Error("provider frame too old or undated");
        var t=tile(p,7);
        var u=API+"/v1/weather/xweather/frame-tiles/"+m.frameId+"/7/"+t.x+"/"+t.y+".png";
        var ctl=new AbortController(),tm=setTimeout(function(){ctl.abort();},12000),r,b,bitmap;
        try{
          r=await fetch(u,{cache:"no-store",mode:"cors",signal:ctl.signal});
          if(!r.ok)throw Error("pinned tile HTTP "+r.status);
          if(!(r.headers.get("content-type")||"").toLowerCase().includes("image/png"))throw Error("tile not PNG");
          b=await r.blob();bitmap=await createImageBitmap(b);
        }finally{clearTimeout(tm);}
        if(run!==epoch||!open){if(bitmap&&bitmap.close)bitmap.close();return;}
        try{if(bitmap.width!==256||bitmap.height!==256)throw Error("unexpected tile size");view(p,bitmap);}
        finally{if(bitmap&&bitmap.close)bitmap.close();}
        frameAt=m.providerResolvedValidTime;activeFrame=m.frameId;lastPosition=p;lastFailure="";
        message("Raw Xweather tile · zoom 7 · time is provider-derived, not a verified observation. Not aligned to streets. Do not use for safety decisions.");
        ageLabel();
      }catch(err){
        if(run===epoch){lastFailure=String(err&&err.message||err).slice(0,75);message((frameAt?"Previous tile retained. ":"Radar unavailable. ")+lastFailure+" · DATA UNVERIFIED");ageLabel();}
      }finally{if(run===epoch)busy=false;}
    }
    /* RADAR IQ V1.1: position relative to the existing traffic horizon, not
       to a fixed screen coordinate. Only moves the independent pilot DOM. */
    function positionRadarPilot(){
      if(!button||!shell)return;
      var traffic=document.querySelector('.diqHorizon .diqSignal');
      var horizon=document.querySelector('.diqHorizon');
      var card=traffic||horizon;
      var bounds=card&&card.getBoundingClientRect();
      var left=bounds?Math.max(9,Math.round(bounds.left)):14;
      var top=horizon?Math.round(horizon.getBoundingClientRect().bottom+12):205;
      top=Math.max(80,Math.min(top,Math.round(window.innerHeight*.58)));
      button.style.left=left+'px';button.style.right='auto';button.style.top=top+'px';
      var panelTop=Math.round(top+button.getBoundingClientRect().height+7);
      shell.style.left=left+'px';shell.style.right='auto';shell.style.top=panelTop+'px';
      var maneuver=document.querySelector('.diqRouteIntel');
      var maneuverTop=maneuver?maneuver.getBoundingClientRect().top:window.innerHeight-185;
      var room=Math.max(135,Math.floor(Math.min(window.innerHeight-14,maneuverTop-10)-panelTop));
      shell.style.maxHeight=Math.min(440,room)+'px';
    }
    function show(){if(!enabled||!shell)return false;positionRadarPilot();open=true;shell.hidden=false;button.setAttribute("aria-expanded","true");refresh();return true;}
    function hide(){open=false;epoch++;busy=false;if(shell)shell.hidden=true;if(button)button.setAttribute("aria-expanded","false");return true;}
    function install(){
      // URL flag is a lab visibility switch, not a security or entitlement mechanism.
      // Do not publicize this route while the provider is under evaluation.
      try{enabled=new URLSearchParams(window.location.search).get("radariq_lab")==="1" && !FreeTierIQ.free();}catch(e){enabled=false;}
      if(!enabled)return false;
      var style=document.createElement("style");style.id="diqRadarIQWebPilotStyle";
      style.textContent="#diqRadarIQPilotButton{position:fixed;left:14px;top:205px;z-index:1900;background:rgba(5,20,31,.95);color:#a8fcfa;border:1px solid #5ab1b9;border-radius:12px;font:750 11px system-ui;padding:10px 12px;box-shadow:0 8px 24px #0009;max-width:185px;text-align:left}#diqRadarIQPilot{position:fixed;left:14px;top:249px;z-index:1901;width:235px;max-width:calc(100vw - 90px);max-height:40vh;overflow:auto;overscroll-behavior:contain;background:rgba(4,18,29,.98);color:#e7f9fc;border:1px solid #638f98;border-radius:13px;padding:10px;box-shadow:0 9px 28px #000b;font:11px system-ui}#diqRadarIQPilot[hidden]{display:none!important}#diqRadarIQPilot canvas{width:100%;height:auto;display:block;margin:8px auto;border:1px solid #3f7380;border-radius:8px}#diqRadarIQPilotCaption{color:#a2d6d9;font-weight:650;margin:8px 0}#diqRadarIQPilotDetail{color:#bbcdd3;line-height:1.45}#diqRadarIQPilotClose{background:#144452;color:white;border:1px solid #42828f;padding:5px 10px;border-radius:7px;float:right}#diqRadarIQPilot strong{color:#86e9ed}#diqRadarIQPilot .diqRadarSafety{font-size:9px;color:#e9bf7c;margin-top:9px}@media(max-width:600px){#diqRadarIQPilotButton{padding:9px 11px;font-size:10px;max-width:155px}#diqRadarIQPilot{width:215px;max-width:calc(100vw - 90px)}}";
      (document.head||document.documentElement).appendChild(style);
      button=document.createElement("button");button.id="diqRadarIQPilotButton";button.type="button";button.textContent="RADAR IQ · LAB";button.setAttribute("aria-expanded","false");
      shell=document.createElement("section");shell.id="diqRadarIQPilot";shell.hidden=true;
      shell.innerHTML='<button id="diqRadarIQPilotClose" type="button" aria-label="Close Radar IQ">✕</button><strong>☁ RADAR IQ</strong><div class="diqRadarSafety">PRIVATE TEST · DATA UNVERIFIED</div><div id="diqRadarIQPilotCaption">Frame time unavailable</div><canvas id="diqRadarIQPilotCanvas" width="256" height="256" aria-label="Raw geographic radar tile preview"></canvas><div id="diqRadarIQPilotDetail">Awaiting test data.</div><div class="diqRadarSafety">Radar is advisory only. No route, CarPlay or voice connection.</div>';
      document.body.appendChild(button);document.body.appendChild(shell);
      canvas=document.getElementById("diqRadarIQPilotCanvas");caption=document.getElementById("diqRadarIQPilotCaption");detail=document.getElementById("diqRadarIQPilotDetail");
      positionRadarPilot();
      window.requestAnimationFrame(positionRadarPilot);
      window.setTimeout(positionRadarPilot,450);
      window.addEventListener('resize',positionRadarPilot,{passive:true});
      window.addEventListener('orientationchange',function(){window.setTimeout(positionRadarPilot,250);},{passive:true});
      button.addEventListener("click",function(){if(open)hide();else show();});
      document.getElementById("diqRadarIQPilotClose").addEventListener("click",hide);
      poll=setInterval(function(){if(open&&now()-lastCheck>=REFRESH)refresh();},30000);
      clock=setInterval(function(){if(open)ageLabel();},30000);
      Diagnostics.push("RadarIQPilot","INSTALLED_OFF_BY_DEFAULT",{test:true});
      return true;
    }
    return {install:install,show:show,hide:hide,status:function(){return {enabled:enabled,open:open,frame:activeFrame,providerTime:frameAt,error:lastFailure};}};
  })();

  /* ==========================================================================
     BOOT — ORDER IS DELIBERATE
  ========================================================================== */
  function boot(){
    try{document.body.setAttribute("data-drive-iq-drive-js","v241-add-stop-route-authority");}catch(e){}
    DriveSession.start();
    FreeTierIQ.init();
    PreferenceAuthority.start();
    CloudIntelligencePresentation.install();
    wireButtons();
    RouteAuthority.loadFromStorage("boot");
    MapAuthority.createOnce();
    try{RadarIQWebPilot.install();}catch(radarPilotError){Diagnostics.error("RadarIQPilot","INSTALL_FAILED",{message:String(radarPilotError&&radarPilotError.message||radarPilotError)});}

    // V255 immediate route handoff recovery. A route can land in localStorage
    // milliseconds after Drive Mode boots; do not wait for the normal watcher.
    [0,120,300,650,1200,2200].forEach(function(delay){
      window.setTimeout(function(){
        try{
          RouteAuthority.loadFromStorage("boot-route-retry-"+delay);
          if(S.session.routeActive){
            RouteAuthority.ensureVisible("boot-route-retry-"+delay);
            RouteAuthority.paintDestination();
          }
        }catch(routeBootErr){
          Diagnostics.error("RouteAuthority","BOOT_RETRY_FAILED",{
            delay:delay,
            message:String(routeBootErr&&routeBootErr.message||routeBootErr)
          });
        }
      },delay);
    });

    LocationAuthority.restoreSaved();
    LocationAuthority.start();
    installLifecycleRecovery();
    startObservers();
    UpgradeIQ.sync();
    WebPlusIQ.verify();
    IncidentIQ.refresh("boot");
    try{WeatherSafetyAuthority.refresh("boot");}catch(weatherBootErr){}
    if(!FreeTierIQ.free()){BriefingAuthority.start();}
    setSub(S.session.routeActive?"Route IQ active":"Drive Mode ready");setSpeed(0);HUD.paint();
    Diagnostics.push("DriveSession","BOOT_COMPLETE",{routeActive:S.session.routeActive});
    try{console.log("✅ DRIVE IQ V270 READY — Route Render Lock active");}catch(e2){}
  }

  if(document.readyState==="loading"){document.addEventListener("DOMContentLoaded",boot,false);}else{window.setTimeout(boot,0);}
})();
